<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { vOnClickOutside } from '@vueuse/components';
import Draggable from 'vuedraggable';
import DealsAPI from 'dashboard/api/deals';
import PipelinesAPI from 'dashboard/api/pipelines';
import AttributesAPI from 'dashboard/api/attributes';
import DealAttributeGroupsAPI from 'dashboard/api/dealAttributeGroups';
import ContactsAPI from 'dashboard/api/contacts';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import AccountAPI from 'dashboard/api/account';
import ReservationsAPI from 'dashboard/api/reservations';
import { usePermissions } from 'dashboard/composables/usePermissions';
import { TASK_CREATOR_USER_IDS } from 'dashboard/constants/taskPermissions';
import { useAlert } from 'dashboard/composables';
import { isChannelDown, networkBrand } from 'dashboard/helper/channelDownHelper';
import NewTaskModal from './NewTaskModal.vue';
import ReservarClienteModal from './ReservarClienteModal.vue';
import RegistrarAbonoModal from './RegistrarAbonoModal.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  branchWaInboxes as mnBranchWaInboxes,
  inboxPhoneDigits as mnInboxPhoneDigits,
  NUMBER_ACCENTS as MN_ACCENTS,
} from 'dashboard/helper/multiNumberHelper';

// Fase 3.2 · permisos para deal/stage actions · admins/legacy → true
const { can, isSuperAdmin, isAdminOrSuperAdmin } = usePermissions();

const store = useStore();

// Roles restringidos Video (fotografo_scope) y Fotógrafo (retoque_scope):
// NO ven el botón ⚙ Configurar del lead (Victor 2026-06-07).
const isPhotographerRole = computed(() => {
  if (isSuperAdmin.value) return false;
  const fp = store.getters.getCurrentAccount?.feature_permissions || {};
  return fp.fotografo_scope === true || fp.retoque_scope === true;
});
const router = useRouter();

const emit = defineEmits(['toggle']);

const DEAL_ATTRIBUTE_MODEL = 'deal_attribute';

const deals = ref([]);
const pipelines = ref([]);
const definitions = ref([]); // CustomAttributeDefinition[] for deal_attribute
const groups = ref([]); // DealAttributeGroup[]
const selectedDealId = ref(null);
const selectedGroupId = ref(null);
const loading = ref(false);
const error = ref(null);
const savingStage = ref(false);

// Modal "Reservar cliente" (Fase 1 · 2026-06-07)
const showReservarModal = ref(false);
const reservations = ref([]);
const latestReservation = computed(() => reservations.value[0] || null);

const fmtMoney = n =>
  `RD$ ${(Number(n) || 0).toLocaleString('es-DO', { maximumFractionDigits: 0 })}`;
const fmtDateShort = d => {
  if (!d) return '';
  const dt = new Date(`${d}T00:00:00`);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fetchReservations = async dealId => {
  if (!dealId) {
    reservations.value = [];
    return;
  }
  try {
    const { data } = await ReservationsAPI.listForDeal(dealId);
    reservations.value = data || [];
  } catch (e) {
    reservations.value = [];
  }
};

const onReservationSaved = () => {
  useAlert('Reserva guardada ✓');
  fetchReservations(selectedDeal.value?.id);
};

// Modal "Registrar abono" (Fase 2)
const showAbonoModal = ref(false);
const abonoReservation = ref(null);
const openAbono = reservation => {
  abonoReservation.value = reservation;
  showAbonoModal.value = true;
};
const onAbonoSaved = () => {
  useAlert('Abono registrado ✓');
  fetchReservations(selectedDeal.value?.id);
};

// Enviar recibo por WhatsApp (Fase 3 · texto)
const sendingReceipt = ref(false);
const onSendReceipt = async reservation => {
  if (sendingReceipt.value || !reservation) return;
  sendingReceipt.value = true;
  try {
    const { data } = await ReservationsAPI.sendReceipt(reservation.id);
    useAlert(data?.sent ? 'Recibo enviado por WhatsApp ✓' : 'No se pudo enviar (sin chat del cliente)');
  } catch (e) {
    useAlert('No se pudo enviar el recibo');
  } finally {
    sendingReceipt.value = false;
  }
};
const savingField = ref(null);
const showCreateForm = ref(false);
const newDealForm = ref({ pipelineId: null, stageId: null, title: '', value: 0, currency: 'DOP' });
const creating = ref(false);

// Selector de inbox para escribirle al cliente por otro número (estilo Kommo).
// Solo super admin. Reusa la plomería de crear conversación + contact_inbox.
const showInboxPicker = ref(false);
const pickerInboxId = ref(null);
const creatingConv = ref(false);
const showHiddenInboxes = ref(false);

const showPipelinePicker = ref(false);
const expandedPipelineId = ref(null);
const movingDeal = ref(false);

// Modal K2 · Cards 3D Carrusel · selector de embudo + etapa
const showStageModal = ref(false);
const centerPipelineIdInModal = ref(null);

// Modal "eliminar contacto y todo su historial" (D2 · papelera roja en header)
const showDeleteModal = ref(false);
const deleteConfirmText = ref('');
const deleting = ref(false);

// Modal "marcar cerrada" (L1 · botón verde en header · M1.B)
const showCloseModal = ref(false);
const closeReason = ref('');
const closing = ref(false);
const CLOSE_REASONS = [
  { key: 'won', emoji: '💚', label: 'Compró / Cerró venta' },
  { key: 'postponed', emoji: '📅', label: 'Pospuesto' },
  { key: 'not_interested', emoji: '🚫', label: 'No le interesa' },
  { key: 'no_response', emoji: '👻', label: 'No responde' },
  { key: 'duplicate', emoji: '🔁', label: 'Duplicado' },
  { key: 'spam', emoji: '⚠️', label: 'Spam' },
];

// Carga los embudos (con sus etapas) una sola vez. El serializer del deal ya NO
// trae pipeline.stages (perf · 2026-05-25), así que la barra de etapas necesita
// esta fuente aparte. Victor 2026-05-27.
async function ensurePipelinesLoaded() {
  if (pipelines.value.length > 0) return;
  try {
    const { data } = await PipelinesAPI.get();
    pipelines.value = data.payload || [];
  } catch (_) { /* silent */ }
}

async function openStageModal() {
  centerPipelineIdInModal.value = selectedDeal.value?.pipeline?.id || null;
  showStageModal.value = true;
  await ensurePipelinesLoaded();
}
function closeStageModal() { showStageModal.value = false; }

async function pickStageFromModal(stageId) {
  closeStageModal();
  await changeStage(stageId);
}

function setCenterPipeline(pipelineId) {
  centerPipelineIdInModal.value = pipelineId;
}
// Helper: click en una card del carrusel · respeta el drag guard
function onK2CardClick(p, position) {
  if (k2DragJustHappened()) return;
  if (position !== 0) setCenterPipeline(p.id);
}

// Devuelve hasta 5 pipelines centrados en centerPipelineIdInModal (con wrap-around si hay 5+).
const visibleCarouselPipelines = computed(() => {
  const all = pipelines.value || [];
  if (all.length === 0) return [];
  const centerIdx = all.findIndex(p => p.id === centerPipelineIdInModal.value);
  const c = centerIdx === -1 ? 0 : centerIdx;
  const n = all.length;
  if (n === 1) return [{ pipeline: all[0], position: 0 }];
  if (n === 2) return [
    { pipeline: all[c], position: 0 },
    { pipeline: all[(c + 1) % 2], position: 1 },
  ];
  if (n === 3) return [
    { pipeline: all[(c + 2) % 3], position: -1 },
    { pipeline: all[c], position: 0 },
    { pipeline: all[(c + 1) % 3], position: 1 },
  ];
  if (n === 4) return [
    { pipeline: all[(c + 3) % 4], position: -1 },
    { pipeline: all[c], position: 0 },
    { pipeline: all[(c + 1) % 4], position: 1 },
    { pipeline: all[(c + 2) % 4], position: 2 },
  ];
  return [
    { pipeline: all[(c - 2 + n) % n], position: -2 },
    { pipeline: all[(c - 1 + n) % n], position: -1 },
    { pipeline: all[c], position: 0 },
    { pipeline: all[(c + 1) % n], position: 1 },
    { pipeline: all[(c + 2) % n], position: 2 },
  ];
});

const centerPipelineInModal = computed(() =>
  (pipelines.value || []).find(p => p.id === centerPipelineIdInModal.value) || selectedDeal.value?.pipeline || null
);

function carouselPrev() {
  const all = pipelines.value || [];
  if (all.length <= 1) return;
  const centerIdx = all.findIndex(p => p.id === centerPipelineIdInModal.value);
  const newIdx = (centerIdx - 1 + all.length) % all.length;
  centerPipelineIdInModal.value = all[newIdx].id;
}
function carouselNext() {
  const all = pipelines.value || [];
  if (all.length <= 1) return;
  const centerIdx = all.findIndex(p => p.id === centerPipelineIdInModal.value);
  const newIdx = (centerIdx + 1) % all.length;
  centerPipelineIdInModal.value = all[newIdx].id;
}

// Wheel scroll · trackpad horizontal o rueda vertical → rota carrusel (throttled)
let wheelLockUntil = 0;
function onCarouselWheel(e) {
  e.preventDefault();
  const now = Date.now();
  if (now < wheelLockUntil) return;
  const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
  if (Math.abs(delta) < 10) return;
  if (delta > 0) carouselNext();
  else carouselPrev();
  wheelLockUntil = now + 250;
}

// Drag-to-rotate · 2026-05-12 PM · permite arrastrar desde las CARDS también
// (antes bloqueaba toda card · ahora solo bloquea controles específicos).
// Threshold reducido a 35px · guard de 80ms para evitar que el click del card
// se dispare después del drag.
const k2Drag = ref({ active: false, startX: 0, moved: false });
let k2DragGuardUntil = 0;
function k2DragJustHappened() { return Date.now() < k2DragGuardUntil; }

function onCarouselDragStart(e) {
  // Permitir drag desde el overlay y las cards · bloquear solo controles
  if (e.target.closest('.k2-nav-arrow, .k2-dot, .k2-close-corner, .k2-mini-stage')) return;
  k2Drag.value = { active: true, startX: e.clientX, moved: false };
}
function onCarouselDragMove(e) {
  if (!k2Drag.value.active) return;
  if (Math.abs(e.clientX - k2Drag.value.startX) > 6) {
    k2Drag.value.moved = true;
    e.preventDefault();
  }
}
function onCarouselDragEnd(e) {
  if (!k2Drag.value.active) return;
  const dx = (e.clientX || 0) - k2Drag.value.startX;
  const wasMoved = k2Drag.value.moved;
  k2Drag.value.active = false;
  if (wasMoved && Math.abs(dx) > 35) {
    if (dx > 0) carouselPrev();
    else carouselNext();
    // Guard breve · el siguiente click event (después de mouseup) no debe disparar setCenterPipeline.
    k2DragGuardUntil = Date.now() + 80;
  }
}

// Teclado: ← → para navegar el carrusel mientras el modal está abierto
function onCarouselKeydown(e) {
  if (!showStageModal.value) return;
  if (e.key === 'ArrowLeft') { e.preventDefault(); carouselPrev(); }
  else if (e.key === 'ArrowRight') { e.preventDefault(); carouselNext(); }
}
onMounted(() => { document.addEventListener('keydown', onCarouselKeydown); });
onBeforeUnmount(() => { document.removeEventListener('keydown', onCarouselKeydown); });

// Live notification: { fromName, toName } cuando se cambia de stage; auto-clear a 4s.
const recentStageChange = ref(null);
let recentStageTimer = null;
function flashStageChange(fromName, toName) {
  recentStageChange.value = { fromName, toName };
  if (recentStageTimer) clearTimeout(recentStageTimer);
  recentStageTimer = setTimeout(() => { recentStageChange.value = null; }, 4000);
}

const showConfigModal = ref(false);
const configTab = ref('fields'); // 'fields' | 'auto' (Kommo-style: fields default, auto en pestaña aparte)
// V5-P1 · 2026-05-15 (Victor) · Grupo seleccionado en el modal Configurar (chips Kommo)
const selectedConfigGroupId = ref(null);

const autoLeadConfig = ref({
  enabled: false,
  pipelineId: null,
  stageId: null,
});
const savingAutoLead = ref(false);

const editingGroup = ref(null);
const addingGroup = ref(false);
const newGroupForm = ref({ name: '' });
const savingGroup = ref(false);

// V5-P1 · helpers del modal Configurar (Kommo)
function startAddGroup() {
  editingGroup.value = null;
  addingGroup.value = true;
  newGroupForm.value = { name: '' };
}
function cancelGroupEdit() {
  editingGroup.value = null;
  addingGroup.value = false;
  newGroupForm.value = { name: '' };
}

const editingField = ref(null);
const newFieldForm = ref({ display_name: '', display_type: 'text', values: '', deal_attribute_group_id: null });
const savingField2 = ref(false);
const showFieldEditor = ref(false);
const uploadingFor = ref(null); // attribute_key currently uploading
const inlineEditingKey = ref(null); // key del campo que está en modo edición inline

const FIELD_TYPES = [
  { category: 'Texto', items: [
    { type: 'text', label: 'Texto corto', icon: 'T', desc: 'Una línea de texto' },
    { type: 'textarea', label: 'Texto largo', icon: '≡', desc: 'Múltiples líneas' },
    { type: 'email', label: 'Correo', icon: '✉', desc: 'Email validado' },
    { type: 'phone', label: 'Teléfono', icon: '☎', desc: 'Número telefónico' },
    { type: 'link', label: 'URL/Link', icon: '🔗', desc: 'Enlace web' },
  ]},
  { category: 'Numérico', items: [
    { type: 'number', label: 'Número', icon: '#', desc: 'Cantidad numérica' },
    { type: 'currency', label: 'Moneda', icon: '$', desc: 'Cantidad monetaria' },
    { type: 'percent', label: 'Porcentaje', icon: '%', desc: '0–100%' },
    { type: 'rating', label: 'Calificación', icon: '⭐', desc: 'Estrellas 1–5' },
  ]},
  { category: 'Fecha y hora', items: [
    { type: 'date', label: 'Fecha', icon: '📅', desc: 'Día/Mes/Año' },
    { type: 'datetime', label: 'Fecha y hora', icon: '🗓', desc: 'Cita programada' },
    { type: 'time', label: 'Hora', icon: '⏰', desc: 'Solo hora' },
  ]},
  { category: 'Selección', items: [
    { type: 'list', label: 'Lista (selector)', icon: '▾', desc: 'Una opción' },
    { type: 'multiselect', label: 'Multi-selección', icon: '☰', desc: 'Varias opciones' },
    { type: 'checkbox', label: 'Sí / No', icon: '☑', desc: 'Verdadero / falso' },
  ]},
  { category: 'Archivos', items: [
    { type: 'file', label: 'Archivo', icon: '📎', desc: 'PDF, doc, etc.' },
    { type: 'image', label: 'Imagen', icon: '🖼', desc: 'JPG, PNG, etc.' },
  ]},
];

function fieldTypeMeta(type) {
  for (const cat of FIELD_TYPES) {
    const it = cat.items.find(i => i.type === type);
    if (it) return it;
  }
  return { label: type, icon: '?', desc: '' };
}

const currentChat = computed(() => store.getters.getSelectedChat || {});
// Canal caído (Victor 2026-05-17 · redes 2026-05-25): el inbox de esta
// conversación está desconectado → no escribir NI cerrar (el cliente no
// recibiría nada). Cubre WhatsApp/TikTok (conn_status) e Instagram/Facebook
// (Meta revocó el token → reauthorization_required).
const downInbox = computed(() =>
  store.getters['inboxes/getInbox'](currentChat.value?.inbox_id)
);
const inboxDisconnected = computed(() =>
  isChannelDown(currentChat.value, downInbox.value)
);
// Marca de la red caída (logo, color, nombre) para el modal.
const downBrand = computed(() => networkBrand(downInbox.value));
const showChannelDownModal = ref(false);
const ackChannelDown = ref(false);
watch(
  () => [
    currentChat.value?.id,
    currentChat.value?.inbox_conn_status,
    downInbox.value?.reauthorization_required,
  ],
  () => {
    if (inboxDisconnected.value) {
      if (!ackChannelDown.value) showChannelDownModal.value = true;
    } else {
      showChannelDownModal.value = false;
      ackChannelDown.value = false;
    }
  },
  { immediate: true }
);
const currentUser = computed(() => store.getters.getCurrentUser || {});
// Asignar/crear tareas · espejo de AgentPermissions::TASK_CREATOR_USER_IDS.
const canAssignTasks = computed(() =>
  TASK_CREATOR_USER_IDS.includes(currentUser.value?.id)
);

// "Responder desde ▾" · SOLO Super Admin (Victor 2026-05-19): el selector
// de número se oculta por completo para admins, agentes y responsables —
// el ▾ ni aparece y el popover no abre. Decisión explícita de Victor para
// evitar respuestas desde números equivocados.
const canPickReplyInbox = computed(() => isSuperAdmin.value);
const accountId = computed(() => store.getters.getCurrentAccountId);
const contactId = computed(() => currentChat.value?.meta?.sender?.id);
const contact = computed(() => currentChat.value?.meta?.sender);

// Inboxes WhatsApp por los que el super admin puede escribirle al cliente.
// Ordena: WAHA conectada → conectada → WAHA → resto (mismo criterio que el
// default de "crear lead").
const branchCodeById = computed(() => {
  const map = {};
  (store.getters['branches/getBranches'] || []).forEach(b => {
    map[b.id] = b.code || b.name;
  });
  return map;
});
const branchNameById = computed(() => {
  const map = {};
  (store.getters['branches/getBranches'] || []).forEach(b => {
    map[b.id] = b.name || b.code;
  });
  return map;
});

// Los inbox WAHA/Bridge son Channel::Api y NO tienen campo phone: el número
// vive dentro del nombre ("SDE WhatsApp +18494600461"). Tomamos la corrida
// de dígitos más larga; <8 dígitos = "sin número".
function inboxPhoneDigits(inbox) {
  const raw = inbox.phone_number
    ? String(inbox.phone_number).replace(/\D/g, '')
    : '';
  if (raw.length >= 8) return raw;
  const runs = String(inbox.name || '').match(/[\d][\d\s().+-]{6,}[\d]/g) || [];
  let best = '';
  runs.forEach(r => {
    const d = r.replace(/\D/g, '');
    if (d.length > best.length) best = d;
  });
  return best.length >= 8 ? best : null;
}

// Clasifica TODOS los inbox WhatsApp y marca por qué se ocultaría cada uno.
const classifiedInboxes = computed(() => {
  const wa = ['Channel::Api', 'Channel::Whatsapp'];
  const list = (store.getters['inboxes/getInboxes'] || []).filter(i =>
    wa.includes(i.channel_type)
  );
  const rank = i => {
    const wh = String(i.webhook_url || '').toLowerCase();
    const conn = i.additional_attributes?.conn_status === 'connected';
    const isWaha = wh.includes('waha');
    if (isWaha && conn) return 0;
    if (conn) return 1;
    if (isWaha) return 2;
    return 3;
  };
  return [...list]
    .map(i => {
      const isWaha = String(i.webhook_url || '')
        .toLowerCase()
        .includes('waha');
      // Solo WAHA reporta conn_status confiable. El bridge Baileys casi nunca
      // lo setea (queda nil aunque el número funcione), así que un Bridge se
      // considera conectado salvo que esté EXPLÍCITAMENTE 'disconnected'
      // (ej. inbox 12 · Victor 2026-05-18).
      const connected =
        i.channel_type !== 'Channel::Api' ||
        i.additional_attributes?.conn_status === 'connected' ||
        (!isWaha && i.additional_attributes?.conn_status !== 'disconnected');
      const digits = inboxPhoneDigits(i);
      const isDup = /no\s*usar|duplicad/i.test(i.name || '');
      let hiddenReason = null;
      if (!connected) hiddenReason = 'Desconectado';
      else if (isDup) hiddenReason = 'Duplicado';
      else if (!digits) hiddenReason = 'Sin número';
      return {
        id: i.id,
        name: i.name,
        branchId: i.branch_id ?? null,
        branch: branchCodeById.value[i.branch_id] || null,
        branchName:
          branchNameById.value[i.branch_id] ||
          branchCodeById.value[i.branch_id] ||
          'Sin sucursal',
        kind: isWaha ? 'WAHA' : 'Bridge',
        connected,
        phone: digits ? formatPhone(`+${digits}`) || `+${digits}` : null,
        hiddenReason,
        _r: rank(i),
      };
    })
    .sort((a, b) => a._r - b._r);
});
// Solo operativos: conectados, con número y no duplicados.
const writableInboxes = computed(() =>
  classifiedInboxes.value.filter(i => !i.hiddenReason)
);
// Agrupados por sucursal, respetando el orden por rank.
const writableGroups = computed(() => {
  const groups = [];
  const byId = {};
  writableInboxes.value.forEach(i => {
    const key = i.branchId ?? 'none';
    if (!byId[key]) {
      byId[key] = { key, label: i.branchName, items: [] };
      groups.push(byId[key]);
    }
    byId[key].items.push(i);
  });
  return groups;
});
const hiddenInboxes = computed(() =>
  classifiedInboxes.value.filter(i => i.hiddenReason)
);
function toggleInboxPicker() {
  if (!canPickReplyInbox.value) return;
  showInboxPicker.value = !showInboxPicker.value;
  if (showInboxPicker.value) {
    showHiddenInboxes.value = false;
    const first = writableInboxes.value.find(i => i.connected);
    pickerInboxId.value = first ? first.id : null;
  }
}
function closeInboxPicker() {
  showInboxPicker.value = false;
}
async function writeFromInbox() {
  const inbox = writableInboxes.value.find(i => i.id === pickerInboxId.value);
  if (!inbox || !inbox.connected || !contact.value?.phone_number) return;
  const digits = String(contact.value.phone_number).replace(/\D/g, '');
  if (digits.length < 8) {
    useAlert('El contacto no tiene un teléfono válido.');
    return;
  }
  creatingConv.value = true;
  try {
    const data = await store.dispatch('contactConversations/create', {
      isFromWhatsApp: true,
      params: {
        inboxId: inbox.id,
        contactId: contactId.value,
        sourceId: `${digits}@c.us`,
      },
    });
    showInboxPicker.value = false;
    const convId = data?.id;
    if (convId) {
      router.push(
        `/app/accounts/${accountId.value}/conversations/${convId}`
      );
    }
  } catch (e) {
    useAlert('No se pudo abrir la conversación por ese número.');
  } finally {
    creatingConv.value = false;
  }
}

// Fase 3a · "También escribió a" (Victor 2026-05-18): otras conversaciones
// del MISMO contacto en números de la MISMA sucursal (respeta la regla "cada
// sucursal su tablero" — cross-sucursal NO se agrupa). Click → salta al chat.
// Mapa inbox_id → conversation_id del contacto (incluye la actual). Lo usa el
// toggle "Responder desde" para saltar a la conversación del número elegido.
const convIdByInbox = ref({});
async function loadSiblingConvs() {
  convIdByInbox.value = {};
  const cid = contactId.value;
  const curId = currentChat.value?.id;
  if (!cid || !curId) return;
  try {
    const { data } = await ContactsAPI.getConversations(cid);
    const convs = data?.payload || data || [];
    const map = {};
    convs.forEach(cv => {
      if (cv.inbox_id != null && map[cv.inbox_id] == null) {
        map[cv.inbox_id] = cv.id;
      }
    });
    convIdByInbox.value = map;
  } catch (e) {
    convIdByInbox.value = {};
  }
}
watch(
  () => [contactId.value, currentChat.value?.id],
  () => loadSiblingConvs(),
  { immediate: true }
);

// "Responder desde" · toggle multi-número (Victor 2026-05-23) ----------------
// Cuando la sucursal de la conversación tiene 2+ números WhatsApp, mostramos
// botones fijos para elegir desde cuál responderle al cliente. El "activo" es
// el número de la conversación abierta; tocar otro salta (o crea) la
// conversación de ese número. Disponible para super admin Y para agentes de
// una sucursal con 2 números (gate por sucursal, no por rol).
const currentInboxId = computed(() => currentChat.value?.inbox_id ?? null);
const currentBranchId = computed(
  () => store.getters['inboxes/getInbox']?.(currentInboxId.value)?.branch_id ?? null
);
// Sucursal a la que pertenece el cliente, debajo del nombre (Victor 2026-06-08).
const leadBranchName = computed(() => {
  const bid = currentChat.value?.branch_id ?? currentBranchId.value;
  return bid ? branchNameById.value[bid] || null : null;
});
const replyFromNumbers = computed(() => {
  const list = mnBranchWaInboxes(store, currentBranchId.value);
  // Solo en conversaciones de WhatsApp de una sucursal con 2+ números.
  if (list.length < 2 || !list.some(ib => ib.id === currentInboxId.value)) {
    return [];
  }
  return list.map((ib, idx) => ({
    id: ib.id,
    accent: MN_ACCENTS[idx] || null,
    phone: formatPhone(`+${mnInboxPhoneDigits(ib)}`) || ib.name,
    label: idx === 0 ? 'Principal' : idx === 1 ? 'Secundario' : `Número ${idx + 1}`,
  }));
});
async function pickReplyFrom(inboxId) {
  if (inboxId === currentInboxId.value) return;
  const existing = convIdByInbox.value[inboxId];
  if (existing) {
    router.push(`/app/accounts/${accountId.value}/conversations/${existing}`);
    return;
  }
  const digits = String(contact.value?.phone_number || '').replace(/\D/g, '');
  if (digits.length < 8) {
    useAlert('El contacto no tiene un teléfono válido.');
    return;
  }
  creatingConv.value = true;
  try {
    const data = await store.dispatch('contactConversations/create', {
      isFromWhatsApp: true,
      params: { inboxId, contactId: contactId.value, sourceId: `${digits}@c.us` },
    });
    const convId = data?.id;
    if (convId) {
      router.push(`/app/accounts/${accountId.value}/conversations/${convId}`);
    }
  } catch (e) {
    useAlert('No se pudo abrir la conversación por ese número.');
  } finally {
    creatingConv.value = false;
  }
}

// Editar el nombre del contacto (viene de WhatsApp · Victor 2026-05-16)
const nameOverride = ref(null);
const isEditingName = ref(false);
const nameDraft = ref('');
const savingName = ref(false);
const displayName = computed(
  () => nameOverride.value || contact.value?.name || 'Sin contacto'
);
watch(contactId, () => {
  nameOverride.value = null;
  isEditingName.value = false;
});
function startEditName() {
  if (!contactId.value) return;
  nameDraft.value = contact.value?.name || '';
  isEditingName.value = true;
}
function cancelEditName() {
  isEditingName.value = false;
}
async function saveName() {
  if (!isEditingName.value) return;
  const newName = (nameDraft.value || '').trim();
  isEditingName.value = false;
  if (!newName || !contactId.value || newName === (contact.value?.name || '')) {
    return;
  }
  savingName.value = true;
  nameOverride.value = newName;
  try {
    await ContactsAPI.update(contactId.value, { name: newName });
    store
      .dispatch('contacts/update', { id: contactId.value, name: newName })
      .catch(() => {});
  } catch (e) {
    nameOverride.value = null;
    // eslint-disable-next-line no-alert
    alert('No se pudo guardar el nombre: ' + (e?.message || 'error'));
  } finally {
    savingName.value = false;
  }
}
// Sticky primary assignee del contacto (Victor 2026-05-14): viene en
// meta.sender.primary_assignee desde el serializer. Si difiere del responsable
// actual de la conv, hubo fallback round-robin (primary offline).
const contactPrimaryAssignee = computed(
  () => currentChat.value?.meta?.sender?.primary_assignee || null
);

// ─── Banner "X está atendiendo" (M v5 · M1.B) ───────────────────────────
// Visible cuando hay un atendedor activo (TTL 30 min, sin reclaim previo).
// El responsable original puede RECLAMAR desde aquí · para los demás es informativo.
const ATTEND_BANNER_TTL_MS = 30 * 60 * 1000;
const attendBanner = computed(() => {
  const c = currentChat.value;
  const me = currentUser.value;
  if (!c?.id || !me?.id) return null;
  const additional = c.additional_attributes || {};
  if (additional.attending_reclaimed_at) return null;
  const attendingId = additional.attending_user_id;
  const attendingName = additional.attending_user_name;
  const attendingSinceSec = additional.attending_since;
  if (!attendingId || !attendingSinceSec) return null;
  const elapsed = Date.now() - Number(attendingSinceSec) * 1000;
  if (elapsed >= ATTEND_BANNER_TTL_MS) return null;
  const assigneeId = c.meta?.assignee?.id;
  const assigneeName = c.meta?.assignee?.name || c.meta?.assignee?.available_name || 'el responsable';
  const isResponsible = assigneeId === me.id;
  const isAttender = Number(attendingId) === me.id;
  const isSuperAdminView = isSuperAdmin.value && !isResponsible && !isAttender;
  if (!isResponsible && !isAttender && !isSuperAdminView) return null;
  return {
    attenderName: attendingName || 'otro agente',
    assigneeName,
    isResponsible,
    isAttender,
    isSuperAdminView,
  };
});

// Fase 2 · 2026-05-12 · Asignar tarea desde el chat
const newTaskModalOpen = ref(false);
function openNewTaskModal() { newTaskModalOpen.value = true; }
function onTaskCreated() {
  useAlert('✓ Tarea asignada al cliente.');
  newTaskModalOpen.value = false;
}

// 2026-05-12 PM · Popover de acciones consolidadas (⚙️ Acciones · C2)
// Reemplaza los botones sueltos del header · 4 items en 3 secciones.
// Usa Teleport a body + position:fixed para no ser recortado por overflow
// del aside lateral.
const actionsMenuOpen = ref(false);
const actionsBtnRef = ref(null);
const actionsPopoverPos = ref({ top: 0, right: 0 });
function toggleActionsMenu() {
  if (actionsMenuOpen.value) {
    actionsMenuOpen.value = false;
    return;
  }
  const rect = actionsBtnRef.value?.getBoundingClientRect();
  if (rect) {
    actionsPopoverPos.value = {
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    };
  }
  actionsMenuOpen.value = true;
}
function closeActionsMenu() { actionsMenuOpen.value = false; }
function onActionsKeydown(e) { if (e.key === 'Escape') closeActionsMenu(); }
onMounted(() => { document.addEventListener('keydown', onActionsKeydown); });
onBeforeUnmount(() => { document.removeEventListener('keydown', onActionsKeydown); });
function actionAssignTask() { closeActionsMenu(); openNewTaskModal(); }
function actionReclaim() { closeActionsMenu(); onReclaimFromCard(); }
function actionClose() { closeActionsMenu(); openCloseModal(); }
function actionDelete() { closeActionsMenu(); openDeleteModal(); }

// Propuesta 3 (M1.B) · 2026-05-19 (Victor): modal glass con vista previa
// EDITABLE de la nota interna antes de reclamar. El botón ya no reclama al
// instante: abre este modal · "Reclamar y notificar" envía la nota (editada
// o no) al otro agente. El cliente NO recibe nada.
const reclaimingFromCard = ref(false);
const reclaimModalOpen = ref(false);
const reclaimNote = ref('');
function onReclaimFromCard() {
  if (reclaimingFromCard.value) return;
  const name =
    currentUser.value?.available_name || currentUser.value?.name || '';
  reclaimNote.value = `Continuaré la conversación, muchas gracias por ayudarme en mi ausencia. Se despide ${name}.`;
  reclaimModalOpen.value = true;
}
async function confirmReclaim() {
  if (reclaimingFromCard.value) return;
  reclaimingFromCard.value = true;
  try {
    const { data } = await ConversationAPI.reclaim(
      currentChat.value.id,
      reclaimNote.value.trim()
    );
    const other =
      data?.other_agent_name ||
      attendBanner.value?.attenderName ||
      'el otro agente';
    reclaimModalOpen.value = false;
    useAlert(`✋ Reclamado · ${other} fue notificado`);
  } catch (_) {
    useAlert('No se pudo reclamar. Intenta de nuevo.');
  } finally {
    reclaimingFromCard.value = false;
  }
}

const selectedDeal = computed(() =>
  deals.value.find(d => d.id === selectedDealId.value) || deals.value[0] || null
);

// Botón "Reservar" SOLO en Ventas, etapa "POR CONFIRMAR + PENDIENTE FACTURAR"
// (Victor 2026-06-07): el cliente cae ahí cuando hace un pago / envía voucher;
// recién en esa etapa el encargado reserva y llena los datos. No se muestra en
// Leads entrantes, Colección de datos, Prueba de vestido, Probabilidad de pago,
// Seguimiento, Logrado/Perdido ni en otros departamentos.
const canReserve = computed(() => {
  const d = selectedDeal.value;
  if (!d) return false;
  const stage = (d.pipeline_stage?.name || '').trim().toUpperCase();
  const pipe = (d.pipeline?.name || '').trim().toUpperCase();
  return pipe.includes('VENTA') && stage === 'POR CONFIRMAR + PENDIENTE FACTURAR';
});

// Reservas del lead seleccionado (Fase 1 · sección "Reserva" en Principal).
watch(
  () => selectedDeal.value?.id,
  id => fetchReservations(id),
  { immediate: true }
);

// Etapas del embudo del deal actual. Fuente: lista cargada por PipelinesAPI
// (el deal ya no serializa pipeline.stages) · fallback a lo que traiga el deal.
const currentPipelineStages = computed(() => {
  const pid = selectedDeal.value?.pipeline_id ?? selectedDeal.value?.pipeline?.id;
  const fromList = pipelines.value.find(p => p.id === pid)?.stages;
  if (fromList?.length) return fromList;
  return selectedDeal.value?.pipeline?.stages || [];
});

const stageIndex = computed(() => {
  const d = selectedDeal.value;
  const all = currentPipelineStages.value;
  if (!all.length || !d) return -1;
  return all.findIndex(s => s.id === d.pipeline_stage_id);
});

// Display amigable del canal: prefiere nombre de inbox, luego teléfono/email, luego limpia "Channel::Xxx" → "Xxx"
const channelDisplay = computed(() => {
  const inbox = store.getters['inboxes/getInbox']?.(currentChat.value?.inbox_id) ||
                store.getters['inboxes/getInboxes']?.find?.(i => i.id === currentChat.value?.inbox_id);
  if (inbox?.name) return inbox.name;
  if (contact.value?.phone_number) return contact.value.phone_number;
  if (contact.value?.email) return contact.value.email;
  const ch = currentChat.value?.meta?.channel || '';
  return ch.replace(/^Channel::/, '') || '—';
});

// "Abrir perfil" en la red social del cliente (Victor 2026-05-23). Solo IG/FB/
// TikTok. Extrae el @usuario; si solo hay un ID interno (numérico) → desactivado
// (no hay perfil público abrible).
function extractSocialHandle(net) {
  const c = contact.value || {};
  const add = c.additional_attributes || {};
  const sp = add.social_profiles || {};
  let h = sp[net] || sp[`${net}_handle`] || sp[`${net}_username`] || '';
  if (!h) {
    const id = String(c.identifier || '');
    if (id.toLowerCase().startsWith(`${net}:`)) h = id.split(':').slice(1).join(':');
  }
  if (!h) h = add.screen_name || add.username || c.screen_name || '';
  return String(h).replace(/^@/, '').trim();
}
const SOCIAL_MAP = {
  'Channel::Instagram': { net: 'instagram', base: 'https://www.instagram.com/', label: 'Instagram', icon: '📸' },
  'Channel::FacebookPage': { net: 'facebook', base: 'https://www.facebook.com/', label: 'Facebook', icon: '👤' },
  'Channel::Tiktok': { net: 'tiktok', base: 'https://www.tiktok.com/@', label: 'TikTok', icon: '🎵' },
};
const profileLink = computed(() => {
  const inbox =
    store.getters['inboxes/getInbox']?.(currentChat.value?.inbox_id) ||
    (store.getters['inboxes/getInboxes'] || []).find(i => i.id === currentChat.value?.inbox_id);
  const m = SOCIAL_MAP[inbox?.channel_type];
  if (!m) return null; // no es red social → no se muestra botón
  const handle = extractSocialHandle(m.net);
  const usable = !!handle && !/^\d+$/.test(handle); // numérico puro = ID interno
  return {
    ...m,
    handle,
    url: usable ? `${m.base}${handle}` : null,
    disabled: !usable,
  };
});

// Teléfono del contacto formateado para mostrar en el header de la lead card.
// Si no hay phone_number, el header cae al fallback channelDisplay (inbox name).
const contactPhone = computed(() => {
  if (!contact.value?.phone_number) return null;
  return formatPhone(contact.value.phone_number);
});

// Ventana deslizante de 5 stages: 2 pasados + actual centrado + 2 siguientes.
// Si el embudo tiene ≤ 5 stages, muestra todos. Pegado al inicio/fin se ajusta.
// Devuelve [{ stage, originalIdx }] para preservar la lógica de pasado/actual/futuro.
const STAGE_WINDOW = 5;
const visibleStages = computed(() => {
  const all = currentPipelineStages.value || [];
  if (all.length <= STAGE_WINDOW) {
    return all.map((stage, idx) => ({ stage, originalIdx: idx }));
  }
  const cur = stageIndex.value < 0 ? 0 : stageIndex.value;
  let start = cur - 2;
  let end = start + STAGE_WINDOW;
  if (start < 0) {
    start = 0;
    end = STAGE_WINDOW;
  } else if (end > all.length) {
    end = all.length;
    start = end - STAGE_WINDOW;
  }
  return all.slice(start, end).map((stage, i) => ({ stage, originalIdx: start + i }));
});

// Total stages reales del embudo (para % de progreso global)
const totalStages = computed(() => currentPipelineStages.value.length || 0);

// Posición del active dentro de la VENTANA visible (para fill bar V3)
const visibleActiveIdx = computed(() => {
  return visibleStages.value.findIndex(item => item.originalIdx === stageIndex.value);
});

// % del fill: centrado en el dot activo de la ventana visible
const fillPct = computed(() => {
  const len = visibleStages.value.length;
  const idx = visibleActiveIdx.value;
  if (len === 0 || idx < 0) return 0;
  return ((idx + 0.5) / len) * 100;
});

// % de progreso global del embudo (mostrado en el chip "67%")
const progressPct = computed(() => {
  if (totalStages.value === 0 || stageIndex.value < 0) return 0;
  return Math.round(((stageIndex.value + 1) / totalStages.value) * 100);
});

// V5-P1 · 2026-05-15 · color de la etapa actual (multi-color segmentado)
// Cada PipelineStage tiene .color en DB. Lo exponemos como CSS variable para
// teñir el count "X/Y", el tab activo "Principal" y el marker centrado.
const activeStageColor = computed(() => {
  const stages = currentPipelineStages.value;
  return stages[stageIndex.value]?.color || '#fbbf24';
});

const conversationLabels = computed(() => currentChat.value?.labels || []);

// ── Label picker (Fase: agregar etiqueta desde la tarjeta del cliente) ──
const showLabelPicker = ref(false);
const labelSearchQuery = ref('');
const allLabels = computed(() => store.getters['labels/getLabels'] || []);
const filteredAllLabels = computed(() => {
  const q = labelSearchQuery.value.trim().toLowerCase();
  if (!q) return allLabels.value;
  return allLabels.value.filter(l => (l.title || '').toLowerCase().includes(q));
});

onMounted(() => {
  // Asegurar que las etiquetas estén cargadas en el store
  store.dispatch('labels/get').catch(() => {});
});

function isLabelOnConv(title) {
  return conversationLabels.value.some(l => String(l).toLowerCase() === String(title).toLowerCase());
}

function colorOf(title) {
  const found = allLabels.value.find(l => (l.title || '').toLowerCase() === String(title).toLowerCase());
  return found?.color || '#94a3b8';
}

async function toggleLabelOnConversation(title) {
  const conv = currentChat.value;
  if (!conv) return;
  const current = conversationLabels.value;
  const next = isLabelOnConv(title)
    ? current.filter(l => String(l).toLowerCase() !== String(title).toLowerCase())
    : [...current, title];
  try {
    await store.dispatch('conversationLabels/update', {
      conversationId: conv.id,
      labels: next,
    });
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error actualizando etiquetas: ' + (e?.message || 'desconocido'));
  }
}

// Teleport a body + position:fixed para que el picker no quede atrapado en
// el stacking context del panel (bug: se mostraba por detrás).
const labelBtnRef = ref(null);
const labelPickerPos = ref({ top: 0, left: 0 });
function toggleLabelPicker() {
  if (showLabelPicker.value) {
    closeLabelPicker();
    return;
  }
  const rect = labelBtnRef.value?.getBoundingClientRect();
  if (rect) {
    const width = 264;
    let left = rect.left;
    if (left + width > window.innerWidth - 8) {
      left = window.innerWidth - width - 8;
    }
    labelPickerPos.value = { top: rect.bottom + 6, left: Math.max(8, left) };
  }
  showLabelPicker.value = true;
}

function closeLabelPicker() {
  showLabelPicker.value = false;
  labelSearchQuery.value = '';
}

const sortedGroups = computed(() => [...groups.value].sort((a, b) => a.position - b.position));

// Orden por position (arrastrable · Victor 2026-05-21); desempata por id.
function byPosition(a, b) {
  return ((a.position ?? 0) - (b.position ?? 0)) || (a.id - b.id);
}

const fieldsForGroup = computed(() => {
  if (!selectedGroupId.value) return [];
  return definitions.value
    .filter(d => d.deal_attribute_group_id === selectedGroupId.value)
    .sort(byPosition);
});

// ===== Reservas múltiples (Victor 2026-06-08) =====
// La pestaña de Reserva/Factura puede tener VARIAS reservas (cuando el cliente
// cambia de plan). Cada reserva es el mismo juego de campos; la Reserva 1 usa
// las llaves actuales (no se pierde nada), las siguientes guardan bajo r2_, r3_…
// en el mismo deal. Un contador (_reservas_count) recuerda cuántas hay. Cada
// reserva es un acordeón colapsado por defecto, se abre al dar clic.
const RESERVA_COUNT_KEY = '_reservas_count';
const selectedGroup = computed(
  () => sortedGroups.value.find(g => g.id === selectedGroupId.value) || null
);
const isReservaGroup = computed(() => {
  const name = (selectedGroup.value?.name || '').toLowerCase();
  if (/factura|reserva/.test(name)) return true;
  return fieldsForGroup.value.some(d => /reserva/.test(d.attribute_key || ''));
});
const reservaCount = computed(() => {
  const n = parseInt(selectedDeal.value?.custom_attributes?.[RESERVA_COUNT_KEY], 10);
  return n && n > 0 ? n : 1;
});
const openReservas = ref([]); // índices abiertos · arranca vacío → todas cerradas
function nsKey(key, i) {
  return i <= 1 ? key : `r${i}_${key}`;
}
function toggleReserva(i) {
  const idx = openReservas.value.indexOf(i);
  if (idx >= 0) openReservas.value.splice(idx, 1);
  else openReservas.value.push(i);
}
function reservaHasData(i) {
  const attrs = selectedDeal.value?.custom_attributes || {};
  return fieldsForGroup.value.some(d => {
    const v = attrs[nsKey(d.attribute_key, i)];
    return v !== undefined && v !== null && v !== '';
  });
}
async function addReserva() {
  if (!selectedDeal.value) return;
  const next = reservaCount.value + 1;
  await saveCustomField(RESERVA_COUNT_KEY, next);
  if (!openReservas.value.includes(next)) openReservas.value.push(next);
}
// Al cambiar de cliente/deal, todas las reservas vuelven a arrancar colapsadas.
watch(selectedDealId, () => {
  openReservas.value = [];
});

const fieldsForConfigGroup = computed(() => {
  // V5-P1 · Modal Kommo: usar selectedConfigGroupId (chip activo en el modal)
  const gid = selectedConfigGroupId.value || newFieldForm.value.deal_attribute_group_id;
  if (!gid) return [];
  return definitions.value
    .filter(d => d.deal_attribute_group_id === gid)
    .sort(byPosition);
});
const selectedConfigGroup = computed(() =>
  sortedGroups.value.find(g => g.id === selectedConfigGroupId.value) || null,
);

// Lista local arrastrable de campos del grupo (vuedraggable la muta en sitio).
const configFieldsLocal = ref([]);
watch(
  fieldsForConfigGroup,
  v => {
    configFieldsLocal.value = [...v];
  },
  { immediate: true }
);

// Al soltar: persiste el nuevo orden (position = índice) y lo refleja localmente.
async function onFieldsReordered() {
  const ids = configFieldsLocal.value.map(f => f.id);
  ids.forEach((id, idx) => {
    const d = definitions.value.find(x => x.id === id);
    if (d) d.position = idx;
  });
  try {
    await AttributesAPI.reorder(ids);
  } catch (e) {
    loadDefinitions();
  }
}

async function loadDealsForContact() {
  if (!contactId.value) {
    deals.value = [];
    selectedDealId.value = null;
    return;
  }
  loading.value = true;
  error.value = null;
  try {
    // El cliente puede tener VARIAS tarjetas (una por sucursal/embudo). El panel
    // debe mostrar la de ESTA conversación abierta. Pedimos por conversación para
    // que la etapa SIEMPRE aparezca, sin importar la sucursal ni si el agente la
    // tiene asignada — si puede ver el chat, ve su etapa (Victor 2026-06-08, caso
    // Marializ). El backend conserva la privacidad de las sucursales personales.
    const conv = currentChat.value;
    const { data } = conv?.id
      ? await DealsAPI.list({ conversationId: conv.id })
      : await DealsAPI.list({ contactId: contactId.value });
    deals.value = data.payload || [];
    const convDeal = conv
      ? deals.value.find(
          d =>
            d.conversation_id === conv.id ||
            d.conversation_display_id === conv.id
        )
      : null;
    selectedDealId.value = (convDeal || deals.value[0])?.id || null;
    // Cargar etapas del embudo aparte para que la barra de progreso se pinte
    // (el deal ya no las trae · ver ensurePipelinesLoaded).
    if (deals.value.length) ensurePipelinesLoaded();
  } catch (e) {
    error.value = e.response?.status ? `HTTP ${e.response.status}` : e.message;
  } finally {
    loading.value = false;
  }
}

async function loadDefinitions() {
  try {
    const { data } = await AttributesAPI.listByModel(DEAL_ATTRIBUTE_MODEL);
    definitions.value = data || [];
  } catch (e) {
    definitions.value = [];
  }
}

async function loadGroups() {
  try {
    const { data } = await DealAttributeGroupsAPI.get();
    groups.value = data.payload || [];
    if (groups.value.length > 0 && !selectedGroupId.value) {
      selectedGroupId.value = sortedGroups.value[0].id;
    }
  } catch (e) {
    groups.value = [];
  }
}

async function loadPipelinesForCreate() {
  if (pipelines.value.length > 0) return;
  try {
    const { data } = await PipelinesAPI.get();
    pipelines.value = data.payload || [];
    if (pipelines.value.length > 0 && !newDealForm.value.pipelineId) {
      newDealForm.value.pipelineId = pipelines.value[0].id;
      newDealForm.value.stageId = pipelines.value[0].stages?.[0]?.id || null;
    }
  } catch (e) { /* silent */ }
}

async function loadAllPipelines() {
  if (pipelines.value.length > 0) return;
  try {
    const { data } = await PipelinesAPI.get();
    pipelines.value = data.payload || [];
  } catch (e) { /* silent */ }
}

watch(contactId, loadDealsForContact, { immediate: true });
watch(() => store.getters.getCurrentAccountId, () => {
  loadDefinitions();
  loadGroups();
  loadAllPipelines();
}, { immediate: true });

// Real-time (Victor 2026-05-20): cuando OTRO agente mueve o crea un deal en
// el Kanban, el backend genera un activity message ("Fulano movió lead…")
// que se broadcastea por ActionCable. actions.js emite 'deal-activity' al
// recibirlo; aquí recargamos la tarjeta sin que el usuario tenga que refrescar.
const onDealActivity = message => {
  if (!message) return;
  const myConvId = currentChat.value?.id;
  if (myConvId && message.conversation_id && message.conversation_id !== myConvId) return;
  loadDealsForContact();
};
onMounted(() => emitter.on('deal-activity', onDealActivity));
onBeforeUnmount(() => emitter.off('deal-activity', onDealActivity));

// Tiempo real directo (Victor 2026-06-08): el backend emite 'deal.updated' por
// websocket cuando un lead cambia de etapa/embudo (bot, agente o uno mismo). No
// depende del mensaje de chat → la barra de etapa se actualiza siempre, al
// instante, para todos los que ven ese cliente. Recargamos si es esta conv/contacto.
const onDealUpdatedLive = data => {
  if (!data) return;
  const myConvId = currentChat.value?.id;
  if (data.conversation_id && myConvId && data.conversation_id === myConvId) {
    loadDealsForContact();
    return;
  }
  if (data.contact_id && contactId.value && data.contact_id === contactId.value) {
    loadDealsForContact();
  }
};
onMounted(() => emitter.on(BUS_EVENTS.DEAL_UPDATED_LIVE, onDealUpdatedLive));
onBeforeUnmount(() => emitter.off(BUS_EVENTS.DEAL_UPDATED_LIVE, onDealUpdatedLive));

async function changeStage(targetStageId) {
  if (!selectedDeal.value || targetStageId === selectedDeal.value.pipeline_stage_id) return;
  savingStage.value = true;
  const previousStageId = selectedDeal.value.pipeline_stage_id;
  const prevStage = currentPipelineStages.value.find(s => s.id === previousStageId);
  selectedDeal.value.pipeline_stage_id = targetStageId;
  try {
    const { data } = await DealsAPI.moveToStage(selectedDeal.value.id, targetStageId);
    const idx = deals.value.findIndex(d => d.id === data.id);
    if (idx >= 0) deals.value[idx] = data;
    flashStageChange(prevStage?.name, data.pipeline_stage?.name);
  } catch (e) {
    selectedDeal.value.pipeline_stage_id = previousStageId;
    // eslint-disable-next-line no-alert
    alert('Error cambiando etapa: ' + (e.response?.data?.message || e.message));
  } finally {
    savingStage.value = false;
  }
}

async function saveCustomField(key, value) {
  if (!selectedDeal.value) return;
  const previous = { ...(selectedDeal.value.custom_attributes || {}) };
  const newAttrs = { ...previous, [key]: value };
  savingField.value = key;
  try {
    const { data } = await DealsAPI.update(selectedDeal.value.id, {
      deal: { custom_attributes: newAttrs },
    });
    const idx = deals.value.findIndex(d => d.id === data.id);
    if (idx >= 0) deals.value[idx] = data;
  } catch (e) {
    selectedDeal.value.custom_attributes = previous;
    // eslint-disable-next-line no-alert
    alert('Error guardando campo: ' + (e.response?.data?.message || e.message));
  } finally {
    savingField.value = null;
  }
}

function openCreateForm() {
  showCreateForm.value = true;
  newDealForm.value.title = currentChat.value?.meta?.sender?.name
    ? `Lead — ${currentChat.value.meta.sender.name}`
    : 'Nuevo lead';
  loadPipelinesForCreate();
}

const newDealStages = computed(() => {
  const p = pipelines.value.find(pp => pp.id === newDealForm.value.pipelineId);
  return p?.stages || [];
});

watch(() => newDealForm.value.pipelineId, () => {
  newDealForm.value.stageId = newDealStages.value[0]?.id || null;
});

async function createDeal() {
  if (creating.value) return;
  creating.value = true;
  try {
    const { data } = await DealsAPI.create({
      deal: {
        pipeline_id: newDealForm.value.pipelineId,
        pipeline_stage_id: newDealForm.value.stageId,
        contact_id: contactId.value,
        conversation_id: currentChat.value?.id,
        title: newDealForm.value.title,
        value: newDealForm.value.value,
        currency: newDealForm.value.currency,
      },
    });
    deals.value.unshift(data);
    selectedDealId.value = data.id;
    showCreateForm.value = false;
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error creando lead: ' + (e.response?.data?.message || e.message));
  } finally {
    creating.value = false;
  }
}

function openDealInKanban() {
  if (!selectedDeal.value) return;
  router.push({
    name: 'pipelines_deal',
    params: {
      accountId: accountId.value,
      pipelineId: selectedDeal.value.pipeline_id,
      dealId: selectedDeal.value.id,
    },
  });
}

// Eliminación total del contacto y su historial · ver D2 + D4
// Borra: deals, conversations, messages, contact_inboxes, notes, csat, contact.
// Si el cliente vuelve a escribir, entra como cliente nuevo.
function openDeleteModal() {
  deleteConfirmText.value = '';
  showDeleteModal.value = true;
}
function closeDeleteModal() {
  if (deleting.value) return;
  showDeleteModal.value = false;
  deleteConfirmText.value = '';
}
const deleteConfirmMatches = computed(() => {
  const target = (contact.value?.name || '').trim();
  return target.length > 0 && deleteConfirmText.value.trim() === target;
});
async function confirmFullDelete() {
  if (!deleteConfirmMatches.value || !contact.value?.id) return;
  deleting.value = true;
  try {
    await ContactsAPI.fullDestroy(contact.value.id);
    useAlert('Contacto y todo su historial eliminados.');
    showDeleteModal.value = false;
    // Redirigir al inicio (la conversación ya no existe).
    router.push({ name: 'home', params: { accountId: accountId.value } });
  } catch (e) {
    const msg = e?.response?.data?.message || 'No se pudo eliminar el contacto.';
    useAlert(msg);
  } finally {
    deleting.value = false;
  }
}

// Marcar cerrada (L1 · M1.B): usa status=resolved que ya existe en Chatwoot.
// La razón es opcional · se guarda en custom_attributes para analytics futuros.
function dismissChannelDown() {
  showChannelDownModal.value = false;
  ackChannelDown.value = true;
}
function openCloseModal() {
  // Canal caído: no permitir cerrar (el cliente no recibiría confirmación).
  if (inboxDisconnected.value) {
    showChannelDownModal.value = true;
    return;
  }
  closeReason.value = '';
  showCloseModal.value = true;
}
function closeCloseModal() {
  if (closing.value) return;
  showCloseModal.value = false;
  closeReason.value = '';
}
function selectCloseReason(key) {
  closeReason.value = closeReason.value === key ? '' : key;
}
async function confirmClose() {
  const convId = currentChat.value?.id;
  if (!convId || closing.value) return;
  if (inboxDisconnected.value) { showChannelDownModal.value = true; return; }
  closing.value = true;
  try {
    await store.dispatch('toggleStatus', { conversationId: convId, status: 'resolved' });
    if (closeReason.value) {
      const existing = currentChat.value?.custom_attributes || {};
      const merged = { ...existing, close_reason: closeReason.value, closed_at: Math.floor(Date.now() / 1000) };
      try {
        await ConversationAPI.updateCustomAttributes({ conversationId: convId, customAttributes: merged });
      } catch (_) { /* la razón es opcional · no falla el cierre */ }
    }
    showCloseModal.value = false;
    useAlert('✓ Conversación cerrada · revisa el tab Cerrados');
  } catch (e) {
    useAlert('No se pudo cerrar. Intenta de nuevo.');
  } finally {
    closing.value = false;
  }
}

function openPipelinePicker() {
  showPipelinePicker.value = true;
  expandedPipelineId.value = selectedDeal.value?.pipeline_id || pipelines.value[0]?.id || null;
}

async function pickStage(pipeline, stage) {
  if (movingDeal.value) return;
  movingDeal.value = true;
  try {
    if (selectedDeal.value) {
      // Move existing deal to (possibly different) pipeline+stage
      const prevStageName = selectedDeal.value.pipeline_stage?.name;
      const { data } = await DealsAPI.moveToStage(selectedDeal.value.id, stage.id);
      const idx = deals.value.findIndex(d => d.id === data.id);
      if (idx >= 0) deals.value[idx] = data;
      flashStageChange(prevStageName, data.pipeline_stage?.name);
    } else {
      // Create new deal in chosen pipeline+stage
      const { data } = await DealsAPI.create({
        deal: {
          pipeline_id: pipeline.id,
          pipeline_stage_id: stage.id,
          contact_id: contactId.value,
          conversation_id: currentChat.value?.id,
          title: contact.value?.name ? `Lead — ${contact.value.name}` : 'Nuevo lead',
        },
      });
      deals.value.unshift(data);
      selectedDealId.value = data.id;
    }
    showPipelinePicker.value = false;
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error: ' + (e.response?.data?.message || e.message));
  } finally {
    movingDeal.value = false;
  }
}

// Clic directo en un color de la barra → mueve el lead a esa etapa (Victor
// 2026-05-21). Respeta el permiso move_lead_between_stages. Ignora si ya está
// en esa etapa o si no hay tarjeta.
function onStageSegmentClick(stage) {
  if (!selectedDeal.value || !stage) return;
  // Mover lead abierto a todos los agentes (Victor 2026-05-23) · queda en el chat.
  if (stage.id === selectedDeal.value.pipeline_stage_id) return;
  pickStage(selectedDeal.value.pipeline, stage);
}

function openConfigModal() {
  showConfigModal.value = true;
  configTab.value = 'fields';
  // V5-P1 · Seleccionar el primer grupo (o el actual del lead si existe)
  selectedConfigGroupId.value = selectedGroupId.value || groups.value[0]?.id || null;
  editingGroup.value = null;
  editingField.value = null;
  newGroupForm.value = { name: '' };
  newFieldForm.value = {
    display_name: '',
    display_type: 'text',
    values: '',
    deal_attribute_group_id: sortedGroups.value[0]?.id || null,
  };
  // Hydrate auto-create config from store
  const acct = store.getters.getCurrentAccount || {};
  const settings = acct.settings || {};
  autoLeadConfig.value = {
    enabled: !!settings.auto_create_lead_enabled,
    pipelineId: settings.default_lead_pipeline_id ? Number(settings.default_lead_pipeline_id) : null,
    stageId: settings.default_lead_pipeline_stage_id ? Number(settings.default_lead_pipeline_stage_id) : null,
  };
}

const autoLeadStages = computed(() => {
  const p = pipelines.value.find(pp => pp.id === autoLeadConfig.value.pipelineId);
  return p?.stages || [];
});

watch(() => autoLeadConfig.value.pipelineId, () => {
  // si cambia el pipeline, resetear el stage si ya no aplica
  if (!autoLeadStages.value.find(s => s.id === autoLeadConfig.value.stageId)) {
    autoLeadConfig.value.stageId = autoLeadStages.value[0]?.id || null;
  }
});

async function saveAutoLeadConfig() {
  if (savingAutoLead.value) return;
  savingAutoLead.value = true;
  try {
    await AccountAPI.updateSettings({
      auto_create_lead_enabled: autoLeadConfig.value.enabled,
      default_lead_pipeline_id: autoLeadConfig.value.pipelineId,
      default_lead_pipeline_stage_id: autoLeadConfig.value.stageId,
    });
    // refresh account in store
    store.dispatch('accounts/get');
    // eslint-disable-next-line no-alert
    alert('Configuración guardada. Próximas conversaciones nuevas crearán un lead automáticamente.');
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error guardando: ' + (e.response?.data?.message || e.message));
  } finally {
    savingAutoLead.value = false;
  }
}

function startEditGroup(g) {
  editingGroup.value = g;
  addingGroup.value = false;
  newGroupForm.value = { name: g.name };
}

function startEditField(f) {
  editingField.value = f;
  newFieldForm.value = {
    display_name: f.attribute_display_name,
    display_type: f.attribute_display_type,
    values: (f.attribute_values || []).join(', '),
    deal_attribute_group_id: f.deal_attribute_group_id || newFieldForm.value.deal_attribute_group_id,
  };
  showFieldEditor.value = true;
}

function startAddField() {
  editingField.value = null;
  newFieldForm.value = {
    display_name: '',
    display_type: 'text',
    values: '',
    // V5-P1 · usar el grupo seleccionado en el chip Kommo
    deal_attribute_group_id: selectedConfigGroupId.value || newFieldForm.value.deal_attribute_group_id || sortedGroups.value[0]?.id || null,
  };
  showFieldEditor.value = true;
}

function slugify(s) {
  return (s || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

async function saveGroup() {
  if (savingGroup.value || !newGroupForm.value.name.trim()) return;
  savingGroup.value = true;
  try {
    if (editingGroup.value) {
      const { data } = await DealAttributeGroupsAPI.update(editingGroup.value.id, {
        deal_attribute_group: { name: newGroupForm.value.name },
      });
      const idx = groups.value.findIndex(g => g.id === data.id);
      if (idx >= 0) groups.value[idx] = data;
    } else {
      const { data } = await DealAttributeGroupsAPI.create({
        deal_attribute_group: { name: newGroupForm.value.name },
      });
      groups.value.push(data);
      if (!selectedGroupId.value) selectedGroupId.value = data.id;
      if (!newFieldForm.value.deal_attribute_group_id) newFieldForm.value.deal_attribute_group_id = data.id;
      // V5-P1 · auto-seleccionar el nuevo grupo en el modal Kommo
      selectedConfigGroupId.value = data.id;
    }
    editingGroup.value = null;
    addingGroup.value = false;
    newGroupForm.value = { name: '' };
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error guardando grupo: ' + (e.response?.data?.message || e.message));
  } finally {
    savingGroup.value = false;
  }
}

async function deleteGroup(g) {
  const fieldsInGroup = definitions.value.filter(d => d.deal_attribute_group_id === g.id).length;
  // eslint-disable-next-line no-alert
  const msg = fieldsInGroup > 0
    ? `¿Eliminar grupo "${g.name}"? Sus ${fieldsInGroup} campo(s) quedarán sin grupo.`
    : `¿Eliminar grupo "${g.name}"?`;
  // eslint-disable-next-line no-alert
  if (!confirm(msg)) return;
  try {
    await DealAttributeGroupsAPI.delete(g.id);
    groups.value = groups.value.filter(x => x.id !== g.id);
    if (selectedGroupId.value === g.id) selectedGroupId.value = sortedGroups.value[0]?.id || null;
    // refrescar definitions para que pierdan group_id
    loadDefinitions();
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error: ' + (e.response?.data?.message || e.message));
  }
}

async function saveField() {
  if (savingField2.value || !newFieldForm.value.display_name.trim() || !newFieldForm.value.deal_attribute_group_id) return;
  savingField2.value = true;
  const usesValues = ['list', 'multiselect'].includes(newFieldForm.value.display_type);
  const payload = {
    attribute_display_name: newFieldForm.value.display_name,
    attribute_display_type: newFieldForm.value.display_type,
    attribute_model: DEAL_ATTRIBUTE_MODEL,
    deal_attribute_group_id: newFieldForm.value.deal_attribute_group_id,
    attribute_values: usesValues
      ? newFieldForm.value.values.split(',').map(v => v.trim()).filter(Boolean)
      : [],
  };
  try {
    if (editingField.value) {
      const { data } = await AttributesAPI.update(editingField.value.id, payload);
      const idx = definitions.value.findIndex(d => d.id === data.id);
      if (idx >= 0) definitions.value[idx] = data;
    } else {
      payload.attribute_key = slugify(newFieldForm.value.display_name);
      const { data } = await AttributesAPI.create(payload);
      definitions.value.push(data);
    }
    showFieldEditor.value = false;
    editingField.value = null;
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error guardando campo: ' + (e.response?.data?.message || e.message));
  } finally {
    savingField2.value = false;
  }
}

async function uploadFileForField(file, attributeKey) {
  if (!file) return;
  uploadingFor.value = attributeKey;
  try {
    const fd = new FormData();
    fd.append('file', file);
    const { data } = await window.axios.post(
      `/api/v1/accounts/${accountId.value}/deal_attachments`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    const payload = { url: data.url, filename: data.filename, content_type: data.content_type };
    await saveCustomField(attributeKey, payload);
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error subiendo archivo: ' + (e.response?.data?.error || e.message));
  } finally {
    uploadingFor.value = null;
  }
}

async function deleteField(f) {
  // eslint-disable-next-line no-alert
  if (!confirm(`¿Eliminar el campo "${f.attribute_display_name}" de TODAS las tarjetas?`)) return;
  try {
    await AttributesAPI.delete(f.id);
    definitions.value = definitions.value.filter(d => d.id !== f.id);
  } catch (e) {
    // eslint-disable-next-line no-alert
    alert('Error: ' + (e.response?.data?.message || e.message));
  }
}

function formatDate(timestamp) {
  if (!timestamp) return '—';
  const d = new Date(timestamp * 1000);
  return d.toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getInitials(name) {
  if (!name) return '??';
  return name.split(' ').slice(0, 2).map(s => s[0]?.toUpperCase()).join('');
}

function formatPhone(phone) {
  if (!phone) return null;
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}

function fieldValue(key) {
  return selectedDeal.value?.custom_attributes?.[key];
}

// Tarjeta del depósito (Fase C · Victor 2026-06-09): cuando la IA detecta un
// baucher, guarda los datos en custom_attributes. Mostramos monto / método /
// referencia / fecha. Un cliente que paga en partes manda varios comprobantes:
// _bauchers es la LISTA (Victor 2026-06-10); _baucher es el último (compat).
const bauchers = computed(() => {
  const ca = selectedDeal.value?.custom_attributes || {};
  if (Array.isArray(ca._bauchers) && ca._bauchers.length) return ca._bauchers;
  const old = ca._baucher;
  return old && typeof old === 'object' ? [old] : [];
});
// Suma de los montos cuando hay varios (formato dominicano: coma = miles).
const baucherTotal = computed(() => {
  if (bauchers.value.length < 2) return null;
  let sum = 0;
  let currency = 'RD$';
  bauchers.value.forEach(b => {
    const s = String(b.amount || '');
    if (/US\$|USD/.test(s)) currency = 'US$';
    else if (/€|EUR/.test(s)) currency = 'EUR';
    sum += parseFloat(s.replace(/[^\d.,]/g, '').replace(/,/g, '')) || 0;
  });
  return `${currency} ${sum.toLocaleString('es-DO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
});
// P1 Hero + acordeón (Victor 2026-06-10): el último detectado va de "hero"
// (siempre visible); los demás quedan plegados, más reciente primero.
const bauchersOpen = ref(false);
const heroBaucher = computed(() => {
  const list = bauchers.value;
  return list.length ? list[list.length - 1] : null;
});
const restBauchers = computed(() => {
  const list = bauchers.value;
  if (list.length < 2) return [];
  return list
    .slice(0, -1)
    .map((b, i) => ({ ...b, _n: i + 1 }))
    .reverse();
});
function refShort(r) {
  const s = String(r || '');
  return s.length > 6 ? `…${s.slice(-6)}` : s;
}
function baucherTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('es-DO', {
      day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit',
      timeZone: 'America/Santo_Domingo',
    });
  } catch (e) {
    return '';
  }
}

// El monto es clickeable siempre que el lead tenga chat (Victor 2026-06-12). Antes
// dependía de heroBaucher.message_id y si faltaba ese id el clic quedaba muerto
// ("se rompió"). Ahora: con chat → siempre navega; si además hay message_id, salta
// al comprobante y lo hace parpadear. Durable.
const baucherClickable = computed(
  () => !!selectedDeal.value?.conversation_display_id
);

// Clic en el monto del comprobante (Victor 2026-06-11): lleva a la conversación
// del cliente donde envió el baucher y hace parpadear ese mensaje (reusa la infra
// de "saltar al mensaje": BUS_EVENTS.SCROLL_TO_MESSAGE → onScrollToMessage + glow).
// Comprobantes viejos sin message_id: solo navega al chat.
function goToBaucher(b) {
  const convId = selectedDeal.value?.conversation_display_id;
  if (!convId) {
    useAlert('Este lead no tiene chat asociado para mostrar el comprobante.');
    return;
  }
  const msgId = b?.message_id;
  const params = router.currentRoute.value?.params || {};
  const current = params.conversationId || params.conversation_id;
  const alreadyHere = String(current) === String(convId);
  if (!alreadyHere) {
    router.push(`/app/accounts/${accountId.value}/conversations/${convId}`);
  }
  if (msgId) {
    // Si navegamos, dar tiempo a que cargue el hilo antes de saltar/parpadear.
    setTimeout(
      () => emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId: Number(msgId) }),
      alreadyHere ? 60 : 750
    );
  }
}

function inputTypeFor(displayType) {
  return {
    number: 'number',
    currency: 'number',
    percent: 'number',
    date: 'date',
    datetime: 'datetime-local',
    time: 'time',
    link: 'url',
    email: 'email',
    phone: 'tel',
    text: 'text',
  }[displayType] || 'text';
}

function placeholderFor(displayType) {
  return {
    email: 'cliente@ejemplo.com',
    phone: '+1 (xxx) xxx-xxxx',
    link: 'https://…',
    currency: '0.00',
    percent: '0',
    rating: '1-5',
  }[displayType] || '…';
}

function ratingValue(key) {
  const v = Number(fieldValue(key));
  return Number.isFinite(v) ? Math.max(0, Math.min(5, v)) : 0;
}

function fileFieldValue(key) {
  const v = fieldValue(key);
  if (!v) return null;
  if (typeof v === 'string') return { url: v, filename: v.split('/').pop() };
  return v;
}

function formatFieldValue(def) {
  const v = fieldValue(def.attribute_key);
  if (v === null || v === undefined || v === '') return null;
  const t = def.attribute_display_type;
  if (t === 'date') {
    try { return new Date(v).toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch (e) { return String(v); }
  }
  if (t === 'datetime') {
    try { return new Date(v).toLocaleString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch (e) { return String(v); }
  }
  if (t === 'time') return String(v);
  if (t === 'currency') return `${selectedDeal.value?.currency || 'DOP'} ${Number(v).toLocaleString('es-DO')}`;
  if (t === 'percent') return `${v}%`;
  if (t === 'checkbox') return v ? '✓ Sí' : '✗ No';
  if (t === 'multiselect') return Array.isArray(v) ? v.join(', ') : String(v);
  if (t === 'rating') return '★'.repeat(Math.max(0, Math.min(5, Number(v) || 0)));
  return String(v);
}

function startInlineEdit(key) {
  inlineEditingKey.value = key;
}

async function commitInlineEdit(def, value) {
  await saveCustomField(def.attribute_key, value);
  inlineEditingKey.value = null;
}

function emptyPlaceholder(type) {
  return {
    list: 'Elegir',
    multiselect: 'Elegir',
    date: 'Fecha',
    datetime: 'Fecha y hora',
    time: 'Hora',
    checkbox: '—',
    rating: '☆☆☆☆☆',
  }[type] || '…';
}
</script>

<template>
  <aside
    v-if="currentChat?.id"
    class="lcp-aside w-[23.5%] min-w-[330px] max-md:!w-full max-md:!min-w-0 shrink-0 flex flex-col h-full overflow-hidden"
    :style="{ '--stage-color': activeStageColor }"
  >
    <div v-if="loading" class="p-4 text-xs text-slate-500">Cargando lead…</div>
    <div v-else-if="error" class="m-3 text-xs text-red-500 bg-red-50 dark:bg-red-900/20 rounded p-2">{{ error }}</div>

    <template v-else-if="showCreateForm">
      <div class="px-4 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-2 shrink-0">
        <button @click="showCreateForm = false" class="text-slate-400 hover:text-slate-900 dark:hover:text-white">←</button>
        <div class="text-sm font-semibold text-slate-900 dark:text-slate-100">Nuevo lead</div>
        <button @click="emit('toggle')" class="ml-auto text-slate-400 hover:text-slate-900 dark:hover:text-white" title="Ocultar tarjeta">‹</button>
      </div>
      <div class="p-4 space-y-3 overflow-y-auto">
        <div>
          <label class="text-xs text-slate-500 block mb-1">Embudo</label>
          <select v-model="newDealForm.pipelineId" class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-2 py-1.5 text-sm text-slate-900 dark:text-slate-100">
            <option v-for="p in pipelines" :key="p.id" :value="p.id">{{ p.name }}</option>
          </select>
        </div>
        <div>
          <label class="text-xs text-slate-500 block mb-1">Etapa</label>
          <select v-model="newDealForm.stageId" class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-2 py-1.5 text-sm text-slate-900 dark:text-slate-100">
            <option v-for="s in newDealStages" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
        </div>
        <div>
          <label class="text-xs text-slate-500 block mb-1">Título</label>
          <input v-model="newDealForm.title" class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-2 py-1.5 text-sm text-slate-900 dark:text-slate-100" />
        </div>
        <div class="grid grid-cols-2 gap-2">
          <div>
            <label class="text-xs text-slate-500 block mb-1">Valor</label>
            <input v-model.number="newDealForm.value" type="number" class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-2 py-1.5 text-sm text-slate-900 dark:text-slate-100" />
          </div>
          <div>
            <label class="text-xs text-slate-500 block mb-1">Moneda</label>
            <select v-model="newDealForm.currency" class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-2 py-1.5 text-sm text-slate-900 dark:text-slate-100">
              <option value="DOP">DOP</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </div>
        <button @click="createDeal" :disabled="creating || !newDealForm.pipelineId || !newDealForm.stageId" class="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium py-2 rounded-md">
          {{ creating ? 'Creando…' : 'Crear lead' }}
        </button>
      </div>
    </template>

    <template v-else>
      <!-- Header Y1 · M1.B (avatar 56px + name + channel + lead corner + tags) -->
      <div class="lcp-head">
        <div class="lcp-head-row">
          <!-- Avatar gradient + halo dorado -->
          <div class="lcp-avatar-wrap">
            <div class="lcp-avatar">
              {{ getInitials(contact?.name) }}
              <div class="lcp-avatar-online"></div>
            </div>
          </div>
          <!-- Nombre + canal -->
          <div class="lcp-head-info">
            <input
              v-if="isEditingName"
              v-model="nameDraft"
              autofocus
              type="text"
              class="lcp-name lcp-name-input"
              maxlength="120"
              @keyup.enter="saveName"
              @keyup.esc="cancelEditName"
              @blur="saveName"
              @click.stop
            />
            <h3
              v-else
              class="lcp-name lcp-name-editable"
              title="Click para editar el nombre"
              @click.stop="startEditName"
            >
              {{ displayName }}
              <span class="lcp-name-pencil">✎</span>
            </h3>
            <div v-if="leadBranchName" class="lcp-branch">
              <span class="lcp-branch-pin">📍</span> Sucursal {{ leadBranchName }}
            </div>
            <div class="lcp-sub">
              <span class="lcp-sub-dot"></span>
              <template v-if="contactPhone">
                <span class="lcp-sub-label-sr">Contacto</span>
                <span class="lcp-sub-phone">{{ contactPhone }}</span>
              </template>
              <span v-else class="lcp-sub-text">{{ channelDisplay }}</span>
            </div>
            <!-- Abrir perfil en la red social del cliente (IG/FB/TikTok) -->
            <a
              v-if="profileLink && !profileLink.disabled"
              :href="profileLink.url"
              target="_blank"
              rel="noopener noreferrer"
              class="lcp-social-btn"
              :class="profileLink.net"
            >
              {{ profileLink.icon }} Abrir perfil en {{ profileLink.label }}
              <span class="lcp-social-ext">↗</span>
            </a>
            <span
              v-else-if="profileLink && profileLink.disabled"
              class="lcp-social-btn is-disabled"
              title="Este contacto no tiene un perfil público abrible (solo ID interno)"
            >
              {{ profileLink.icon }} {{ profileLink.label }} · sin perfil público
            </span>
          </div>
          <!-- Lead corner: # + 3 botones glass -->
          <div class="lcp-lead-corner">
            <div class="lcp-lead-lbl">Lead</div>
            <div v-if="selectedDeal" class="lcp-lead-num">#{{ selectedDeal.id }}</div>
            <div v-else class="lcp-lead-empty">sin lead</div>
            <div class="lcp-actions">
              <!-- Botón "Ver en kanban" (⤢) oculto el 2026-05-12 PM por pedido de Victor.
                   La funcionalidad sigue disponible vía el botón de abajo "🗂️ Ver en kanban". -->
              <!-- Botón ⚙ "Acciones" oculto por pedido de Victor 2026-05-18
                   (Asignar tarea / Marcar cerrada / Eliminar ya no se acceden
                   desde el panel del lead). -->
              <button @click="emit('toggle')" class="lcp-act-btn" title="Ocultar">‹</button>
            </div>
          </div>
        </div>

        <!-- Selector de deal si hay múltiples -->
        <div v-if="deals.length > 1" class="lcp-deal-selector">
          <select v-model="selectedDealId" class="lcp-deal-select">
            <option v-for="d in deals" :key="d.id" :value="d.id">
              {{ d.title || `Lead #${d.id}` }} · {{ d.pipeline?.name }} · {{ d.pipeline_stage?.name }}
            </option>
          </select>
        </div>

        <!-- Etiquetas -->
        <div class="lcp-tags">
          <span v-for="label in conversationLabels" :key="label"
                class="lcp-tag-chip"
                :style="{ background: colorOf(label) + '26', color: colorOf(label), borderColor: colorOf(label) + '59' }">
            <span>{{ label }}</span>
            <button class="lcp-tag-x"
                    title="Quitar etiqueta"
                    @click.stop="toggleLabelOnConversation(label)">×</button>
          </span>

          <div class="lcp-tag-picker-wrap">
            <button ref="labelBtnRef"
                    class="lcp-tag-add"
                    title="Agregar etiqueta"
                    @click.stop="toggleLabelPicker">+ etiqueta</button>

            <Teleport to="body">
              <div v-if="showLabelPicker"
                   v-on-click-outside="closeLabelPicker"
                   class="lcp-label-picker"
                   :style="{ top: labelPickerPos.top + 'px', left: labelPickerPos.left + 'px' }"
                   @click.stop>
                <div class="lcp-label-picker-search">
                  <input v-model="labelSearchQuery" type="text" placeholder="Buscar etiqueta…" class="lcp-label-input" />
                </div>
                <div class="lcp-label-list">
                  <button v-for="label in filteredAllLabels" :key="label.id"
                          type="button"
                          class="lcp-label-item"
                          :class="{ 'is-on': isLabelOnConv(label.title) }"
                          @click="toggleLabelOnConversation(label.title)">
                    <span class="lcp-label-color" :style="{ background: label.color }" />
                    <span class="lcp-label-name">{{ label.title }}</span>
                    <span v-if="isLabelOnConv(label.title)" class="lcp-label-check">✓</span>
                  </button>
                  <div v-if="filteredAllLabels.length === 0" class="lcp-label-empty">
                    {{ labelSearchQuery ? 'Sin resultados' : 'No hay etiquetas. Créalas en Ajustes → Etiquetas' }}
                  </div>
                </div>
                <a href="#" class="lcp-label-create" @click.prevent="router.push({ name: 'labels_wrapper' })">
                  + Crear nueva etiqueta en Ajustes
                </a>
              </div>
            </Teleport>
          </div>
        </div>
      </div>

      <!-- Embudo M1.B + V3 Flow + X2 Modal -->
      <div v-if="selectedDeal" class="lcp-pipe-wrap relative shrink-0" :style="{ '--stage-color': activeStageColor }">
        <div class="lcp-pipe">
          <!-- Header: nombre embudo + stage clickable + stats -->
          <div class="lcp-ph">
            <div class="lcp-ph-left">
              <div class="lcp-ph-icon-name">📂 {{ selectedDeal.pipeline?.name || 'Embudo' }}</div>
              <button
                type="button"
                class="lcp-stage-trigger"
                :class="{ 'is-open': showStageModal }"
                @click="openStageModal()"
                title="Click para cambiar de etapa"
              >
                <span class="lcp-stage-now">{{ selectedDeal.pipeline_stage?.name || '—' }}</span>
                <span class="lcp-chevron">▼</span>
              </button>
            </div>
            <div class="lcp-stats">
              <span class="lcp-stat"><strong>{{ selectedDeal.days_in_stage || 0 }}d</strong> col</span>
              <span v-if="totalStages > 0" class="lcp-stat lcp-stat-count"><strong>{{ stageIndex + 1 }}</strong> / {{ totalStages }}</span>
            </div>
          </div>

          <!-- V5-P1 · 2026-05-15 (Victor) · Pipeline multi-color segmentado · cada etapa con su color -->
          <div class="lcp-flow">
            <div class="lcp-flow-track">
              <!-- Segmentos: cada etapa pinta su segmento con su .color -->
              <div class="lcp-flow-segments" :style="{ gridTemplateColumns: `repeat(${visibleStages.length}, 1fr)` }">
                <div
                  v-for="{ stage: s } in visibleStages"
                  :key="`s-${s.id}`"
                  class="lcp-flow-seg is-clickable"
                  :style="{ background: s.color || '#fbbf24' }"
                  :title="`Click para mover a: ${s.name}`"
                  @click="onStageSegmentClick(s)"
                ></div>
              </div>
              <!-- Markers centrados (sobre la barra) -->
              <div class="lcp-flow-markers" :style="{ gridTemplateColumns: `repeat(${visibleStages.length}, 1fr)` }">
                <div
                  v-for="{ stage: s, originalIdx } in visibleStages"
                  :key="`m-${s.id}`"
                  class="lcp-flow-marker"
                  :class="originalIdx < stageIndex ? 'is-done' : (originalIdx === stageIndex ? 'is-active' : 'is-pending')"
                  :style="originalIdx === stageIndex ? { background: s.color || '#fbbf24', boxShadow: `0 0 8px ${s.color || '#fbbf24'}` } : {}"
                  :title="s.name"
                ></div>
              </div>
            </div>
          </div>

          <!-- Indicadores de stages ocultos · preservados -->
          <div v-if="currentPipelineStages.length > 5" class="lcp-window-hint">
            <span v-if="visibleStages[0]?.originalIdx > 0">← {{ visibleStages[0].originalIdx }} atrás</span>
            <span v-else></span>
            <span v-if="savingStage || movingDeal" class="lcp-saving">guardando…</span>
            <span v-if="visibleStages[visibleStages.length - 1]?.originalIdx < currentPipelineStages.length - 1">
              {{ currentPipelineStages.length - 1 - visibleStages[visibleStages.length - 1].originalIdx }} adelante →
            </span>
          </div>
        </div>

        <!-- Live notification: aparece 4s al cambiar de stage -->
        <Transition
          enter-active-class="transition duration-300 ease-out"
          enter-from-class="opacity-0 -translate-y-1"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-200 ease-in"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div v-if="recentStageChange" class="lcp-live-flash" :style="{ background: (selectedDeal.pipeline_stage?.color || '#fbbf24') + '14', borderColor: (selectedDeal.pipeline_stage?.color || '#fbbf24') + '33' }">
            <span class="lcp-live-dot" :style="{ background: selectedDeal.pipeline_stage?.color || '#fbbf24' }"></span>
            <span class="lcp-live-text" :style="{ color: selectedDeal.pipeline_stage?.color || '#fbbf24' }">
              Movido a <b>{{ recentStageChange.toName }}</b>
              <span v-if="recentStageChange.fromName" class="opacity-70">desde {{ recentStageChange.fromName }}</span>
            </span>
            <span class="lcp-live-tag" :style="{ color: selectedDeal.pipeline_stage?.color || '#fbbf24' }">live</span>
          </div>
        </Transition>

        <!-- Modal K2 · Cards 3D Carrusel · teleport a body para escapar el stacking context del panel -->
        <Teleport to="body">
        <Transition
          enter-active-class="k2-fade-enter-active"
          enter-from-class="k2-fade-enter-from"
          leave-active-class="k2-fade-leave-active"
          leave-to-class="k2-fade-leave-to"
        >
          <div
            v-if="showStageModal"
            class="k2-overlay"
            @click.self="closeStageModal"
            @keydown.esc="closeStageModal"
            @wheel="onCarouselWheel"
            @mousedown="onCarouselDragStart"
            @mousemove="onCarouselDragMove"
            @mouseup="onCarouselDragEnd"
            @mouseleave="onCarouselDragEnd"
            tabindex="0"
          >
            <button type="button" class="k2-close-corner" @click="closeStageModal" aria-label="Cerrar">×</button>

            <div class="k2-stage" role="dialog" aria-modal="true">
              <div class="k2-eyebrow">PIPELINE SWITCHER · 3D</div>
              <div class="k2-title-h">¿A dónde mueves este lead?</div>
              <div class="k2-help">← → para navegar entre embudos · click en una etapa para asignar</div>

              <div class="k2-carousel">
                <div
                  v-for="{ pipeline: p, position } in visibleCarouselPipelines"
                  :key="p.id"
                  class="k2-card"
                  :class="[
                    `k2-pos-${position}`,
                    { 'is-center': position === 0, 'is-current': p.id === selectedDeal.pipeline?.id }
                  ]"
                  @click="onK2CardClick(p, position)"
                >
                  <div class="k2-card-head">
                    <div class="k2-card-icon">📁</div>
                    <div v-if="p.id === selectedDeal.pipeline?.id" class="k2-card-active-tag">ACTUAL</div>
                  </div>
                  <div class="k2-card-name">{{ p.name }}</div>
                  <div class="k2-card-meta">{{ p.stages?.length || 0 }} etapas · {{ p.id === selectedDeal.pipeline?.id ? 'lead aquí' : 'cambiar embudo' }}</div>

                  <div class="k2-stages-preview">
                    <div v-if="position === 0" class="k2-stages-preview-label">Etapas · click para mover</div>
                    <button
                      v-for="s in (position === 0 ? (p.stages || []) : (p.stages || []).slice(0, 3))"
                      :key="s.id"
                      type="button"
                      class="k2-mini-stage"
                      :class="{
                        'is-on': p.id === selectedDeal.pipeline?.id && s.id === selectedDeal.pipeline_stage_id,
                        'is-disabled': position !== 0 || (p.id === selectedDeal.pipeline?.id && s.id === selectedDeal.pipeline_stage_id)
                      }"
                      :disabled="savingStage || position !== 0 || (p.id === selectedDeal.pipeline?.id && s.id === selectedDeal.pipeline_stage_id)"
                      @click.stop="position === 0 ? pickStageFromModal(s.id) : null"
                    >
                      <div class="k2-mini-stage-dot" :style="{ background: s.color, color: s.color }"></div>
                      <div class="k2-mini-stage-name">
                        {{ s.name }}
                        <span v-if="p.id === selectedDeal.pipeline?.id && s.id === selectedDeal.pipeline_stage_id" class="k2-here">◀ aquí</span>
                      </div>
                      <div v-if="position === 0" class="k2-mini-stage-arrow">→</div>
                    </button>
                    <div v-if="position !== 0 && (p.stages?.length || 0) > 3" class="k2-stages-more">+{{ (p.stages.length - 3) }} más</div>
                  </div>
                </div>
              </div>

              <div v-if="(pipelines || []).length > 1" class="k2-nav">
                <button type="button" class="k2-nav-arrow" @click="carouselPrev" aria-label="Anterior">‹</button>
                <div class="k2-dots">
                  <div
                    v-for="p in pipelines"
                    :key="`dot-${p.id}`"
                    class="k2-dot"
                    :class="{ 'is-on': p.id === centerPipelineIdInModal }"
                    @click="setCenterPipeline(p.id)"
                  ></div>
                </div>
                <button type="button" class="k2-nav-arrow" @click="carouselNext" aria-label="Siguiente">›</button>
              </div>
            </div>
          </div>
        </Transition>
        </Teleport>
      </div>

      <div v-else class="lcp-assign-empty">
        <div class="lcp-assign-title">📂 Asignar a embudo</div>
        <div class="lcp-assign-list">
          <button
            v-for="p in pipelines"
            :key="p.id"
            @click="openPipelinePicker(); expandedPipelineId = p.id"
            class="lcp-assign-row"
          >
            <span class="lcp-assign-name">{{ p.name }}</span>
            <span class="lcp-assign-meta">{{ p.stages?.length || 0 }} etapas →</span>
          </button>
          <div v-if="pipelines.length === 0" class="lcp-assign-noop">Sin embudos creados</div>
        </div>
      </div>

      <!-- Tabs (grupos) — M1.B dorado, más grandes -->
      <div v-if="sortedGroups.length > 0" class="lcp-tabs">
        <button
          v-for="g in sortedGroups"
          :key="g.id"
          @click="selectedGroupId = g.id"
          class="lcp-tab"
          :class="{ 'is-active': selectedGroupId === g.id }"
        >{{ g.name }}</button>
        <button v-if="!isPhotographerRole" @click="openConfigModal" class="lcp-tab-add lcp-tab-configurar" title="Configurar grupos y campos">⚙ Configurar</button>
      </div>

      <!-- Body -->
      <div class="lcp-body flex-1 overflow-y-auto">
        <!-- Tarjeta del depósito · P3 "Compacta mínima" (Victor 2026-06-12): 2 líneas,
             sutil y sin el bloque verde grande. Línea 1 = título + fecha/hora (o total
             si hay varios); línea 2 = monto (clic → comprobante + parpadeo) + chips
             Método/Ref/Recibo. Los demás comprobantes quedan en el acordeón. -->
        <div v-if="bauchers.length" class="lcp-baucher" :class="{ 'is-open': bauchersOpen }">
          <div class="lcp-baucher-ln1">
            <span class="lcp-baucher-ic">🧾</span>
            <span class="lcp-baucher-title">{{ bauchers.length > 1 ? 'Depósitos detectados' : 'Depósito detectado' }}</span>
            <span v-if="bauchers.length > 1" class="lcp-baucher-total">
              <b>{{ baucherTotal }}</b> · {{ bauchers.length }} comprobantes
            </span>
            <span v-else-if="heroBaucher.at" class="lcp-baucher-when">{{ baucherTime(heroBaucher.at) }}</span>
          </div>

          <div class="lcp-baucher-ln2">
            <span
              class="lcp-baucher-monto"
              :class="{ 'is-link': baucherClickable }"
              :title="baucherClickable ? 'Ver comprobante en el chat' : ''"
              @click="goToBaucher(heroBaucher)"
            >{{ heroBaucher.amount || '—' }}</span>
            <span v-if="heroBaucher.method" class="lcp-baucher-tag"><b>Método</b>{{ heroBaucher.method }}</span>
            <span v-if="heroBaucher.reference" class="lcp-baucher-tag"><b>Ref</b>{{ heroBaucher.reference }}</span>
            <span v-if="heroBaucher.date" class="lcp-baucher-tag"><b>Recibo</b>{{ heroBaucher.date }}</span>
          </div>

          <!-- Acordeón: los demás comprobantes -->
          <template v-if="restBauchers.length">
            <div class="lcp-baucher-acc" @click="bauchersOpen = !bauchersOpen">
              <span class="lcp-baucher-acc-t">{{ bauchersOpen ? 'Ocultar comprobantes' : `Ver los otros ${restBauchers.length} comprobantes` }}</span>
              <span class="lcp-baucher-acc-chev">▶</span>
            </div>
            <div v-show="bauchersOpen" class="lcp-baucher-accbody">
              <div
                v-for="b in restBauchers"
                :key="b._n"
                class="lcp-baucher-row"
                :class="{ 'is-link': !!b.message_id }"
                :title="b.message_id ? 'Ir al comprobante en el chat' : ''"
                @click="goToBaucher(b)"
              >
                <span class="lcp-baucher-row-idx">#{{ b._n }}</span>
                <span class="lcp-baucher-row-m">{{ b.amount || '—' }}</span>
                <span class="lcp-baucher-row-dt">
                  <template v-if="b.at">{{ baucherTime(b.at) }}</template>
                  <template v-if="b.reference"> · ref {{ refShort(b.reference) }}</template>
                </span>
              </div>
            </div>
          </template>
        </div>
        <!-- Reservar cliente (Fase 1) -->
        <button v-if="canReserve" class="lcp-reservar-btn" @click="showReservarModal = true">
          📅 Reservar cliente
        </button>
        <!-- Banner: alguien está atendiendo en ausencia (M v5 · M1.B) -->
        <div v-if="attendBanner" class="lcp-attend-banner" :class="{ 'is-responsible': attendBanner.isResponsible, 'is-attender': attendBanner.isAttender, 'is-admin': attendBanner.isSuperAdminView }">
          <div class="lcp-attend-banner__icon">🙋</div>
          <div class="lcp-attend-banner__body">
            <div v-if="attendBanner.isResponsible" class="lcp-attend-banner__text">
              <b>{{ attendBanner.attenderName }}</b> está atendiendo en tu ausencia
            </div>
            <div v-else-if="attendBanner.isAttender" class="lcp-attend-banner__text">
              Estás atendiendo · responsable: <b>{{ attendBanner.assigneeName }}</b>
            </div>
            <div v-else class="lcp-attend-banner__text">
              <b>{{ attendBanner.attenderName }}</b> atiende para <b>{{ attendBanner.assigneeName }}</b>
            </div>
          </div>
          <button
            v-if="attendBanner.isResponsible"
            class="lcp-attend-banner__btn"
            :disabled="reclaimingFromCard"
            @click="onReclaimFromCard"
            title="Recuperar el control de la conversación"
          >✋ {{ reclaimingFromCard ? '...' : 'RECLAMAR' }}</button>
        </div>
        <!-- V5-P1 · 2026-05-15 (Victor) · Bloque read-only compacto · 3 filas tight 3px:
             Responsable + Contacto + Lead creado. Oculta Agente principal y Presupuesto. -->
        <div class="lcp-fields">
          <div v-if="selectedDeal" class="lcp-readonly-block">
            <!-- Responsable -->
            <div class="lcp-field-row lcp-field-resp" :class="{ 'is-empty-resp': !selectedDeal.assignee?.name }">
              <div class="lcp-field-key">Responsable</div>
              <div class="lcp-field-val lcp-field-val--resp">
                <template v-if="selectedDeal.assignee?.name">
                  <span class="lcp-resp-avatar">{{ getInitials(selectedDeal.assignee.name) }}</span>
                  <span class="lcp-resp-name">{{ selectedDeal.assignee.name }}</span>
                </template>
                <span v-else class="is-empty">Sin asignar</span>
              </div>
              <span v-if="selectedDeal.assignee?.name" class="lcp-resp-auto">⚡ auto</span>
              <span v-else class="lcp-field-ro">esperando</span>
            </div>
            <!-- Contacto · phone/handle según canal · sin icono -->
            <div class="lcp-field-row">
              <div class="lcp-field-key">Contacto</div>
              <div class="lcp-field-val">{{ contactPhone || channelDisplay || '—' }}</div>
              <span></span>
            </div>
            <!-- Lead creado · renombrado de "Creado" -->
            <div class="lcp-field-row">
              <div class="lcp-field-key">Lead creado</div>
              <div class="lcp-field-val is-mono">{{ formatDate(selectedDeal.created_at) }}</div>
              <span class="lcp-field-ro">read-only</span>
            </div>
          </div>
          <div v-else>
            <div class="lcp-field-row"><div class="lcp-field-key">Responsable</div><div class="lcp-field-val is-empty">…</div><span></span></div>
            <div class="lcp-field-row"><div class="lcp-field-key">Contacto</div><div class="lcp-field-val is-empty">…</div><span></span></div>
            <div class="lcp-field-row"><div class="lcp-field-key">Lead creado</div><div class="lcp-field-val is-empty">…</div><span></span></div>
          </div>
        </div>

        <!-- Reserva del cliente (Fase 1 · última reserva) · oculta para Fotógrafo -->
        <div v-if="latestReservation && can('view_reservations')" class="lcp-reserva-card">
          <div class="lcp-reserva-head">
            <span class="lcp-reserva-title">🧾 Reserva</span>
            <span v-if="reservations.length > 1" class="lcp-reserva-count">{{ reservations.length }} reservas</span>
          </div>
          <div class="lcp-reserva-row"><span class="k">Plan</span><span class="v">{{ latestReservation.plan_name || '—' }}</span></div>
          <div v-if="latestReservation.client_name" class="lcp-reserva-row"><span class="k">{{ latestReservation.name_label || 'Cliente' }}</span><span class="v">{{ latestReservation.client_name }}</span></div>
          <div v-if="latestReservation.session_on" class="lcp-reserva-row"><span class="k">Sesión</span><span class="v">{{ fmtDateShort(latestReservation.session_on) }}<template v-if="latestReservation.session_time"> · {{ latestReservation.session_time }}</template></span></div>
          <div v-if="latestReservation.location" class="lcp-reserva-row"><span class="k">Lugar</span><span class="v">{{ latestReservation.location }}</span></div>
          <div class="lcp-reserva-sep"></div>
          <div class="lcp-reserva-row"><span class="k">Total</span><span class="v">{{ fmtMoney(latestReservation.total) }}</span></div>
          <div class="lcp-reserva-row"><span class="k">Abonado</span><span class="v green">{{ fmtMoney(latestReservation.paid) }}</span></div>
          <div class="lcp-reserva-row"><span class="k">Pendiente</span><span class="v" :class="{ red: Number(latestReservation.pending) > 0 }">{{ fmtMoney(latestReservation.pending) }}</span></div>
          <div v-if="latestReservation.payment_type" class="lcp-reserva-row"><span class="k">Pago</span><span class="v">{{ latestReservation.payment_type }}</span></div>

          <button
            v-if="Number(latestReservation.pending) > 0"
            class="lcp-abono-btn"
            @click="openAbono(latestReservation)"
          >+ Registrar abono</button>

          <template v-if="latestReservation.payments && latestReservation.payments.length">
            <div class="lcp-reserva-sep"></div>
            <div class="lcp-reserva-subh">Abonos</div>
            <div
              v-for="p in latestReservation.payments"
              :key="p.id"
              class="lcp-reserva-row lcp-abono-row"
            >
              <span class="k">{{ fmtDateShort(String(p.created_at).slice(0, 10)) }} · {{ p.created_by_name || '—' }}</span>
              <span class="v green">{{ fmtMoney(p.amount) }}</span>
            </div>
          </template>

          <div class="lcp-reserva-sep"></div>
          <div class="lcp-reserva-row"><span class="k">Agendado por</span><span class="v gold">{{ latestReservation.created_by_name || '—' }}</span></div>

          <button class="lcp-recibo-btn" :disabled="sendingReceipt" @click="onSendReceipt(latestReservation)">
            📲 {{ sendingReceipt ? 'Enviando…' : 'Enviar recibo por WhatsApp' }}
          </button>
        </div>

        <!-- Campos del grupo seleccionado -->
        <div v-if="sortedGroups.length === 0" class="lcp-empty-block">
          <div class="lcp-empty-icon">📁</div>
          <div class="lcp-empty-text">Sin grupos creados</div>
          <button @click="openConfigModal" class="lcp-empty-btn">+ Crear primer grupo</button>
        </div>

        <div v-else-if="fieldsForGroup.length === 0" class="lcp-empty-block lcp-empty-block-sm">
          <div class="lcp-empty-text">Sin campos en este grupo</div>
          <button @click="openConfigModal" class="lcp-empty-btn">+ Agregar campo</button>
        </div>

        <!-- Reservas múltiples: cada reserva es un acordeón colapsado (P1) -->
        <div v-else-if="isReservaGroup" class="lcp-fields">
          <div
            v-for="i in reservaCount"
            :key="'res' + i"
            class="res-acc"
            :class="{ 'res-open': openReservas.includes(i) }"
          >
            <div class="res-head" @click="toggleReserva(i)">
              <span class="res-chev">▶</span>
              <span class="res-title">Reserva {{ i }}</span>
              <span v-if="reservaHasData(i)" class="res-dot"></span>
              <span class="res-count">{{ reservaHasData(i) ? 'con datos' : 'sin datos' }}</span>
            </div>
            <div v-show="openReservas.includes(i)" class="res-body">
              <div
                v-for="def in fieldsForGroup"
                :key="def.id + '_' + i"
                class="lcp-field-row lcp-field-row-2col group"
              >
                <div class="lcp-field-key" :title="def.attribute_display_name">{{ def.attribute_display_name }}</div>
                <div class="lcp-field-val-wrap min-w-0">
                  <template v-if="!selectedDeal">
                    <span class="text-slate-300 dark:text-slate-600">…</span>
                  </template>
                  <!-- Archivo / imagen (lo normal en reservas) -->
                  <div v-else-if="def.attribute_display_type === 'file' || def.attribute_display_type === 'image'" class="flex items-center gap-2 min-w-0">
                    <a v-if="fileFieldValue(nsKey(def.attribute_key, i))?.url"
                       :href="fileFieldValue(nsKey(def.attribute_key, i)).url"
                       target="_blank"
                       class="text-blue-600 dark:text-blue-400 hover:underline truncate min-w-0">
                      📎 {{ fileFieldValue(nsKey(def.attribute_key, i)).filename || 'archivo' }}
                    </a>
                    <label v-else class="cursor-pointer inline-flex items-center gap-1 text-slate-400 hover:text-blue-500 transition-colors">
                      <input type="file" class="hidden" @change="uploadFileForField($event.target.files[0], nsKey(def.attribute_key, i))" />
                      <span>{{ uploadingFor === nsKey(def.attribute_key, i) ? '⏳ Subiendo…' : '📎 Subir archivo' }}</span>
                    </label>
                    <button v-if="fileFieldValue(nsKey(def.attribute_key, i))?.url && can('leads_deals.delete')" @click="saveCustomField(nsKey(def.attribute_key, i), null)" class="text-red-500 hover:underline text-[10px] shrink-0">×</button>
                  </div>
                  <!-- Cualquier otro tipo: texto simple -->
                  <input
                    v-else
                    type="text"
                    :value="fieldValue(nsKey(def.attribute_key, i)) ?? ''"
                    @blur="saveCustomField(nsKey(def.attribute_key, i), $event.target.value)"
                    @keyup.enter="$event.target.blur()"
                    class="w-full bg-transparent border-b border-white/10 px-1 py-0.5 text-[11px] text-slate-200 focus:outline-none focus:border-blue-400"
                    placeholder="…"
                  />
                  <span v-if="savingField === nsKey(def.attribute_key, i)" class="text-[9px] text-blue-500 ml-1">⏳</span>
                </div>
              </div>
            </div>
          </div>
          <div class="res-add" @click="addReserva">
            <span class="res-add-plus">＋</span> Otra reserva
          </div>
        </div>

        <div v-else class="lcp-fields">
          <div>
            <div
              v-for="def in fieldsForGroup"
              :key="def.id"
              class="lcp-field-row lcp-field-row-2col group"
            >
              <div class="lcp-field-key" :title="def.attribute_display_name">{{ def.attribute_display_name }}</div>
              <div class="lcp-field-val-wrap min-w-0">
                <template v-if="!selectedDeal">
                  <span class="text-slate-300 dark:text-slate-600">…</span>
                </template>

                <!-- ▼ Tipos simples: text, number, currency, percent, link, email, phone, date, datetime, time -->
                <template v-else-if="['text','number','currency','percent','link','email','phone','date','datetime','time'].includes(def.attribute_display_type)">
                  <input
                    v-if="inlineEditingKey === def.attribute_key"
                    :type="inputTypeFor(def.attribute_display_type)"
                    :value="fieldValue(def.attribute_key) ?? ''"
                    @blur="commitInlineEdit(def, $event.target.value)"
                    @keyup.enter="commitInlineEdit(def, $event.target.value)"
                    @keyup.escape="inlineEditingKey = null"
                    class="w-full bg-white dark:bg-slate-800 border border-blue-400 rounded px-1.5 py-0.5 text-[11px] text-slate-900 dark:text-slate-100 focus:outline-none"
                    autofocus
                  />
                  <span
                    v-else
                    @click="startInlineEdit(def.attribute_key)"
                    class="cursor-text px-1 -mx-1 py-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 inline-block max-w-full truncate"
                    :class="formatFieldValue(def) ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500'"
                  >{{ formatFieldValue(def) || emptyPlaceholder(def.attribute_display_type) }}</span>
                </template>

                <!-- Textarea -->
                <template v-else-if="def.attribute_display_type === 'textarea'">
                  <textarea
                    v-if="inlineEditingKey === def.attribute_key"
                    :value="fieldValue(def.attribute_key) ?? ''"
                    @blur="commitInlineEdit(def, $event.target.value)"
                    rows="3"
                    class="w-full bg-white dark:bg-slate-800 border border-blue-400 rounded px-2 py-1 text-[11px] text-slate-900 dark:text-slate-100 focus:outline-none resize-y"
                    autofocus
                  />
                  <span
                    v-else
                    @click="startInlineEdit(def.attribute_key)"
                    class="cursor-text px-1 -mx-1 py-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 inline-block max-w-full whitespace-pre-wrap break-words"
                    :class="formatFieldValue(def) ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500'"
                  >{{ formatFieldValue(def) || '…' }}</span>
                </template>

                <!-- List (single select) -->
                <template v-else-if="def.attribute_display_type === 'list'">
                  <select
                    v-if="inlineEditingKey === def.attribute_key"
                    :value="fieldValue(def.attribute_key) ?? ''"
                    @change="commitInlineEdit(def, $event.target.value)"
                    @blur="inlineEditingKey = null"
                    class="bg-white dark:bg-slate-800 border border-blue-400 rounded px-1.5 py-0.5 text-[11px] text-slate-900 dark:text-slate-100 focus:outline-none"
                    autofocus
                  >
                    <option value="">Elegir…</option>
                    <option v-for="opt in (def.attribute_values || [])" :key="opt" :value="opt">{{ opt }}</option>
                  </select>
                  <span
                    v-else
                    @click="startInlineEdit(def.attribute_key)"
                    class="cursor-pointer px-1 -mx-1 py-0.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 inline-block max-w-full truncate"
                    :class="fieldValue(def.attribute_key) ? 'text-slate-900 dark:text-slate-100' : 'text-slate-400 dark:text-slate-500'"
                  >{{ fieldValue(def.attribute_key) || 'Elegir' }} <span class="text-slate-400">▾</span></span>
                </template>

                <!-- Multi-select como pills -->
                <div v-else-if="def.attribute_display_type === 'multiselect'" class="flex flex-wrap gap-1">
                  <button
                    v-for="opt in (def.attribute_values || [])"
                    :key="opt"
                    type="button"
                    @click="(() => { const cur = Array.isArray(fieldValue(def.attribute_key)) ? [...fieldValue(def.attribute_key)] : []; const idx = cur.indexOf(opt); if (idx>=0) cur.splice(idx,1); else cur.push(opt); saveCustomField(def.attribute_key, cur); })()"
                    class="text-[10px] px-1.5 py-0.5 rounded-full transition-colors"
                    :class="(fieldValue(def.attribute_key) || []).includes(opt) ? 'bg-blue-500 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 hover:bg-slate-200'"
                  >{{ opt }}</button>
                </div>

                <!-- Checkbox -->
                <input
                  v-else-if="def.attribute_display_type === 'checkbox'"
                  type="checkbox"
                  :checked="!!fieldValue(def.attribute_key)"
                  @change="saveCustomField(def.attribute_key, $event.target.checked)"
                  class="w-3.5 h-3.5"
                />

                <!-- Rating estrellas -->
                <div v-else-if="def.attribute_display_type === 'rating'" class="flex gap-0.5 leading-none">
                  <button
                    v-for="n in 5"
                    :key="n"
                    type="button"
                    @click="saveCustomField(def.attribute_key, n)"
                    class="text-sm leading-none"
                    :class="n <= ratingValue(def.attribute_key) ? 'text-amber-400' : 'text-slate-300 dark:text-slate-600 hover:text-amber-300'"
                  >★</button>
                </div>

                <!-- Image -->
                <div v-else-if="def.attribute_display_type === 'image'">
                  <div v-if="fileFieldValue(def.attribute_key)?.url" class="relative inline-block group/img">
                    <img :src="fileFieldValue(def.attribute_key).url" class="max-w-full max-h-24 rounded" />
                    <button @click="saveCustomField(def.attribute_key, null)" class="absolute top-0.5 right-0.5 bg-black/70 text-white rounded-full w-4 h-4 text-[10px] leading-none flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity" title="Quitar">×</button>
                  </div>
                  <label v-else class="cursor-pointer inline-flex items-center gap-1 text-slate-400 hover:text-blue-500 transition-colors">
                    <input type="file" accept="image/*" class="hidden" @change="uploadFileForField($event.target.files[0], def.attribute_key)" />
                    <span>{{ uploadingFor === def.attribute_key ? '⏳ Subiendo…' : '📷 Subir imagen' }}</span>
                  </label>
                </div>

                <!-- File -->
                <div v-else-if="def.attribute_display_type === 'file'" class="flex items-center gap-2 min-w-0">
                  <a v-if="fileFieldValue(def.attribute_key)?.url"
                     :href="fileFieldValue(def.attribute_key).url"
                     target="_blank"
                     class="text-blue-600 dark:text-blue-400 hover:underline truncate min-w-0">
                    📎 {{ fileFieldValue(def.attribute_key).filename || 'archivo' }}
                  </a>
                  <label v-else class="cursor-pointer inline-flex items-center gap-1 text-slate-400 hover:text-blue-500 transition-colors">
                    <input type="file" class="hidden" @change="uploadFileForField($event.target.files[0], def.attribute_key)" />
                    <span>{{ uploadingFor === def.attribute_key ? '⏳ Subiendo…' : '📎 Subir archivo' }}</span>
                  </label>
                  <button v-if="fileFieldValue(def.attribute_key)?.url && can('leads_deals.delete')" @click="saveCustomField(def.attribute_key, null)" class="text-red-500 hover:underline text-[10px] shrink-0">×</button>
                </div>

                <span v-if="savingField === def.attribute_key" class="text-[9px] text-blue-500 ml-1">⏳</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Contacto -->
        <div class="lcp-contact">
          <div class="lcp-contact-head">
            <img v-if="contact?.thumbnail" :src="contact.thumbnail" class="lcp-contact-img" :alt="contact.name" />
            <div v-else class="lcp-contact-init">{{ getInitials(contact?.name) }}</div>
            <div class="lcp-contact-info">
              <div class="lcp-contact-name">{{ contact?.name || '—' }}</div>
              <div class="lcp-contact-channel">{{ channelDisplay }}</div>
            </div>
          </div>
          <div class="lcp-contact-rows">
            <div v-if="contact?.phone_number" class="lcp-field-row lcp-field-row-2col">
              <div class="lcp-field-key">Teléfono</div>
              <div class="lcp-phone-wrap" v-on-click-outside="closeInboxPicker">
                <button
                  type="button"
                  class="lcp-field-val is-gold lcp-phone-btn"
                  :class="{ 'is-clickable': canPickReplyInbox }"
                  @click="toggleInboxPicker"
                >
                  {{ formatPhone(contact.phone_number) }}
                  <span v-if="canPickReplyInbox" class="lcp-phone-chev">▾</span>
                </button>
                <div v-if="showInboxPicker" class="lcp-inbox-pop">
                  <div class="lcp-inbox-pop-head">
                    <span class="lcp-inbox-pop-lock">🔒 Solo Super Admin</span>
                    <div class="lcp-inbox-pop-t">Escribir por…</div>
                    <div class="lcp-inbox-pop-d">
                      Número activo para contactar a
                      {{ contact?.name || 'este cliente' }}
                    </div>
                  </div>
                  <div class="lcp-inbox-pop-list">
                    <template v-for="g in writableGroups" :key="g.key">
                      <div class="lcp-inbox-grp">{{ g.label }}</div>
                      <button
                        v-for="ib in g.items"
                        :key="ib.id"
                        type="button"
                        class="lcp-inbox-opt"
                        :class="{ 'is-sel': pickerInboxId === ib.id }"
                        @click="pickerInboxId = ib.id"
                      >
                        <span class="lcp-inbox-top">
                          <span class="lcp-inbox-num">{{ ib.phone }}</span>
                          <span class="lcp-inbox-dot" />
                        </span>
                        <span class="lcp-inbox-sub">{{ ib.name }} · {{ ib.kind }}</span>
                      </button>
                    </template>
                    <div v-if="!writableInboxes.length" class="lcp-inbox-empty">
                      No hay números operativos.
                    </div>
                    <template v-if="showHiddenInboxes && hiddenInboxes.length">
                      <div class="lcp-inbox-grp is-muted">Ocultos</div>
                      <div
                        v-for="ib in hiddenInboxes"
                        :key="`h-${ib.id}`"
                        class="lcp-inbox-opt is-hidden"
                      >
                        <span class="lcp-inbox-top">
                          <span class="lcp-inbox-num">{{ ib.name }}</span>
                        </span>
                        <span class="lcp-inbox-sub">
                          {{ ib.hiddenReason }}{{ ib.branch ? ' · ' + ib.branch : '' }}
                        </span>
                      </div>
                    </template>
                  </div>
                  <button
                    v-if="hiddenInboxes.length"
                    type="button"
                    class="lcp-inbox-hidden-bar"
                    @click="showHiddenInboxes = !showHiddenInboxes"
                  >
                    {{ hiddenInboxes.length }} inboxes ocultos · desconectados /
                    sin número / duplicados ·
                    <u>{{ showHiddenInboxes ? 'ocultar' : 'ver' }}</u>
                  </button>
                  <div class="lcp-inbox-pop-foot">
                    <button type="button" class="lcp-inbox-btn sec" @click="closeInboxPicker">
                      Cancelar
                    </button>
                    <button
                      type="button"
                      class="lcp-inbox-btn pri"
                      :disabled="creatingConv || !pickerInboxId"
                      @click="writeFromInbox"
                    >
                      {{ creatingConv ? 'Abriendo…' : '✍ Escribir por este número' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="replyFromNumbers.length" class="lcp-field-row lcp-field-row-2col">
              <div class="lcp-field-key">Responder desde</div>
              <div class="lcp-rf-seg">
                <button
                  v-for="n in replyFromNumbers"
                  :key="n.id"
                  type="button"
                  class="lcp-rf-btn"
                  :class="[
                    n.id === currentInboxId ? `is-on-${n.accent}` : '',
                    { 'is-busy': creatingConv },
                  ]"
                  :title="`Responder al cliente desde ${n.phone}`"
                  :disabled="creatingConv"
                  @click="pickReplyFrom(n.id)"
                >
                  <span class="lcp-rf-num">
                    <span class="lcp-rf-dot" :class="`dot-${n.accent}`" />{{ n.phone }}
                  </span>
                  <span class="lcp-rf-lbl">{{ n.label }}</span>
                </button>
              </div>
            </div>
            <div v-if="contact?.email" class="lcp-field-row lcp-field-row-2col">
              <div class="lcp-field-key">Correo</div>
              <div class="lcp-field-val">{{ contact.email }}</div>
            </div>
          </div>
        </div>

        <div v-if="selectedDeal" class="lcp-bottom-actions">
          <button v-if="can('leads_deals.view')" @click="openDealInKanban" class="lcp-bottom-btn">🗂️ Ver en kanban</button>
          <button v-if="can('leads_deals.create')" @click="openCreateForm" class="lcp-bottom-btn">+ Crear otro lead</button>
          <!-- Botón "Asignar tarea" movido al popover de ⚙️ Acciones (2026-05-12 PM) -->
        </div>
      </div>
    </template>

    <!-- Modal Picker Embudo + Etapa -->
    <div
      v-if="showPipelinePicker"
      class="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
      @click.self="showPipelinePicker = false"
    >
      <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg w-full max-w-md max-h-[85vh] flex flex-col">
        <div class="px-4 py-3 border-b border-slate-200 dark:border-slate-700 flex justify-between items-center shrink-0">
          <div>
            <h3 class="text-base font-semibold text-slate-900 dark:text-slate-100">
              {{ selectedDeal ? 'Mover a embudo y etapa' : 'Asignar a embudo y etapa' }}
            </h3>
            <p class="text-[11px] text-slate-500 mt-0.5">{{ contact?.name || 'Este contacto' }}</p>
          </div>
          <button @click="showPipelinePicker = false" class="text-slate-500 hover:text-slate-900 dark:hover:text-white text-xl leading-none">×</button>
        </div>

        <div class="flex-1 overflow-y-auto p-3 space-y-2">
          <div
            v-for="p in pipelines"
            :key="p.id"
            class="rounded border"
            :class="selectedDeal?.pipeline_id === p.id ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-200 dark:border-slate-700'"
          >
            <button
              @click="expandedPipelineId = expandedPipelineId === p.id ? null : p.id"
              class="w-full text-left px-3 py-2 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50"
            >
              <div class="flex items-center gap-2">
                <span class="text-sm font-semibold text-slate-900 dark:text-slate-100">{{ p.name }}</span>
                <span v-if="selectedDeal?.pipeline_id === p.id" class="text-[9px] bg-blue-500 text-white px-1.5 py-0.5 rounded">actual</span>
              </div>
              <span class="text-slate-400 text-xs">{{ expandedPipelineId === p.id ? '▾' : '▸' }} {{ p.stages?.length || 0 }}</span>
            </button>
            <div v-if="expandedPipelineId === p.id" class="px-2 pb-2 space-y-1">
              <button
                v-for="s in p.stages || []"
                :key="s.id"
                @click="pickStage(p, s)"
                :disabled="movingDeal"
                class="w-full text-left rounded px-3 py-2 text-sm flex items-center gap-2 transition-colors"
                :class="selectedDeal?.pipeline_stage_id === s.id ? 'bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100' : 'bg-white dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-800 dark:text-slate-200'"
              >
                <span class="w-2 h-6 rounded-sm shrink-0" :style="{ background: s.color || '#3b82f6' }"></span>
                <span class="flex-1 truncate font-medium">{{ s.name }}</span>
                <span v-if="selectedDeal?.pipeline_stage_id === s.id" class="text-emerald-600 text-sm">✓</span>
              </button>
              <div v-if="(p.stages?.length || 0) === 0" class="text-[11px] text-slate-500 italic text-center py-2">Sin etapas en este embudo</div>
            </div>
          </div>
          <div v-if="pipelines.length === 0" class="text-center py-6 text-sm text-slate-500">Sin embudos creados aún</div>
        </div>

        <div class="px-4 py-3 border-t border-slate-200 dark:border-slate-700 text-right shrink-0">
          <button @click="showPipelinePicker = false" class="text-sm bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 px-3 py-1.5 rounded text-slate-700 dark:text-slate-200">Cancelar</button>
        </div>
      </div>
    </div>

    <!-- Propuesta 3 (M1.B) · 2026-05-19 · Modal glass: reclamar con nota editable -->
    <Teleport to="body">
      <div
        v-if="reclaimModalOpen"
        class="rcl-overlay"
        @click.self="reclaimModalOpen = false"
      >
        <div class="rcl-modal">
          <button
            class="rcl-close"
            :disabled="reclaimingFromCard"
            @click="reclaimModalOpen = false"
          >×</button>
          <div class="rcl-head">
            <div class="rcl-emoji">✋</div>
            <h3 class="rcl-title">¿Reclamar esta conversación?</h3>
            <p class="rcl-sub">
              <b>{{ attendBanner?.attenderName || 'El otro agente' }}</b>
              quedará bloqueado y recibirá esta nota interna. El cliente no
              recibe nada.
            </p>
          </div>
          <label class="rcl-label">Nota interna (editable)</label>
          <textarea
            v-model="reclaimNote"
            class="rcl-textarea"
            rows="3"
            :disabled="reclaimingFromCard"
          />
          <div class="rcl-actions">
            <button
              class="rcl-btn-ghost"
              :disabled="reclaimingFromCard"
              @click="reclaimModalOpen = false"
            >Cancelar</button>
            <button
              class="rcl-btn-gold"
              :disabled="reclaimingFromCard || !reclaimNote.trim()"
              @click="confirmReclaim"
            >{{ reclaimingFromCard ? 'Reclamando…' : 'Reclamar y notificar' }}</button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- V5-P1 · 2026-05-15 (Victor) · Modal Configurar estilo Kommo M1.B -->
    <div
      v-if="showConfigModal"
      class="cfg-overlay"
      @click.self="showConfigModal = false"
    >
      <div class="cfg-modal">
        <!-- Header -->
        <div class="cfg-head">
          <div>
            <h3 class="cfg-title">Campos y grupos</h3>
            <p class="cfg-sub">Añade tus propios campos y úsalos para cualquier filtro o informe.</p>
          </div>
          <button @click="showConfigModal = false" class="cfg-close" title="Cerrar">×</button>
        </div>

        <!-- Tab switcher (Kommo + Auto) -->
        <div class="cfg-section-tabs">
          <button @click="configTab = 'fields'" class="cfg-section-tab" :class="{ active: configTab === 'fields' }">Campos y grupos</button>
          <button @click="configTab = 'auto'" class="cfg-section-tab" :class="{ active: configTab === 'auto' }">🎯 Auto-creación</button>
        </div>

        <!-- TAB · CAMPOS Y GRUPOS (Kommo) -->
        <template v-if="configTab === 'fields'">
          <!-- Chips de grupos · click para seleccionar -->
          <div class="cfg-groups-row">
            <button
              v-for="g in sortedGroups"
              :key="g.id"
              class="cfg-group-chip"
              :class="{ active: selectedConfigGroupId === g.id }"
              @click="selectedConfigGroupId = g.id"
            >
              <span class="cfg-group-drag">⋮⋮</span>
              <span class="cfg-group-name">{{ g.name }}</span>
              <span class="cfg-group-count">{{ definitions.filter(d => d.deal_attribute_group_id === g.id).length }}</span>
            </button>
            <button class="cfg-group-add" @click="startAddGroup" title="Nuevo grupo">+ Grupo</button>
          </div>

          <!-- Form de agregar/editar grupo (visible cuando se está editando o agregando) -->
          <div v-if="editingGroup || addingGroup" class="cfg-group-form">
            <input
              v-model="newGroupForm.name"
              placeholder="Nombre del grupo (ej: Principal, Facturas…)"
              class="cfg-input"
              @keyup.enter="saveGroup"
              autofocus
            />
            <button class="cfg-btn-secondary" @click="cancelGroupEdit">Cancelar</button>
            <button class="cfg-btn-primary" :disabled="savingGroup || !newGroupForm.name.trim()" @click="saveGroup">
              {{ savingGroup ? '…' : (editingGroup ? 'Guardar' : '+ Crear') }}
            </button>
          </div>

          <!-- Acciones del grupo seleccionado -->
          <div v-if="selectedConfigGroup && !editingGroup && !addingGroup" class="cfg-group-actions">
            <div class="cfg-group-title">📁 {{ selectedConfigGroup.name }}</div>
            <div class="cfg-group-actions-btns">
              <button class="cfg-btn-mini" @click="startEditGroup(selectedConfigGroup)">✏ Renombrar</button>
              <button class="cfg-btn-mini cfg-btn-danger" @click="deleteGroup(selectedConfigGroup)">× Borrar grupo</button>
            </div>
          </div>

          <!-- Lista de campos del grupo seleccionado -->
          <div class="cfg-fields-body">
            <div v-if="!selectedConfigGroup" class="cfg-empty">
              <div class="cfg-empty-icon">📁</div>
              <div class="cfg-empty-text">Crea o selecciona un grupo para ver sus campos</div>
            </div>
            <template v-else>
              <div class="cfg-fields-label">Campos de "{{ selectedConfigGroup.name }}"</div>
              <div v-if="fieldsForConfigGroup.length === 0" class="cfg-empty-small">
                Aún sin campos · click "+ Campo" abajo
              </div>
              <Draggable
                :list="configFieldsLocal"
                item-key="id"
                handle=".cfg-field-drag"
                :animation="160"
                @end="onFieldsReordered"
              >
                <template #item="{ element: f }">
                  <div class="cfg-field-item">
                    <span class="cfg-field-drag" title="Arrastra para reordenar">⋮⋮</span>
                    <span class="cfg-field-ico">{{ fieldTypeMeta(f.attribute_display_type).icon }}</span>
                    <!-- V5-P1 · click en el nombre/área principal abre el editor (no solo en ✏) -->
                    <button class="cfg-field-info" @click="startEditField(f)" title="Click para editar">
                      <div class="cfg-field-name">{{ f.attribute_display_name }}</div>
                      <div class="cfg-field-meta">{{ fieldTypeMeta(f.attribute_display_type).label }}<span v-if="(f.attribute_values||[]).length"> · {{ (f.attribute_values||[]).length }} variantes</span></div>
                    </button>
                    <button class="cfg-field-btn cfg-field-btn-danger" @click="deleteField(f)" title="Eliminar">×</button>
                  </div>
                </template>
              </Draggable>
              <button v-if="selectedConfigGroup" class="cfg-add-field" @click="startAddField">+ Agregar campo</button>
            </template>
          </div>
        </template>

          <!-- TAB AUTO-CREACIÓN -->
          <template v-else-if="configTab === 'auto'">
            <div class="cfg-auto-wrap">
            <div class="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3 text-xs text-blue-800 dark:text-blue-200">
              <strong>📌 ¿Qué hace esto?</strong> Cada vez que un cliente nuevo escriba (WhatsApp, Instagram, Facebook, TikTok), se creará automáticamente un lead en el embudo y etapa que escojas aquí.
            </div>

            <label class="flex items-center gap-2 cursor-pointer p-3 bg-slate-50 dark:bg-slate-800 rounded">
              <input
                type="checkbox"
                v-model="autoLeadConfig.enabled"
                class="w-4 h-4 rounded"
              />
              <div class="flex-1">
                <div class="text-sm font-medium text-slate-900 dark:text-slate-100">Activar auto-creación</div>
                <div class="text-[10px] text-slate-500">Cuando alguien escribe por primera vez, crea un lead</div>
              </div>
            </label>

            <div :class="autoLeadConfig.enabled ? '' : 'opacity-50 pointer-events-none'" class="space-y-3 transition-opacity">
              <div>
                <label class="text-xs text-slate-500 block mb-1">Embudo por defecto</label>
                <select v-model="autoLeadConfig.pipelineId" class="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-2 py-1.5 text-sm text-slate-900 dark:text-slate-100">
                  <option :value="null">— Selecciona un embudo —</option>
                  <option v-for="p in pipelines" :key="p.id" :value="p.id">{{ p.name }}</option>
                </select>
              </div>
              <div>
                <label class="text-xs text-slate-500 block mb-1">Etapa por defecto</label>
                <select v-model="autoLeadConfig.stageId" :disabled="!autoLeadConfig.pipelineId" class="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded px-2 py-1.5 text-sm text-slate-900 dark:text-slate-100">
                  <option :value="null">— Selecciona una etapa —</option>
                  <option v-for="s in autoLeadStages" :key="s.id" :value="s.id">{{ s.name }}</option>
                </select>
                <div v-if="autoLeadConfig.pipelineId && autoLeadStages.length === 0" class="text-[10px] text-amber-600 mt-1">
                  Este embudo no tiene etapas. Crea etapas primero en el kanban.
                </div>
              </div>
            </div>

            <div class="flex justify-end pt-2">
              <button
                @click="saveAutoLeadConfig"
                :disabled="savingAutoLead || (autoLeadConfig.enabled && (!autoLeadConfig.pipelineId || !autoLeadConfig.stageId))"
                class="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm px-4 py-1.5 rounded"
              >
                {{ savingAutoLead ? 'Guardando…' : 'Guardar configuración' }}
              </button>
            </div>
            </div>
          </template>

        <!-- Footer cerrar -->
        <div class="cfg-footer">
          <button @click="showConfigModal = false" class="cfg-btn-secondary">Cerrar</button>
        </div>
      </div>
    </div>

    <!-- ════════════════════════════════════════════════════════════════ -->
    <!-- Sub-modal: Editor premium de campo (agregar/editar) -->
    <!-- V5-P1 · Teleport a body para z-index siempre encima del modal padre -->
    <!-- ════════════════════════════════════════════════════════════════ -->
    <Teleport to="body">
    <div
      v-if="showFieldEditor"
      class="lcp-feditor-overlay fixed inset-0 backdrop-blur-md flex items-center justify-center p-4"
      @click.self="showFieldEditor = false"
    >
      <div class="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">
        <!-- Header con gradiente -->
        <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-700 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 shrink-0">
          <div class="flex justify-between items-center">
            <div class="flex items-center gap-3">
              <div class="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-lg shadow-lg">
                {{ editingField ? '✏️' : '✨' }}
              </div>
              <div>
                <h3 class="text-lg font-bold text-slate-900 dark:text-slate-100">
                  {{ editingField ? 'Editar campo' : 'Nuevo campo personalizado' }}
                </h3>
                <p class="text-xs text-slate-500">
                  {{ editingField ? 'Los cambios aplican a TODAS las tarjetas' : 'Aparecerá en todas las tarjetas de leads del grupo seleccionado' }}
                </p>
              </div>
            </div>
            <button @click="showFieldEditor = false" class="text-slate-400 hover:text-slate-900 dark:hover:text-white text-2xl leading-none">×</button>
          </div>
        </div>

        <!-- Body -->
        <div class="flex-1 overflow-y-auto p-6 space-y-5">
          <!-- Información básica -->
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1.5 uppercase tracking-wider">Grupo</label>
              <select v-model="newFieldForm.deal_attribute_group_id" class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-blue-500">
                <option v-for="g in sortedGroups" :key="g.id" :value="g.id">{{ g.name }}</option>
              </select>
            </div>
            <div>
              <label class="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1.5 uppercase tracking-wider">Nombre del campo</label>
              <input v-model="newFieldForm.display_name" placeholder="Ej: Hora de sesión, Editor, etc." class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-blue-500" />
            </div>
          </div>

          <!-- Selector de tipo (cards visuales agrupadas) -->
          <div>
            <label class="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-2 uppercase tracking-wider">Tipo de campo</label>
            <div class="space-y-3">
              <div v-for="cat in FIELD_TYPES" :key="cat.category">
                <div class="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">{{ cat.category }}</div>
                <div class="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  <button
                    v-for="t in cat.items"
                    :key="t.type"
                    type="button"
                    @click="newFieldForm.display_type = t.type"
                    :disabled="editingField !== null && editingField.attribute_display_type !== t.type && editingField.attribute_display_type !== newFieldForm.display_type"
                    class="text-left rounded-lg border p-2.5 transition-all"
                    :class="newFieldForm.display_type === t.type
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-500/30 shadow-sm'
                      : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 hover:shadow-sm'"
                  >
                    <div class="flex items-center gap-1.5 mb-0.5">
                      <span class="text-base leading-none">{{ t.icon }}</span>
                      <span class="text-xs font-semibold text-slate-900 dark:text-slate-100 truncate">{{ t.label }}</span>
                    </div>
                    <div class="text-[10px] text-slate-500 dark:text-slate-400 truncate">{{ t.desc }}</div>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Config específica del tipo -->
          <div v-if="['list','multiselect'].includes(newFieldForm.display_type)" class="bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
            <label class="text-xs font-semibold text-slate-700 dark:text-slate-300 block mb-1.5 uppercase tracking-wider">Opciones</label>
            <input v-model="newFieldForm.values" placeholder="Banreservas, Popular, BHD, Efectivo" class="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-blue-500" />
            <div class="text-[10px] text-slate-500 mt-1">Separa cada opción con coma. Estas serán las opciones disponibles en el dropdown.</div>
          </div>

          <div v-if="['file','image'].includes(newFieldForm.display_type)" class="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-800 dark:text-amber-200">
            💡 En la tarjeta del lead aparecerá un botón para subir {{ newFieldForm.display_type === 'image' ? 'una imagen (JPG, PNG, etc.)' : 'un archivo (PDF, doc, etc.)' }}. La imagen mostrará una vista previa.
          </div>

          <!-- Preview del campo -->
          <div class="bg-slate-100 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-700 rounded-lg p-4">
            <div class="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Vista previa</div>
            <div class="grid grid-cols-[120px_1fr] gap-3 items-center text-sm">
              <div class="text-slate-500 truncate">{{ newFieldForm.display_name || 'Nombre del campo' }}</div>
              <div>
                <input v-if="['text','number','currency','percent','date','datetime','time','link','email','phone'].includes(newFieldForm.display_type)" :type="inputTypeFor(newFieldForm.display_type)" :placeholder="placeholderFor(newFieldForm.display_type)" disabled class="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-2 py-1 text-xs w-full opacity-60" />
                <textarea v-else-if="newFieldForm.display_type === 'textarea'" rows="2" placeholder="…" disabled class="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-2 py-1 text-xs w-full opacity-60 resize-none"></textarea>
                <select v-else-if="newFieldForm.display_type === 'list'" disabled class="bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded px-2 py-1 text-xs w-full opacity-60"><option>Elegir…</option></select>
                <div v-else-if="newFieldForm.display_type === 'multiselect'" class="flex gap-1">
                  <span v-for="opt in (newFieldForm.values.split(',').slice(0,4))" :key="opt" class="bg-slate-200 dark:bg-slate-700 text-[10px] px-1.5 py-0.5 rounded-full">{{ opt.trim() || '—' }}</span>
                </div>
                <input v-else-if="newFieldForm.display_type === 'checkbox'" type="checkbox" disabled class="opacity-60" />
                <div v-else-if="newFieldForm.display_type === 'rating'" class="flex gap-0.5 text-base text-amber-400">★★★<span class="text-slate-300">★★</span></div>
                <span v-else-if="newFieldForm.display_type === 'image'" class="bg-slate-200 dark:bg-slate-700 text-[10px] px-2 py-1 rounded">📷 Subir imagen</span>
                <span v-else-if="newFieldForm.display_type === 'file'" class="bg-slate-200 dark:bg-slate-700 text-[10px] px-2 py-1 rounded">📎 Subir archivo</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="px-6 py-4 border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/30 flex justify-between items-center shrink-0">
          <div class="text-[11px] text-slate-500">
            <span v-if="editingField">Editando — la <code>key</code> no cambiará para no romper datos existentes.</span>
            <span v-else>Se generará una <code>key</code> automática a partir del nombre.</span>
          </div>
          <div class="flex gap-2">
            <button @click="showFieldEditor = false" class="text-sm bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 px-4 py-2 rounded-lg text-slate-700 dark:text-slate-200 font-medium">Cancelar</button>
            <button
              @click="saveField"
              :disabled="savingField2 || !newFieldForm.display_name.trim() || !newFieldForm.deal_attribute_group_id"
              class="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2 rounded-lg shadow"
            >
              {{ savingField2 ? 'Guardando…' : (editingField ? 'Actualizar' : '✨ Crear campo') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    </Teleport>

    <!-- Modal "eliminar contacto y todo su historial" · D2 + D4 -->
    <Teleport to="body">
      <div v-if="showDeleteModal" class="lcp-del-overlay" @click.self="closeDeleteModal">
        <div class="lcp-del-modal">
          <div class="lcp-del-head">
            <div class="lcp-del-icon">⚠️</div>
            <div class="lcp-del-title-block">
              <div class="lcp-del-lbl">Acción irreversible</div>
              <h3 class="lcp-del-title">Eliminar contacto e historial</h3>
            </div>
          </div>

          <div class="lcp-del-contact">
            <div class="lcp-del-avatar">{{ getInitials(contact?.name) }}</div>
            <div class="lcp-del-info">
              <div class="lcp-del-name">{{ contact?.name || 'Sin contacto' }}</div>
              <div v-if="contactPhone" class="lcp-del-phone">{{ contactPhone }}</div>
            </div>
          </div>

          <div class="lcp-del-warn">
            <ul>
              <li>Se borran <b>todas las conversaciones</b> y mensajes</li>
              <li>Se borran <b>todos los leads</b> de este contacto en cualquier pipeline</li>
              <li>Se borra el contacto, notas y datos asociados</li>
              <li>Si vuelve a escribir por WhatsApp, entrará <b>como cliente nuevo</b></li>
            </ul>
          </div>

          <div class="lcp-del-confirm-lbl">
            Para confirmar, escribe el nombre <b>{{ contact?.name }}</b>:
          </div>
          <input
            v-model="deleteConfirmText"
            type="text"
            class="lcp-del-input"
            :placeholder="contact?.name || ''"
            :disabled="deleting"
            @keyup.enter="deleteConfirmMatches && confirmFullDelete()"
          />

          <div class="lcp-del-actions">
            <button class="lcp-del-cancel" :disabled="deleting" @click="closeDeleteModal">Cancelar</button>
            <button
              class="lcp-del-confirm"
              :class="{ 'is-enabled': deleteConfirmMatches }"
              :disabled="!deleteConfirmMatches || deleting"
              @click="confirmFullDelete"
            >
              {{ deleting ? 'Eliminando…' : 'Sí, eliminar todo' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Modal "marcar cerrada" · L1 · M1.B -->
    <Teleport to="body">
      <div v-if="showCloseModal" class="lcp-close-overlay" @click.self="closeCloseModal">
        <div class="lcp-close-modal">
          <div class="lcp-close-icon">✓</div>
          <h3 class="lcp-close-title">¿Marcar como cerrada?</h3>
          <div class="lcp-close-desc">
            Saldrá de tu bandeja activa y aparecerá en el tab <b>"✓ Cerrados"</b>. Puedes reabrirla cuando quieras.
          </div>

          <div class="lcp-close-reason-lbl">Razón del cierre (opcional)</div>
          <div class="lcp-close-reason-grid">
            <button
              v-for="r in CLOSE_REASONS"
              :key="r.key"
              type="button"
              class="lcp-close-reason"
              :class="{ 'is-selected': closeReason === r.key }"
              :disabled="closing"
              @click="selectCloseReason(r.key)"
            >
              <span class="lcp-close-reason-emoji">{{ r.emoji }}</span>
              <span class="lcp-close-reason-label">{{ r.label }}</span>
            </button>
          </div>

          <div class="lcp-close-actions">
            <button class="lcp-close-cancel" :disabled="closing" @click="closeCloseModal">Cancelar</button>
            <button class="lcp-close-confirm" :disabled="closing" @click="confirmClose">
              {{ closing ? 'Cerrando…' : '✓ Cerrar conversación' }}
            </button>
          </div>
        </div>
      </div>
    </Teleport>
    <NewTaskModal
      v-if="newTaskModalOpen"
      :prefill-conversation-id="currentChat?.id"
      :prefill-contact-name="contact?.name"
      :prefill-contact-id="contactId"
      @close="newTaskModalOpen = false"
      @created="onTaskCreated"
    />

    <ReservarClienteModal
      v-if="showReservarModal"
      :deal-id="selectedDeal?.id"
      :contact-id="contactId"
      :conversation-id="currentChat?.id"
      :contact-name="displayName"
      :phone="contact?.phone_number"
      @close="showReservarModal = false"
      @saved="onReservationSaved"
    />

    <RegistrarAbonoModal
      v-if="showAbonoModal && abonoReservation"
      :reservation-id="abonoReservation.id"
      :total="abonoReservation.total"
      :pending="abonoReservation.pending"
      @close="showAbonoModal = false"
      @saved="onAbonoSaved"
    />

    <!-- Popover de Acciones (⚙ · 2026-05-12 PM · C2) · teleport a body para evitar overflow clipping -->
    <Teleport to="body">
      <div
        v-if="actionsMenuOpen"
        class="lcp-actions-popover"
        :style="{ top: actionsPopoverPos.top + 'px', right: actionsPopoverPos.right + 'px' }"
        v-on-click-outside="closeActionsMenu"
        @click.stop
      >
        <div class="lcp-actions-pop-header">
          <span>⚙️ Acciones</span>
        </div>

        <div class="lcp-actions-pop-section">
          <div class="lcp-actions-pop-title">— Cliente</div>
          <button v-if="canAssignTasks" class="lcp-actions-pop-item" @click="actionAssignTask">
            <span class="lcp-pop-icon lcp-pop-icon--task">📋</span>
            <span class="lcp-pop-info">
              <span class="lcp-pop-lbl">Asignar tarea</span>
              <span class="lcp-pop-desc">Vinculada a este cliente</span>
            </span>
          </button>
          <button v-if="attendBanner" class="lcp-actions-pop-item" @click="actionReclaim">
            <span class="lcp-pop-icon lcp-pop-icon--reclaim">✋</span>
            <span class="lcp-pop-info">
              <span class="lcp-pop-lbl">Reclamar cliente</span>
              <span class="lcp-pop-desc">Retomar la conversación</span>
            </span>
          </button>
        </div>

        <div class="lcp-actions-pop-section">
          <div class="lcp-actions-pop-title">— Conversación</div>
          <button v-if="!isPhotographerRole" class="lcp-actions-pop-item" @click="actionClose">
            <span class="lcp-pop-icon lcp-pop-icon--close">✓</span>
            <span class="lcp-pop-info">
              <span class="lcp-pop-lbl">Marcar cerrada</span>
              <span class="lcp-pop-desc">Mover a Cerrados</span>
            </span>
          </button>
        </div>

        <div v-if="isAdminOrSuperAdmin" class="lcp-actions-pop-section">
          <div class="lcp-actions-pop-title lcp-actions-pop-title--danger">— Zona peligrosa</div>
          <button class="lcp-actions-pop-item lcp-actions-pop-item--danger" @click="actionDelete">
            <span class="lcp-pop-icon lcp-pop-icon--delete">🗑</span>
            <span class="lcp-pop-info">
              <span class="lcp-pop-lbl">Eliminar conversación</span>
              <span class="lcp-pop-desc">No se puede deshacer</span>
            </span>
          </button>
        </div>
      </div>
    </Teleport>

    <!-- Canal caído · modal glass (Victor 2026-05-17 · diseño aprobado) -->
    <Teleport to="body">
      <div
        v-if="showChannelDownModal"
        class="fixed inset-0 z-[120] flex items-center justify-center p-4"
        style="background: rgba(0,0,0,0.6)"
        @click.self="dismissChannelDown"
      >
        <div
          class="relative"
          style="width:440px;max-width:calc(100vw - 40px);border-radius:26px;padding:34px 36px 36px;
            background:radial-gradient(circle at 12% 8%, rgba(255,140,90,0.10), transparent 42%),
            radial-gradient(circle at 92% 96%, rgba(120,90,200,0.16), transparent 50%),
            linear-gradient(155deg,#211b2b 0%,#181722 45%,#14131b 100%);
            border:1px solid rgba(255,255,255,0.07);
            box-shadow:0 40px 90px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)"
        >
          <div
            class="absolute flex items-center justify-center cursor-pointer"
            style="top:20px;right:20px;width:36px;height:36px;border-radius:50%;
              background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.08);color:#b9bccb"
            @click="dismissChannelDown"
          >✕</div>
          <div class="text-center" style="margin-bottom:24px">
            <div
              class="flex items-center justify-center mx-auto"
              style="width:74px;height:74px;border-radius:20px;margin-bottom:16px"
              :style="{ background: downBrand.color, boxShadow: `0 0 28px ${downBrand.glow}66` }"
            >
              <span style="width:40px;height:40px;display:flex" v-html="downBrand.icon" />
            </div>
            <span
              class="inline-flex items-center gap-2"
              style="background:rgba(255,91,91,0.14);border:1px solid rgba(255,91,91,0.4);
                color:#ff9d9d;font-size:12px;font-weight:800;letter-spacing:.12em;
                padding:6px 14px;border-radius:999px;margin-bottom:14px"
            >
              <span style="width:8px;height:8px;border-radius:50%;background:#ff5b5b;box-shadow:0 0 10px #ff5b5b"></span>
              ⚠ URGENTE
            </span>
            <div style="font-size:26px;font-weight:800;margin-top:4px">
              {{ downBrand.label }} <span style="color:#ff8a8a">Desconectado</span>
            </div>
            <p style="color:#9a9db0;font-size:14px;margin-top:10px">
              <b style="color:#e7e8ee">{{ downInbox?.name }}</b> · dejaron de entrar mensajes. Comunícate con el encargado y revisa la conexión. No puedes escribir ni cerrar conversaciones de este canal hasta que se restablezca.
            </p>
          </div>
          <div
            class="text-center cursor-pointer"
            style="max-width:360px;margin:0 auto;border-radius:18px;padding:22px;
              background:rgba(255,255,255,0.035);border:1px solid rgba(255,255,255,0.07)"
            @click="dismissChannelDown"
          >
            <div style="font-size:28px;margin-bottom:10px">📞</div>
            <div style="font-size:18px;font-weight:800;color:#7db4ff;margin-bottom:6px">Avisar al encargado</div>
            <div style="color:#9a9db0;font-size:13px;margin-bottom:14px">Notifica al responsable y deja registro.</div>
            <span
              style="display:inline-block;font-size:12px;font-weight:700;padding:7px 16px;border-radius:999px;
                background:rgba(125,180,255,0.14);color:#7db4ff;border:1px solid rgba(125,180,255,0.3)"
            >Entendido</span>
          </div>
        </div>
      </div>
    </Teleport>
  </aside>
</template>

<style>
/* ─────────────────────────────────────────────────────────────────
   LeadCardPanel · M1.B Glass Negro + V3 Flow + X2 Modal
   Pixel-perfect del mockup lead-flow-X2-modal-flotante.html
   Prefijo .lcp- para evitar colisiones globales.
   ───────────────────────────────────────────────────────────────── */

/* Pipeline section · Glass negro M1.B */
.lcp-pipe-wrap {
  position: relative;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  background: #0a0a0a;
  overflow: hidden;
}
.lcp-pipe { padding: 16px 22px 18px; position: relative; z-index: 2; }
.lcp-pipe-wrap::before {
  content: '';
  position: absolute; inset: 0;
  pointer-events: none; z-index: 1;
  background:
    linear-gradient(120deg, transparent 12%, rgba(255,255,255,0.04) 22%, transparent 24%),
    radial-gradient(circle at 100% 0%, rgba(251,191,36,0.10), transparent 60%);
}

/* Header del pipeline */
.lcp-ph {
  display: flex; justify-content: space-between; align-items: flex-start;
  gap: 12px; margin-bottom: 14px;
}
.lcp-ph-left { flex: 1; min-width: 0; }
.lcp-ph-icon-name {
  font-size: 11px; letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.50); font-weight: 800;
}
.lcp-stage-trigger {
  display: inline-flex; align-items: center; gap: 8px;
  margin-top: 2px;
  padding: 4px 10px 4px 0;
  border: none; background: transparent;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  font-family: inherit;
  max-width: 100%;
  min-width: 0;
  transition: all 0.2s;
}
.lcp-stage-trigger:hover { padding-left: 8px; padding-right: 14px; background: rgba(251,191,36,0.06); }
.lcp-stage-trigger.is-open { background: rgba(251,191,36,0.10); padding-left: 8px; padding-right: 14px; }
.lcp-stage-trigger.is-readonly { cursor: not-allowed; opacity: 0.85; }
.lcp-stage-trigger.is-readonly:hover { background: transparent; padding-left: 0; padding-right: 10px; }
.lcp-chevron-lock { color: rgba(255,255,255,0.40); font-size: 11px; }
.lcp-stage-now {
  font-size: 22px; font-weight: 900;
  color: #fbbf24; letter-spacing: -0.3px;
  filter: drop-shadow(0 0 10px rgba(251,191,36,0.30));
  text-transform: uppercase;
  line-height: 1.1;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  min-width: 0;
}
.lcp-chevron {
  color: #fbbf24; font-size: 12px; opacity: 0.6;
  transition: transform 0.25s, opacity 0.2s;
  flex-shrink: 0;
}
.lcp-stage-trigger.is-open .lcp-chevron { transform: rotate(180deg); opacity: 1; }

.lcp-stats { display: flex; gap: 6px; align-items: center; flex-shrink: 0; margin-top: 18px; }
.lcp-stat {
  padding: 5px 9px; border-radius: 6px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
  font-size: 10px; font-weight: 800;
  color: rgba(255,255,255,0.85);
  white-space: nowrap;
}
.lcp-stat strong { color: #fbbf24; }

/* V3 flow: línea fina + orbe corriendo + dot pulsante */
/* V5-P1 · 2026-05-15 (Victor) · Pipeline multi-color segmentado · cada etapa con su color */
.lcp-flow { padding: 10px 0 0; }
.lcp-flow-track {
  position: relative; height: 14px;
  display: flex; align-items: center;
}
.lcp-flow-segments {
  display: grid; gap: 0;
  width: 100%; height: 3px;
  border-radius: 2px; overflow: hidden;
  background: rgba(255,255,255,0.06);
}
.lcp-flow-seg {
  height: 100%;
  transition: filter 0.2s;
}
.lcp-flow-seg:hover { filter: brightness(1.2); }
.lcp-flow-seg.is-clickable { cursor: pointer; }
.lcp-flow-seg.is-clickable:hover { filter: brightness(1.35); }
/* Markers: centrados verticalmente sobre la barra (sobresalen arriba/abajo) */
.lcp-flow-markers {
  position: absolute; left: 0; right: 0; top: 50%;
  transform: translateY(-50%);
  display: grid; gap: 0;
  pointer-events: none;
}
.lcp-flow-marker {
  width: 1px; height: 7px;
  background: rgba(255,255,255,0.22);
  margin: 0 auto;
  cursor: pointer;
  pointer-events: auto;
  transition: all 0.2s;
}
.lcp-flow-marker:hover { height: 9px; }
.lcp-flow-marker.is-done { background: rgba(255,255,255,0.40); }
.lcp-flow-marker.is-active {
  width: 3px; height: 11px;
  border-radius: 2px;
  /* background y box-shadow se setean inline desde el template con el color de la etapa */
}

/* Stat "X / Y" se tinta con el color de la etapa activa */
.lcp-stat-count strong { color: var(--stage-color, #fbbf24); }

/* Indicador de stages ocultos (preservado) */
.lcp-window-hint {
  display: flex; justify-content: space-between; align-items: center;
  margin-top: 12px;
  font-size: 9px; color: rgba(255,255,255,0.45);
  font-family: ui-monospace, SFMono-Regular, monospace;
}
.lcp-saving { color: #fbbf24; font-weight: 700; }

/* Live notification (preservado, dorado-friendly) */
.lcp-live-flash {
  position: relative; z-index: 2;
  margin: 0 22px 12px;
  display: flex; align-items: center; gap: 8px;
  padding: 8px 12px;
  border-radius: 10px;
  border: 1px solid;
}
.lcp-live-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.lcp-live-text { font-size: 11px; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lcp-live-text b { font-weight: 700; }
.lcp-live-tag { font-family: ui-monospace, SFMono-Regular, monospace; font-size: 9px; opacity: 0.7; flex-shrink: 0; }

/* K2 · Cards 3D Carrusel · pixel-perfect del mockup stage-switcher-K2-cards-3d.html */
.k2-overlay {
  position: fixed; inset: 0; z-index: 9999;
  background: rgba(0,0,0,0.78);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  display: flex; align-items: center; justify-content: center;
  padding: 32px;
  perspective: 1400px;
  outline: none;
  cursor: grab;
  user-select: none;
}
.k2-overlay:active { cursor: grabbing; }
.k2-overlay .k2-card,
.k2-overlay .k2-mini-stage,
.k2-overlay .k2-nav-arrow,
.k2-overlay .k2-dot,
.k2-overlay .k2-close-corner { cursor: pointer; }
.k2-fade-enter-active, .k2-fade-leave-active { transition: opacity 0.25s ease-out; }
.k2-fade-enter-from, .k2-fade-leave-to { opacity: 0; }

.k2-close-corner {
  position: absolute; top: 18px; right: 22px;
  width: 36px; height: 36px;
  border-radius: 50%;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.75);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  font-size: 22px; line-height: 1; padding: 0;
  font-family: inherit;
  transition: all 0.18s;
  z-index: 10;
}
.k2-close-corner:hover {
  background: rgba(239,68,68,0.25); color: #fff;
  border-color: rgba(239,68,68,0.50); transform: rotate(90deg);
}

.k2-stage {
  width: 100%; max-width: 1100px;
  transform-style: preserve-3d;
  animation: k2-rise 0.45s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes k2-rise {
  from { opacity: 0; transform: translateY(40px) rotateX(15deg) scale(0.92); }
  to { opacity: 1; transform: translateY(0) rotateX(0) scale(1); }
}

.k2-eyebrow {
  text-align: center;
  font-size: 11px; letter-spacing: 3px;
  color: rgba(255,255,255,0.55);
  margin-bottom: 8px;
  font-weight: 800; text-transform: uppercase;
}
.k2-title-h {
  text-align: center;
  font-size: 28px; font-weight: 900;
  background: linear-gradient(135deg, #fbbf24 0%, #fff 50%, #a855f7 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 4px;
  line-height: 1.2;
}
.k2-help {
  text-align: center;
  font-size: 12px; color: rgba(255,255,255,0.50);
  margin-bottom: 32px;
}

.k2-carousel {
  display: flex; gap: 18px;
  justify-content: center; align-items: center;
  transform-style: preserve-3d; perspective: 1400px;
  min-height: 360px;
}

.k2-card {
  width: 220px; min-height: 320px;
  padding: 22px 18px;
  background: linear-gradient(155deg, rgba(20,20,20,0.95), rgba(8,8,8,0.95));
  border: 1.5px solid rgba(255,255,255,0.10);
  border-radius: 18px;
  position: relative; cursor: pointer;
  transition: all 0.32s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-style: preserve-3d; overflow: hidden;
  flex-shrink: 0;
}
.k2-card::before {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  border-radius: 18px;
  background:
    linear-gradient(125deg, transparent 8%, rgba(255,255,255,0.06) 18%, transparent 22%),
    radial-gradient(circle at 100% 0%, rgba(168,85,247,0.10), transparent 60%);
}

.k2-card.k2-pos--2 { transform: rotateY(18deg) translateZ(-30px); opacity: 0.55; }
.k2-card.k2-pos--1 { transform: rotateY(8deg) translateZ(-10px); opacity: 0.80; }
.k2-card.k2-pos-0 {
  transform: scale(1.10) translateZ(20px);
  border-color: rgba(251,191,36,0.55);
  box-shadow: 0 24px 60px rgba(0,0,0,0.7), 0 0 60px rgba(251,191,36,0.40);
  z-index: 5;
}
.k2-card.k2-pos-0::before {
  background:
    linear-gradient(125deg, transparent 8%, rgba(255,255,255,0.10) 18%, transparent 22%),
    radial-gradient(circle at 100% 0%, rgba(251,191,36,0.20), transparent 55%);
}
.k2-card.k2-pos-1 { transform: rotateY(-8deg) translateZ(-10px); opacity: 0.80; }
.k2-card.k2-pos-2 { transform: rotateY(-18deg) translateZ(-30px); opacity: 0.55; }

.k2-card:hover:not(.k2-pos-0) {
  transform: rotateY(0) translateZ(0);
  opacity: 1;
}

.k2-card-head {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 14px;
  position: relative;
}
.k2-card-icon {
  width: 50px; height: 50px;
  border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  font-size: 22px;
  background: linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02));
  border: 1px solid rgba(255,255,255,0.10);
}
.k2-card.k2-pos-0 .k2-card-icon {
  background: linear-gradient(135deg, rgba(251,191,36,0.20), rgba(217,119,6,0.05));
  border-color: rgba(251,191,36,0.50);
  box-shadow: 0 0 20px rgba(251,191,36,0.40);
}
.k2-card-active-tag {
  padding: 3px 8px;
  background: rgba(251,191,36,0.20);
  border: 1px solid rgba(251,191,36,0.50);
  border-radius: 5px;
  font-size: 8.5px; font-weight: 800;
  color: #fbbf24; letter-spacing: 0.6px;
}

.k2-card-name {
  position: relative;
  font-size: 16px; font-weight: 900;
  color: #fff;
  letter-spacing: 0.3px;
  line-height: 1.3;
  margin-bottom: 4px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.k2-card.k2-pos-0 .k2-card-name {
  color: #fbbf24;
  text-shadow: 0 0 10px rgba(251,191,36,0.30);
}
.k2-card-meta {
  position: relative;
  font-size: 10px; color: rgba(255,255,255,0.50);
  margin-bottom: 14px;
}

.k2-stages-preview {
  position: relative;
  display: flex; flex-direction: column; gap: 4px;
  max-height: 280px; overflow-y: auto;
}
.k2-stages-preview::-webkit-scrollbar { width: 4px; }
.k2-stages-preview::-webkit-scrollbar-track { background: transparent; }
.k2-stages-preview::-webkit-scrollbar-thumb { background: rgba(251,191,36,0.30); border-radius: 2px; }
.k2-stages-preview-label {
  font-size: 9px; letter-spacing: 1.2px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.40);
  font-weight: 800;
  margin-bottom: 4px;
  flex-shrink: 0;
}
.k2-mini-stage {
  display: flex; align-items: center; gap: 7px;
  padding: 5px 8px;
  background: rgba(255,255,255,0.03);
  border-radius: 5px;
  border: 1px solid rgba(255,255,255,0.05);
  transition: all 0.18s;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  flex-shrink: 0;
}
.k2-mini-stage:disabled,
.k2-mini-stage.is-disabled {
  cursor: default;
}
.k2-mini-stage-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  box-shadow: 0 0 6px currentColor;
}
.k2-mini-stage-name {
  font-size: 9.5px; font-weight: 700;
  color: rgba(255,255,255,0.75);
  flex: 1;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.k2-here {
  margin-left: 4px;
  font-size: 9px; color: #fbbf24;
  font-weight: 800;
}
.k2-mini-stage-arrow {
  font-size: 11px; color: rgba(251,191,36,0.50);
  font-weight: 800;
  opacity: 0; transition: all 0.18s;
}
.k2-mini-stage.is-on {
  background: linear-gradient(90deg, rgba(251,191,36,0.18), rgba(251,191,36,0.04));
  border-color: rgba(251,191,36,0.40);
}
.k2-mini-stage.is-on .k2-mini-stage-name { color: #fbbf24; }

.k2-card.k2-pos-0 .k2-mini-stage:not(.is-on):not(:disabled):hover {
  background: rgba(251,191,36,0.10);
  border-color: rgba(251,191,36,0.30);
  transform: translateX(2px);
}
.k2-card.k2-pos-0 .k2-mini-stage:not(:disabled):hover .k2-mini-stage-arrow {
  opacity: 1;
  color: #fbbf24;
}
.k2-stages-more {
  font-size: 9px; color: rgba(255,255,255,0.40);
  text-align: center;
  padding: 4px;
  font-style: italic;
  font-weight: 700;
}

/* Navegación · arrows + dots */
.k2-nav {
  display: flex; justify-content: center; align-items: center;
  gap: 14px;
  margin-top: 24px;
}
.k2-nav-arrow {
  width: 36px; height: 36px;
  border-radius: 50%;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.75);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  font-size: 22px; line-height: 1; padding: 0;
  font-family: inherit;
  transition: all 0.18s;
}
.k2-nav-arrow:hover {
  background: rgba(251,191,36,0.15);
  border-color: rgba(251,191,36,0.40);
  color: #fbbf24;
}
.k2-dots { display: flex; gap: 8px; }
.k2-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
  background: rgba(255,255,255,0.15);
  border: 1.5px solid rgba(255,255,255,0.20);
  cursor: pointer;
  transition: all 0.18s;
}
.k2-dot:hover { background: rgba(251,191,36,0.30); }
.k2-dot.is-on {
  background: linear-gradient(135deg, #fbbf24, #d97706);
  border-color: #fbbf24;
  transform: scale(1.4);
  box-shadow: 0 0 12px rgba(251,191,36,0.55);
}

/* Tabs · M1.B dorado, más grandes (13px en vez de 12px) */
.lcp-tabs {
  display: flex;
  padding: 0 22px;
  background: #0a0a0a;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  overflow-x: auto;
  flex-shrink: 0;
  scrollbar-width: thin;
}
.lcp-tab {
  padding: 14px 16px;
  font-size: 13px; font-weight: 800;
  color: rgba(255,255,255,0.45);
  letter-spacing: 0.5px;
  cursor: pointer;
  border: none;
  background: transparent;
  border-bottom: 2.5px solid transparent;
  transition: all 0.2s;
  white-space: nowrap;
  font-family: inherit;
}
.lcp-tab:hover { color: rgba(255,255,255,0.75); }
.lcp-tab.is-active {
  color: #fbbf24;
  border-bottom-color: #fbbf24;
  filter: drop-shadow(0 0 8px rgba(251,191,36,0.40));
}
.lcp-tab-add {
  margin-left: auto;
  padding: 14px 14px;
  font-size: 11px; font-weight: 800;
  color: rgba(255,255,255,0.40);
  background: transparent; border: none;
  cursor: pointer;
  letter-spacing: 0.4px;
  white-space: nowrap;
  font-family: inherit;
  transition: color 0.2s;
}
.lcp-tab-add:hover { color: #fbbf24; }

/* ─────────────────────────────────────────────────────────────────
   Y1 · Compacto Refinado · Tarjeta completa M1.B
   Pixel-perfect del mockup lead-card-Y1-compacto-refinado.html
   ───────────────────────────────────────────────────────────────── */

/* ASIDE container — fuerza fondo negro M1.B independiente del tema */
.lcp-aside {
  background: #050505 !important;
  color: #fff;
  border-right: 1px solid rgba(251,191,36,0.18);
  position: relative;
}
.lcp-aside::before {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background: radial-gradient(circle at 100% 0%, rgba(251,191,36,0.08), transparent 60%);
  z-index: 0;
}
.lcp-aside > * { position: relative; z-index: 1; }

/* HEADER · padding 18 18 14, border-bottom blanco 6%, glow índigo en esquina */
.lcp-head {
  padding: 18px 18px 14px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  position: relative;
  flex-shrink: 0;
  background: radial-gradient(ellipse at 0% 0%, rgba(99,102,241,0.14), transparent 60%);
}
.lcp-head-row { display: flex; gap: 12px; align-items: flex-start; }

/* Avatar 56px gradient + halo dorado */
.lcp-avatar-wrap { position: relative; flex-shrink: 0; }
.lcp-avatar {
  position: relative;
  width: 56px; height: 56px;
  border-radius: 16px;
  background: linear-gradient(135deg, #6366f1, #4338ca, #7c3aed);
  display: flex; align-items: center; justify-content: center;
  font-weight: 900; font-size: 20px; color: #fff;
  box-shadow:
    0 0 0 2.5px rgba(251,191,36,0.40),
    0 0 0 4px #050505,
    0 0 18px rgba(99,102,241,0.40);
}
.lcp-avatar-online {
  position: absolute; bottom: -1px; right: -1px;
  width: 14px; height: 14px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 0 3px #050505, 0 0 8px #22c55e;
  animation: lcp-pulse-online 1.6s ease-in-out infinite;
}
@keyframes lcp-pulse-online {
  0%,100% { opacity: 1; }
  50% { opacity: 0.5; }
}

/* Nombre + canal */
.lcp-head-info {
  flex: 1; min-width: 0;
  padding-right: 60px; /* hueco para lead-corner absoluto */
}
.lcp-name {
  font-size: 18px; font-weight: 900;
  line-height: 1.15; color: #fff;
  letter-spacing: -0.2px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  margin: 0;
}
.lcp-name-editable { cursor: text; display: inline-flex; align-items: center; gap: 6px; }
.lcp-name-pencil {
  font-size: 12px; color: rgba(251,191,36,0.6); opacity: 0;
  transition: opacity .15s; flex-shrink: 0;
}
.lcp-name-editable:hover .lcp-name-pencil { opacity: 1; }
.lcp-name-input {
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(251,191,36,0.5);
  border-radius: 6px;
  padding: 2px 8px;
  outline: none;
  width: 100%;
  max-width: 280px;
}
.lcp-sub {
  margin-top: 3px;
  font-size: 11px; color: rgba(255,255,255,0.55);
  display: flex; align-items: center; gap: 5px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.lcp-sub-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #22c55e;
  box-shadow: 0 0 6px #22c55e;
  flex-shrink: 0;
}
.lcp-sub-text { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
/* Sucursal del cliente, línea dorada bajo el nombre (P1) */
.lcp-branch {
  margin-top: 6px;
  font-size: 13px; font-weight: 600;
  color: #e7cf9a;
  display: flex; align-items: center; gap: 6px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.lcp-branch-pin { color: #c9a35a; flex-shrink: 0; }
/* Botón "Abrir perfil" en red social (IG / FB / TikTok) */
.lcp-social-btn { display: inline-flex; align-items: center; gap: 6px; margin-top: 8px; padding: 6px 12px; border-radius: 999px; font-size: 11.5px; font-weight: 800; color: #fff; text-decoration: none; cursor: pointer; transition: 0.15s; }
.lcp-social-btn .lcp-social-ext { font-size: 11px; opacity: 0.85; }
.lcp-social-btn:hover { filter: brightness(1.08); }
.lcp-social-btn.instagram { background: linear-gradient(135deg, #feda75, #fa7e1e 25%, #d62976 55%, #962fbf 80%, #4f5bd5); box-shadow: 0 6px 16px rgba(214, 41, 118, 0.32); }
.lcp-social-btn.facebook { background: #1877f2; box-shadow: 0 6px 16px rgba(24, 119, 242, 0.32); }
.lcp-social-btn.tiktok { background: linear-gradient(135deg, #25f4ee, #000 55%, #fe2c55); box-shadow: 0 6px 16px rgba(254, 44, 85, 0.3); }
.lcp-social-btn.is-disabled { background: rgba(255, 255, 255, 0.06); color: rgba(255, 255, 255, 0.4); border: 1px solid rgba(255, 255, 255, 0.12); box-shadow: none; cursor: not-allowed; }
/* Label "Contacto" oculto visualmente pero accesible a screen readers */
.lcp-sub-label-sr {
  position: absolute !important;
  width: 1px; height: 1px;
  padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0,0,0,0);
  white-space: nowrap; border: 0;
}
.lcp-sub-phone {
  font-size: 12px;
  color: #fbbf24;
  font-weight: 600;
  letter-spacing: 0.3px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  min-width: 0;
}

/* Lead corner: absolute top-right */
.lcp-lead-corner {
  position: absolute; top: 12px; right: 16px;
  display: flex; flex-direction: column; align-items: flex-end;
  z-index: 2;
}
.lcp-lead-lbl {
  font-size: 8px; letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.40); font-weight: 700;
}
.lcp-lead-num {
  font-size: 24px; font-weight: 900;
  color: #fbbf24;
  line-height: 1; letter-spacing: -1px;
  filter: drop-shadow(0 4px 10px rgba(251,191,36,0.4));
  margin-top: 2px;
  font-family: ui-monospace, SFMono-Regular, monospace;
}
.lcp-lead-empty {
  font-size: 11px; font-style: italic;
  color: rgba(255,255,255,0.35);
  margin-top: 4px;
}
.lcp-actions { display: flex; gap: 6px; margin-top: 6px; }
.lcp-act-btn {
  width: 22px; height: 22px;
  border-radius: 6px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.55);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  font-size: 11px; line-height: 1; padding: 0;
  font-family: inherit;
  transition: all 0.15s;
}
.lcp-act-btn:hover {
  color: #fbbf24;
  border-color: rgba(251,191,36,0.40);
  background: rgba(251,191,36,0.08);
}
/* Variante danger: papelera roja para eliminar contacto e historial */
.lcp-act-btn--danger {
  background: rgba(239,68,68,0.05);
  border-color: rgba(239,68,68,0.18);
  color: rgba(239,68,68,0.65);
}
.lcp-act-btn--danger:hover {
  color: #fff;
  background: rgba(239,68,68,0.85);
  border-color: #ef4444;
  box-shadow: 0 0 10px rgba(239,68,68,0.4);
}
/* Variante success: ✓ verde para marcar cerrada (L1 · M1.B) */
.lcp-act-btn--success {
  background: rgba(16,185,129,0.06);
  border-color: rgba(16,185,129,0.25);
  color: rgba(16,185,129,0.85);
  font-weight: 800;
}
.lcp-act-btn--success:hover {
  color: #fff;
  background: rgba(16,185,129,0.85);
  border-color: #10b981;
  box-shadow: 0 0 10px rgba(16,185,129,0.4);
}

/* Modal "marcar cerrada" · L1 · cristal negro + verde esmeralda */
.lcp-close-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.7);
  display: flex; align-items: center; justify-content: center;
  z-index: 10000;
  padding: 20px;
  backdrop-filter: blur(4px);
}
.lcp-close-modal {
  width: 100%; max-width: 460px;
  background: linear-gradient(180deg, #0d0d0d 0%, #060606 100%);
  border: 1px solid rgba(16,185,129,0.32);
  border-radius: 18px;
  padding: 28px;
  position: relative; overflow: hidden;
  box-shadow:
    inset 0 1px 0 rgba(16,185,129,0.12),
    0 24px 60px rgba(0,0,0,0.7);
}
.lcp-close-modal::before {
  content: "";
  position: absolute; inset: 0;
  background: radial-gradient(circle at 50% 0%, rgba(16,185,129,0.10), transparent 60%);
  pointer-events: none;
}
.lcp-close-icon {
  width: 56px; height: 56px; border-radius: 16px;
  background: linear-gradient(135deg, #10b981, #047857);
  display: flex; align-items: center; justify-content: center;
  font-size: 26px; color: #fff; font-weight: 900;
  margin: 0 auto 18px;
  box-shadow: 0 8px 20px rgba(16,185,129,0.35);
  position: relative; z-index: 1;
}
.lcp-close-title {
  text-align: center;
  font-size: 18px; font-weight: 800; color: #fff;
  margin: 0 0 8px;
  position: relative; z-index: 1;
}
.lcp-close-desc {
  text-align: center;
  font-size: 13px; color: rgba(255,255,255,0.65);
  margin-bottom: 22px;
  line-height: 1.5;
  position: relative; z-index: 1;
}
.lcp-close-desc b { color: #6ee7b7; font-weight: 700; }
.lcp-close-reason-lbl {
  font-size: 11px; letter-spacing: 1.5px; text-transform: uppercase;
  color: rgba(251,191,36,0.75); font-weight: 700;
  margin-bottom: 10px;
  position: relative; z-index: 1;
}
.lcp-close-reason-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
  margin-bottom: 20px;
  position: relative; z-index: 1;
}
.lcp-close-reason {
  padding: 10px 12px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  color: rgba(255,255,255,0.75);
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  text-align: left;
  display: flex; align-items: center; gap: 8px;
  font-family: inherit;
  transition: all 0.15s;
}
.lcp-close-reason:hover:not(:disabled):not(.is-selected) {
  background: rgba(16,185,129,0.06);
  border-color: rgba(16,185,129,0.3);
  color: #fff;
}
.lcp-close-reason.is-selected {
  background: linear-gradient(180deg, rgba(16,185,129,0.20), rgba(16,185,129,0.06));
  border-color: rgba(16,185,129,0.55);
  color: #fff;
  box-shadow: inset 0 1px 0 rgba(16,185,129,0.2);
}
.lcp-close-reason:disabled { opacity: 0.6; cursor: not-allowed; }
.lcp-close-reason-emoji { font-size: 15px; flex-shrink: 0; }
.lcp-close-reason-label { flex: 1; line-height: 1.3; }
.lcp-close-actions {
  display: flex; gap: 10px;
  position: relative; z-index: 1;
}
.lcp-close-cancel {
  flex: 1; padding: 12px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  color: rgba(255,255,255,0.7);
  font-size: 12px; font-weight: 600;
  cursor: pointer;
  font-family: inherit;
}
.lcp-close-cancel:disabled { opacity: 0.5; cursor: wait; }
.lcp-close-confirm {
  flex: 2; padding: 12px;
  background: linear-gradient(180deg, #10b981, #047857);
  border: 1px solid rgba(16,185,129,0.6);
  border-radius: 10px;
  color: #fff;
  font-size: 12px; font-weight: 800;
  cursor: pointer;
  font-family: inherit;
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.3),
    0 4px 12px rgba(16,185,129,0.3);
  transition: transform 0.15s;
}
.lcp-close-confirm:hover:not(:disabled) { transform: translateY(-1px); }
.lcp-close-confirm:disabled { opacity: 0.6; cursor: wait; }

/* Selector de deal (si hay múltiples) */
.lcp-deal-selector { margin-top: 12px; }
.lcp-deal-select {
  width: 100%;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(251,191,36,0.30);
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 12px; color: #fff;
  font-family: inherit;
  cursor: pointer;
}
.lcp-deal-select option { background: #0a0a0a; color: #fff; }

/* TAGS · chip pill M1.B */
.lcp-tags {
  display: flex; flex-wrap: wrap; align-items: center;
  gap: 6px; margin-top: 12px;
  position: relative;
}
.lcp-tag-chip {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 10px; font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  border: 1px solid;
}
.lcp-tag-x {
  background: transparent; border: none;
  color: inherit;
  opacity: 0; cursor: pointer;
  font-size: 12px; line-height: 1;
  padding: 0 2px;
  transition: opacity 0.15s;
}
.lcp-tag-chip:hover .lcp-tag-x { opacity: 0.85; }
.lcp-tag-x:hover { opacity: 1 !important; }
.lcp-tag-add {
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 10px; font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  background: transparent;
  color: rgba(255,255,255,0.40);
  border: 1px dashed rgba(255,255,255,0.20);
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}
.lcp-tag-add:hover {
  color: #fbbf24;
  border-color: rgba(251,191,36,0.40);
}

.lcp-tag-picker-wrap { position: relative; }
.lcp-label-picker {
  position: fixed;
  width: 264px;
  background: rgba(15,15,15,0.98);
  border: 1.5px solid rgba(251,191,36,0.40);
  border-radius: 10px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.8);
  z-index: 10000;
  max-height: 320px;
  overflow: hidden;
  display: flex; flex-direction: column;
}
.lcp-label-picker-search {
  padding: 8px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.lcp-label-input {
  width: 100%;
  padding: 6px 10px;
  font-size: 11px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 6px;
  color: #fff;
  font-family: inherit;
}
.lcp-label-input:focus {
  outline: none;
  border-color: rgba(251,191,36,0.55);
}
.lcp-label-list {
  flex: 1; overflow-y: auto;
  padding: 4px;
}
.lcp-label-item {
  width: 100%;
  display: flex; align-items: center; gap: 8px;
  padding: 7px 10px;
  font-size: 11px;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: rgba(255,255,255,0.85);
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}
.lcp-label-item:hover { background: rgba(251,191,36,0.10); }
.lcp-label-item.is-on {
  background: rgba(251,191,36,0.15);
  color: #fbbf24;
}
.lcp-label-color {
  width: 12px; height: 12px;
  border-radius: 3px;
  flex-shrink: 0;
}
.lcp-label-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lcp-label-check { color: #fbbf24; font-weight: 900; }
.lcp-label-empty {
  font-size: 11px; color: rgba(255,255,255,0.40);
  font-style: italic; text-align: center; padding: 12px;
}
.lcp-label-create {
  display: block;
  padding: 8px 12px;
  font-size: 10px;
  color: #fbbf24;
  border-top: 1px solid rgba(255,255,255,0.06);
  text-decoration: none;
}
.lcp-label-create:hover { background: rgba(251,191,36,0.05); }

/* EMPTY STATE · Asignar a embudo */
.lcp-assign-empty {
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  background: linear-gradient(180deg, rgba(251,191,36,0.05), transparent);
}
.lcp-assign-title {
  font-size: 11px; letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(251,191,36,0.85); font-weight: 800;
  margin-bottom: 10px;
}
.lcp-assign-list {
  display: flex; flex-direction: column; gap: 6px;
  max-height: 280px; overflow-y: auto;
}
.lcp-assign-row {
  width: 100%;
  display: flex; align-items: center; justify-content: space-between;
  padding: 9px 12px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 8px;
  font-size: 12px; color: rgba(255,255,255,0.85);
  cursor: pointer; text-align: left;
  font-family: inherit;
  transition: all 0.15s;
}
.lcp-assign-row:hover {
  background: rgba(251,191,36,0.10);
  border-color: rgba(251,191,36,0.40);
  transform: translateX(2px);
}
.lcp-assign-name { font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.lcp-assign-meta { color: rgba(255,255,255,0.45); font-size: 10px; flex-shrink: 0; margin-left: 10px; }
.lcp-assign-noop {
  font-size: 11px; color: rgba(255,255,255,0.40);
  font-style: italic; text-align: center; padding: 12px;
}

/* BODY · scroll */
.lcp-body { background: #050505; }
/* Tarjeta del depósito (Fase C · Victor 2026-06-09) · verde-dinero */
/* Tarjeta del depósito · P3 "Compacta mínima" (Victor 2026-06-12): sutil, fondo
   neutro (sin el bloque verde grande), 2 líneas + chips. */
.lcp-baucher {
  margin: 12px 12px 6px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 13px;
  background: #111114;
  overflow: hidden;
  padding: 13px 15px;
}
.lcp-baucher-ln1 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.60);
}
.lcp-baucher-ic { font-size: 12px; }
.lcp-baucher-title {
  font-weight: 600;
  letter-spacing: 0.02em;
  color: rgba(255, 255, 255, 0.60);
}
.lcp-baucher-when {
  margin-left: auto;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.38);
  white-space: nowrap;
}
.lcp-baucher-total {
  margin-left: auto;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.45);
  white-space: nowrap;
}
.lcp-baucher-total b {
  color: #5aa377;
  font-weight: 700;
}
.lcp-baucher-ln2 {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 9px;
  flex-wrap: wrap;
}
.lcp-baucher-monto {
  font-size: 21px;
  font-weight: 700;
  color: #f4f4f5;
  letter-spacing: -0.02em;
  line-height: 1;
}
/* Monto clickeable (Victor 2026-06-12): salta al comprobante en el chat + parpadeo. */
.lcp-baucher-monto.is-link {
  cursor: pointer;
  border-bottom: 1.5px dotted rgba(201, 163, 90, 0.45);
  transition: color 0.15s;
}
.lcp-baucher-monto.is-link:hover {
  color: #e8c98a;
}
.lcp-baucher-tag {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.07);
  padding: 3px 9px;
  border-radius: 7px;
  white-space: nowrap;
}
.lcp-baucher-tag b {
  color: rgba(255, 255, 255, 0.38);
  font-weight: 600;
  margin-right: 4px;
}
/* Acordeón: los demás comprobantes (varios pagos) */
.lcp-baucher-acc {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 0 2px;
  margin-top: 9px;
  cursor: pointer;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  user-select: none;
}
.lcp-baucher-acc-t {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.50);
  font-weight: 500;
}
.lcp-baucher-acc-chev {
  margin-left: auto;
  color: rgba(255, 255, 255, 0.34);
  font-size: 9px;
  transition: transform 0.22s ease;
}
.lcp-baucher.is-open .lcp-baucher-acc-chev {
  transform: rotate(90deg);
}
.lcp-baucher-row {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}
.lcp-baucher-row.is-link {
  cursor: pointer;
}
.lcp-baucher-row.is-link:hover {
  opacity: 0.8;
}
.lcp-baucher-row-idx {
  font-size: 9px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.34);
  width: 18px;
}
.lcp-baucher-row-m {
  font-size: 13px;
  font-weight: 700;
  color: #5aa377;
}
.lcp-baucher-row-dt {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.34);
  margin-left: auto;
  white-space: nowrap;
}
/* Botón Reservar cliente (Fase 1 · 2026-06-07) */
.lcp-reservar-btn {
  display: block;
  width: calc(100% - 24px);
  margin: 12px 12px 4px;
  padding: 10px;
  border: 1px solid rgba(201, 163, 90, 0.5);
  border-radius: 10px;
  background: rgba(201, 163, 90, 0.12);
  color: #d8bd86;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.14s;
}
.lcp-reservar-btn:hover {
  background: rgba(201, 163, 90, 0.2);
  border-color: rgba(201, 163, 90, 0.7);
}
/* Tarjeta "Reserva" en Principal */
.lcp-reserva-card {
  margin: 8px 12px 10px;
  padding: 11px 13px;
  border: 1px solid rgba(201, 163, 90, 0.35);
  border-radius: 11px;
  background: linear-gradient(180deg, rgba(201, 163, 90, 0.07), rgba(201, 163, 90, 0.02));
}
.lcp-reserva-head { display: flex; align-items: center; margin-bottom: 8px; }
.lcp-reserva-title { font-size: 10px; letter-spacing: 1.1px; text-transform: uppercase; font-weight: 800; color: #d8bd86; }
.lcp-reserva-count { margin-left: auto; font-size: 9px; color: #8b8b91; border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 999px; padding: 1px 7px; }
.lcp-reserva-row { display: flex; justify-content: space-between; align-items: baseline; gap: 10px; padding: 3px 0; font-size: 12px; }
.lcp-reserva-row .k { color: #8b8b91; flex: none; }
.lcp-reserva-row .v { color: #fff; font-weight: 600; text-align: right; }
.lcp-reserva-row .v.green { color: #5fae7e; }
.lcp-reserva-row .v.red { color: #cc6b6b; }
.lcp-reserva-row .v.gold { color: #d8bd86; }
.lcp-reserva-sep { height: 1px; background: rgba(255, 255, 255, 0.06); margin: 6px 0; }
.lcp-reserva-subh { font-size: 8.5px; letter-spacing: 1px; text-transform: uppercase; color: #6c6c72; font-weight: 700; margin: 2px 0 4px; }
.lcp-abono-row .k { font-size: 11px; }
.lcp-abono-btn {
  display: block;
  width: 100%;
  margin: 9px 0 2px;
  padding: 7px;
  border: 1px solid rgba(95, 174, 126, 0.4);
  border-radius: 8px;
  background: rgba(95, 174, 126, 0.12);
  color: #5fae7e;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.14s;
}
.lcp-abono-btn:hover { background: rgba(95, 174, 126, 0.2); }
.lcp-recibo-btn {
  display: block;
  width: 100%;
  margin: 9px 0 2px;
  padding: 8px;
  border: 1px solid rgba(201, 163, 90, 0.5);
  border-radius: 8px;
  background: rgba(201, 163, 90, 0.12);
  color: #d8bd86;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.14s;
}
.lcp-recibo-btn:hover { background: rgba(201, 163, 90, 0.2); }
.lcp-recibo-btn:disabled { opacity: 0.5; cursor: default; }

/* FIELDS · M1.B */
.lcp-fields {
  padding: 8px 18px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
/* Reservas múltiples · acordeón delicado estilo sucursal (P1) */
.res-acc { border-top: 1px solid rgba(255,255,255,0.06); }
.res-acc:first-child { border-top: none; }
.res-head {
  display: flex; align-items: center; gap: 9px;
  padding: 11px 4px; cursor: pointer; user-select: none;
}
.res-head:hover { background: rgba(255,255,255,0.02); }
.res-chev { color: #646973; font-size: 10px; transition: transform .2s; width: 10px; }
.res-acc.res-open .res-chev { transform: rotate(90deg); }
.res-title {
  font-size: 11.5px; font-weight: 700; letter-spacing: 1.2px;
  color: #8b8f98; text-transform: uppercase;
}
.res-acc.res-open .res-title { color: #e7cf9a; }
.res-dot { width: 6px; height: 6px; border-radius: 50%; background: #c9a35a; box-shadow: 0 0 6px rgba(201,163,90,.6); }
.res-count { margin-left: auto; font-size: 10.5px; color: #646973; }
.res-body { padding: 2px 0 8px; }
.res-add {
  display: flex; align-items: center; gap: 8px;
  margin: 8px 2px 2px; padding: 10px 12px; cursor: pointer;
  border: 1px dashed rgba(201,163,90,.32); border-radius: 10px;
  color: #e7cf9a; font-size: 12.5px; font-weight: 600;
  background: rgba(201,163,90,.04); transition: .15s;
}
.res-add:hover { background: rgba(201,163,90,.10); border-color: rgba(201,163,90,.55); }
.res-add-plus { font-size: 15px; line-height: 1; }
.lcp-field-row {
  display: grid;
  grid-template-columns: 110px 1fr auto;
  gap: 10px;
  align-items: center;
  padding: 7px 0;
  font-size: 11px;
}
.lcp-field-row-2col {
  grid-template-columns: 110px 1fr;
}
.lcp-field-key {
  color: rgba(255,255,255,0.50);
  font-weight: 600;
  letter-spacing: 0.2px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.lcp-field-val,
.lcp-field-val-wrap {
  color: rgba(255,255,255,0.85);
  overflow: hidden;
  min-width: 0;
}
.lcp-field-val { text-overflow: ellipsis; white-space: nowrap; }
.lcp-field-val.is-empty { color: rgba(255,255,255,0.35); font-style: italic; }
.lcp-field-val.is-gold { color: #fbbf24; font-weight: 700; }
.lcp-field-val.is-green { color: #4ade80; font-weight: 700; }
.lcp-field-val.is-mono { font-family: ui-monospace, SFMono-Regular, monospace; color: rgba(255,255,255,0.75); }

/* Responsable del lead (H3 · M1.B) · misma fila pero con tinte dorado sutil */
.lcp-field-resp {
  background: linear-gradient(90deg, rgba(251,191,36,0.07), rgba(251,191,36,0.01));
  border-left: 2px solid rgba(251,191,36,0.40);
  margin: 2px -4px 2px -18px;
  padding-left: 16px;
  padding-right: 4px;
  border-radius: 0 6px 6px 0;
}
.lcp-field-resp.is-empty-resp {
  background: transparent;
  border-left-color: rgba(255,255,255,0.10);
}
/* Primary assignee mismatch (sticky · Victor 2026-05-14): alerta sutil cuando
   el agente principal NO está atendiendo (fallback round-robin). */
.lcp-field-resp.is-primary-mismatch {
  background: linear-gradient(90deg, rgba(244,114,182,0.10), rgba(244,114,182,0.01));
  border-left-color: rgba(244,114,182,0.55);
}
.lcp-field-resp.is-primary-mismatch .lcp-resp-name {
  color: #f472b6;
}
.lcp-field-resp.is-primary-mismatch .lcp-resp-avatar {
  background: linear-gradient(135deg, #f472b6, #db2777);
  box-shadow: 0 0 0 1px #050505, 0 0 0 2px rgba(244,114,182,0.40);
}
.lcp-field-val--resp {
  display: inline-flex !important;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
}
.lcp-resp-avatar {
  width: 20px; height: 20px;
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  font-weight: 800; font-size: 9px; color: #fff;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  box-shadow: 0 0 0 1px #050505, 0 0 0 2px rgba(251,191,36,0.40);
  flex-shrink: 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}
.lcp-resp-name {
  font-weight: 700;
  color: #fbbf24;
  font-size: 12px;
  letter-spacing: 0.1px;
  overflow: hidden; text-overflow: ellipsis;
}
.lcp-resp-auto {
  font-size: 9px;
  color: rgba(255,255,255,0.40);
  font-style: italic;
  letter-spacing: 0.2px;
  white-space: nowrap;
}

/* ================================================================== */
/* V5-P1 · 2026-05-15 (Victor) · MONOCROMO MINIMAL REDESIGN OVERRIDE
   Aplica el estilo "tipo empresa" del mockup lead-panel-redesign-completo:
   - Quita avatar grande del header
   - LEAD #121 mini text gris (en lugar de banner dorado)
   - DTO. de Ventas / Leads Entrantes con jerarquía sutil
   - Quita backgrounds amarillos de filas (Responsable, Agente)
   - Quita avatares VG circulares en filas
   - Valores monocromo (no dorado/verde)
   - Footer buttons solo en hover
   - Glow dorado del panel removido
   No requiere cambios de template (solo CSS).
*/
/* ================================================================== */

/* Quitar glow dorado ambiental del panel */
.lcp-aside::before { display: none; }
.lcp-pipe-wrap::before { display: none; }
.lcp-head { background: none; }

/* 1. Header · Quitar avatar grande */
.lcp-avatar-wrap { display: none !important; }
.lcp-head-info { padding-right: 0; }

/* 2. Header · Nombre + phone sutiles */
.lcp-name { font-size: 16px !important; font-weight: 600 !important; letter-spacing: -0.3px !important; }
.lcp-sub {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
  font-size: 11.5px !important;
  color: rgba(255,255,255,0.45) !important;
  margin-top: 3px !important;
}
.lcp-sub-dot { display: none !important; }
.lcp-sub-phone {
  color: rgba(255,255,255,0.45) !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  font-size: 11.5px !important;
}
.lcp-avatar-online { display: none !important; }

/* 3. Header · Lead corner: mini text gris (no banner dorado enorme) */
.lcp-lead-corner { top: 18px !important; right: 18px !important; }
.lcp-lead-lbl {
  font-size: 9px !important;
  letter-spacing: 1.5px !important;
  color: rgba(255,255,255,0.35) !important;
}
.lcp-lead-num {
  font-size: 11px !important;
  font-weight: 600 !important;
  color: rgba(255,255,255,0.50) !important;
  letter-spacing: 0.8px !important;
  filter: none !important;
  margin-top: 2px !important;
  font-family: inherit !important;
}

/* 4. Tags (RD, etiquetas) y "+ etiqueta" sutiles */
.lcp-tag-chip {
  /* color real de la etiqueta: lo aplica el :style con colorOf (Victor 2026-05-16) */
  font-weight: 600 !important;
  font-size: 10.5px !important;
}
.lcp-tag-add {
  background: transparent !important;
  color: rgba(255,255,255,0.45) !important;
  border: 1px dashed rgba(255,255,255,0.15) !important;
  font-weight: 500 !important;
  font-size: 10.5px !important;
}

/* 5. Pipeline · Header del embudo: "DTO. de Ventas" tiny, "Leads Entrantes" normal */
.lcp-pipe { padding: 12px 18px 14px !important; }
.lcp-ph-icon-name {
  font-size: 9.5px !important;
  font-weight: 700 !important;
  letter-spacing: 1.5px !important;
  text-transform: uppercase !important;
  color: rgba(255,255,255,0.45) !important;
  margin-bottom: 4px !important;
}
.lcp-stage-trigger { padding: 0 !important; background: transparent !important; }
.lcp-stage-trigger:hover { background: transparent !important; padding: 0 !important; }
.lcp-stage-trigger.is-open { background: transparent !important; padding: 0 !important; }
.lcp-stage-now {
  font-size: 13.5px !important;
  font-weight: 600 !important;
  color: #fff !important;
  letter-spacing: -0.2px !important;
  text-transform: none !important;
  text-shadow: none !important;
}
.lcp-chevron { font-size: 9px !important; color: rgba(255,255,255,0.40) !important; }

/* 6. Pipeline · Stats sutiles ("0d col" + "1 / 10") */
.lcp-stats { margin-top: 0 !important; }
.lcp-stat {
  font-size: 10.5px !important;
  padding: 0 !important;
  background: transparent !important;
  border: 0 !important;
  color: rgba(255,255,255,0.50) !important;
}
.lcp-stat strong { color: rgba(255,255,255,0.85) !important; font-weight: 600 !important; }
.lcp-stat-count strong { color: #fbbf24 !important; font-weight: 700 !important; }

/* 7. Tabs · Variante 4 · gradient sutil + active dorado con glow + pegadas al pipeline */
.lcp-tabs {
  padding: 0 18px !important;
  gap: 22px !important;
  background: linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.015)) !important;
  border-top: 1px solid rgba(255,255,255,0.06) !important;
  border-bottom: 1px solid rgba(255,255,255,0.06) !important;
}
.lcp-tab {
  font-size: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  padding: 10px 0 !important;
}
.lcp-tab.is-active {
  color: var(--stage-color, #fbbf24) !important;
  font-size: 12px !important;
  border-bottom-color: transparent !important;
  filter: none !important;
  text-shadow: 0 0 12px var(--stage-color, #fbbf24) !important;
}
.lcp-tab-add { padding: 10px 0 10px 14px !important; font-size: 11px !important; }

/* Ocultar "← X atrás" y "5 adelante →" para que tabs se peguen al strip */
.lcp-window-hint { display: none !important; }
.lcp-pipe { padding-bottom: 8px !important; }

/* 7.5 · V5-P1 · Bloque read-only ULTRA compacto · 3px padding + sin tags/iconos */
.lcp-readonly-block { margin-bottom: 6px; }
.lcp-readonly-block .lcp-field-row {
  padding: 3px 0 !important;
  border-bottom: 0 !important;
  display: grid !important;
  grid-template-columns: 130px 1fr auto !important;
  gap: 14px !important;
  align-items: center !important;
}
.lcp-readonly-block .lcp-field-key {
  font-size: 12px !important;
  color: rgba(255,255,255,0.50) !important;
  font-weight: 500 !important;
  margin: 0 !important;
}
.lcp-readonly-block .lcp-field-val {
  text-align: right !important;
  font-size: 12.5px !important;
  color: rgba(255,255,255,0.92) !important;
  font-weight: 500 !important;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
}
/* La fila "Responsable" usa display:inline-flex (heredado) · forzar align right */
.lcp-readonly-block .lcp-field-val--resp {
  justify-content: flex-end !important;
}
.lcp-readonly-block .lcp-resp-auto,
.lcp-readonly-block .lcp-field-ro {
  display: none !important;
}
/* Divider sutil al final del bloque read-only */
.lcp-readonly-block::after {
  content: "";
  display: block;
  height: 1px;
  background: rgba(255,255,255,0.04);
  margin: 7px 0 0;
}

/* 8. Field rows · Quitar highlight amarillo y avatares VG circulares */
.lcp-field-resp,
.lcp-field-resp.is-empty-resp,
.lcp-field-resp.is-primary-mismatch {
  background: transparent !important;
  border-left: 0 !important;
  margin: 0 !important;
}
.lcp-field-resp.is-primary-mismatch .lcp-resp-name { color: rgba(255,255,255,0.92) !important; }
.lcp-resp-avatar { display: none !important; }
.lcp-resp-name {
  color: rgba(255,255,255,0.92) !important;
  font-weight: 500 !important;
  font-size: 12.5px !important;
  letter-spacing: 0 !important;
}
.lcp-resp-auto {
  font-size: 10px !important;
  font-style: normal !important;
  color: rgba(255,255,255,0.30) !important;
}
.lcp-field-ro {
  font-size: 9.5px !important;
  padding: 0 !important;
  background: transparent !important;
  border: 0 !important;
  color: rgba(255,255,255,0.30) !important;
  font-family: inherit !important;
  font-style: normal !important;
  letter-spacing: 0.3px !important;
}

/* 9. Field values · monocromo (no dorado, no verde) */
.lcp-field-val.is-gold { color: rgba(255,255,255,0.92) !important; font-weight: 500 !important; }
.lcp-field-val.is-green { color: rgba(255,255,255,0.92) !important; font-weight: 500 !important; }
.lcp-field-val.is-mono { font-family: inherit !important; color: rgba(255,255,255,0.92) !important; }

/* 10. Contact footer · simpler */
.lcp-contact-img,
.lcp-contact-init {
  display: none !important;
}
.lcp-contact-head { gap: 0 !important; }
.lcp-contact-name { font-size: 12.5px !important; font-weight: 600 !important; color: #fff !important; }
.lcp-contact-channel { font-size: 10.5px !important; color: rgba(255,255,255,0.45) !important; margin-top: 2px !important; }

/* 11. Bottom action buttons · ocultos permanentemente (Victor 2026-05-15) */
.lcp-bottom-actions { display: none !important; }
.lcp-bottom-btn {
  background: transparent !important;
  border: 1px solid rgba(255,255,255,0.10) !important;
  color: rgba(255,255,255,0.75) !important;
  font-weight: 600 !important;
}
.lcp-bottom-btn:hover {
  border-color: rgba(251,191,36,0.30) !important;
  color: #fde68a !important;
  background: transparent !important;
}
/* === FIN V5-P1 MONOCROMO MINIMAL REDESIGN OVERRIDE === */

/* ================================================================== */
/* Propuesta 3 · 2026-05-19 (Victor) · Modal glass reclamar (M1.B)     */
/* Receta glass oficial aprobada · nota interna editable               */
/* ================================================================== */
.rcl-overlay {
  position: fixed; inset: 0; z-index: 130;
  display: flex; align-items: center; justify-content: center; padding: 16px;
  background: rgba(2, 2, 4, 0.62); backdrop-filter: blur(3px);
}
.rcl-modal {
  position: relative; width: 100%; max-width: 520px;
  padding: 34px 38px 32px; border-radius: 26px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  background:
    radial-gradient(circle at 12% 8%, rgba(255, 140, 90, 0.10), transparent 42%),
    radial-gradient(circle at 92% 96%, rgba(120, 90, 200, 0.16), transparent 50%),
    linear-gradient(155deg, #211b2b, #181722 45%, #14131b);
  box-shadow: 0 40px 90px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.05);
}
.rcl-close {
  position: absolute; top: 16px; right: 16px;
  width: 38px; height: 38px; border-radius: 50%;
  background: rgba(255, 255, 255, 0.06); border: 1px solid rgba(255, 255, 255, 0.08);
  color: #cfcad8; font-size: 18px; line-height: 1; cursor: pointer;
}
.rcl-close:hover { background: rgba(255, 255, 255, 0.10); }
.rcl-head { text-align: center; margin-bottom: 22px; }
.rcl-emoji { font-size: 28px; }
.rcl-title { font-size: 22px; font-weight: 700; color: #f3eefb; margin-top: 8px; }
.rcl-sub { font-size: 13.5px; color: #9a93a8; margin-top: 8px; line-height: 1.5; }
.rcl-sub b { color: #e8c766; }
.rcl-label {
  display: block; font-size: 10.5px; letter-spacing: 0.16em;
  text-transform: uppercase; color: #7a7488; margin-bottom: 8px;
}
.rcl-textarea {
  width: 100%; resize: vertical; min-height: 78px;
  padding: 12px 14px; border-radius: 14px;
  background: rgba(255, 255, 255, 0.035); border: 1px solid rgba(255, 255, 255, 0.09);
  color: #ece8f4; font-size: 13.5px; line-height: 1.55; font-family: inherit;
}
.rcl-textarea:focus { outline: none; border-color: rgba(184, 146, 74, 0.5); }
.rcl-actions { display: flex; gap: 10px; margin-top: 22px; }
.rcl-btn-ghost {
  flex: 1; padding: 11px; border-radius: 13px; cursor: pointer;
  background: transparent; border: 1px solid rgba(255, 255, 255, 0.10);
  color: #b3aec0; font-size: 13.5px;
}
.rcl-btn-ghost:hover { background: rgba(255, 255, 255, 0.04); }
.rcl-btn-gold {
  flex: 1.5; padding: 11px; border-radius: 13px; cursor: pointer;
  border: none; font-size: 13.5px; font-weight: 650; color: #1a1407;
  background: linear-gradient(135deg, #e8c766 0%, #c9a227 100%);
  box-shadow: 0 8px 22px -10px rgba(232, 199, 102, 0.7);
}
.rcl-btn-gold:disabled, .rcl-btn-ghost:disabled, .rcl-close:disabled {
  opacity: 0.55; cursor: not-allowed;
}

/* ================================================================== */
/* V5-P1 · 2026-05-15 (Victor) · Modal Configurar estilo Kommo M1.B    */
/* Chips horizontales de grupos · lista de campos del grupo activo     */
/* ================================================================== */
.cfg-overlay {
  position: fixed; inset: 0; z-index: 60;  /* z-60: por debajo del field editor sub-modal (z-70) */
  background: rgba(0,0,0,0.72);
  backdrop-filter: blur(14px);
  display: flex; align-items: flex-start; justify-content: center;
  padding: 60px 24px 24px;
  overflow-y: auto;
}
.cfg-modal {
  width: 100%; max-width: 620px;
  background: linear-gradient(135deg, rgba(20,20,20,0.99), rgba(8,8,8,0.99));
  border: 1px solid rgba(251,191,36,0.25);
  border-radius: 12px;
  box-shadow: 0 24px 60px rgba(0,0,0,0.75), 0 0 0 1px rgba(251,191,36,0.10);
  overflow: hidden;
  position: relative;
}
.cfg-modal::before {
  content: ""; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, transparent, #fbbf24, transparent); opacity: 0.55;
}
.cfg-head {
  padding: 16px 20px 14px;
  display: flex; align-items: flex-start; justify-content: space-between;
  border-bottom: 1px solid rgba(255,255,255,0.05);
}
.cfg-title { font-size: 15px; font-weight: 700; color: #fff; letter-spacing: -0.2px; }
.cfg-sub { font-size: 11.5px; color: rgba(255,255,255,0.50); margin-top: 3px; font-weight: 500; }
.cfg-close {
  width: 28px; height: 28px; border-radius: 7px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.55); cursor: pointer; font-size: 16px; line-height: 1;
  display: flex; align-items: center; justify-content: center;
  font-family: inherit;
}
.cfg-close:hover { background: rgba(239,68,68,0.10); border-color: rgba(239,68,68,0.30); color: #fca5a5; }

.cfg-section-tabs {
  display: flex; gap: 4px;
  padding: 8px 14px 0;
  border-bottom: 1px solid rgba(255,255,255,0.04);
}
.cfg-section-tab {
  padding: 8px 14px;
  background: transparent; border: 0; border-bottom: 2px solid transparent;
  color: rgba(255,255,255,0.50); font-size: 12px; font-weight: 600;
  cursor: pointer; font-family: inherit;
}
.cfg-section-tab:hover { color: rgba(255,255,255,0.80); }
.cfg-section-tab.active { color: #fbbf24; border-bottom-color: #fbbf24; }

.cfg-groups-row {
  display: flex; gap: 6px; padding: 14px 18px 8px; flex-wrap: wrap;
  border-bottom: 1px dashed rgba(255,255,255,0.05);
}
.cfg-group-chip {
  display: inline-flex; align-items: center; gap: 5px;
  padding: 6px 11px; border-radius: 7px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.70); font-size: 11.5px; font-weight: 600;
  cursor: pointer; transition: all 0.15s; font-family: inherit; white-space: nowrap;
}
.cfg-group-chip:hover { border-color: rgba(251,191,36,0.30); color: #fde68a; }
.cfg-group-chip.active {
  background: rgba(251,191,36,0.12);
  border-color: rgba(251,191,36,0.40);
  color: #fbbf24;
  box-shadow: 0 2px 8px rgba(251,191,36,0.15);
}
.cfg-group-drag { opacity: 0.35; font-size: 10px; cursor: grab; }
.cfg-group-count {
  font-size: 9.5px; padding: 1px 6px; border-radius: 8px;
  background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.55);
  font-weight: 700; font-variant-numeric: tabular-nums;
}
.cfg-group-chip.active .cfg-group-count { background: rgba(0,0,0,0.30); color: #fde68a; }
.cfg-group-add {
  padding: 6px 12px; border-radius: 7px;
  background: rgba(251,191,36,0.06);
  border: 1px dashed rgba(251,191,36,0.30);
  color: #fde68a; font-size: 11.5px; font-weight: 700;
  cursor: pointer; font-family: inherit;
}
.cfg-group-add:hover { background: rgba(251,191,36,0.12); border-color: rgba(251,191,36,0.50); }

.cfg-group-form {
  padding: 12px 18px;
  display: flex; gap: 8px; align-items: center;
  background: rgba(251,191,36,0.04);
  border-bottom: 1px solid rgba(251,191,36,0.15);
}
.cfg-input {
  flex: 1;
  background: rgba(0,0,0,0.40);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 7px; padding: 7px 11px;
  color: #fff; font-size: 12.5px; font-weight: 500;
  outline: none; font-family: inherit;
}
.cfg-input:focus { border-color: rgba(251,191,36,0.50); }
.cfg-btn-secondary {
  padding: 7px 12px; border-radius: 7px; cursor: pointer;
  background: transparent; border: 1px solid rgba(255,255,255,0.12);
  color: rgba(255,255,255,0.70); font-size: 11.5px; font-weight: 700;
  font-family: inherit;
}
.cfg-btn-secondary:hover { background: rgba(255,255,255,0.04); color: #fff; }
.cfg-btn-primary {
  padding: 7px 14px; border-radius: 7px; cursor: pointer;
  background: linear-gradient(135deg, #fbbf24, #f59e0b); border: 0;
  color: #000; font-size: 11.5px; font-weight: 800;
  font-family: inherit; box-shadow: 0 2px 8px rgba(251,191,36,0.30);
}
.cfg-btn-primary:hover { box-shadow: 0 4px 14px rgba(251,191,36,0.45); }
.cfg-btn-primary:disabled { opacity: 0.50; cursor: not-allowed; box-shadow: none; }

.cfg-group-actions {
  padding: 11px 18px; display: flex; align-items: center; justify-content: space-between;
  border-bottom: 1px dashed rgba(255,255,255,0.05);
}
.cfg-group-title { font-size: 12.5px; font-weight: 700; color: #fff; }
.cfg-group-actions-btns { display: flex; gap: 5px; }
.cfg-btn-mini {
  padding: 5px 10px; border-radius: 6px; cursor: pointer;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.70); font-size: 10.5px; font-weight: 600;
  font-family: inherit;
}
.cfg-btn-mini:hover { border-color: rgba(251,191,36,0.30); color: #fde68a; }
.cfg-btn-mini.cfg-btn-danger { background: rgba(239,68,68,0.06); border-color: rgba(239,68,68,0.20); color: #fca5a5; }
.cfg-btn-mini.cfg-btn-danger:hover { background: rgba(239,68,68,0.15); border-color: rgba(239,68,68,0.40); color: #fff; }

.cfg-fields-body {
  padding: 12px 18px 18px;
  max-height: 50vh; overflow-y: auto;
}
.cfg-fields-label {
  font-size: 9.5px; color: rgba(255,255,255,0.45);
  font-weight: 700; letter-spacing: 1.2px; text-transform: uppercase;
  margin-bottom: 8px;
}
.cfg-empty {
  text-align: center; padding: 30px 18px;
}
.cfg-empty-icon { font-size: 32px; opacity: 0.40; margin-bottom: 8px; }
.cfg-empty-text { font-size: 12px; color: rgba(255,255,255,0.50); }
.cfg-empty-small {
  text-align: center; padding: 16px 12px;
  font-size: 11px; color: rgba(255,255,255,0.40); font-style: italic;
}

.cfg-field-item {
  display: flex; align-items: center; gap: 10px;
  padding: 9px 12px; margin-bottom: 5px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.05);
  border-radius: 7px;
  transition: all 0.15s;
}
.cfg-field-item:hover { border-color: rgba(251,191,36,0.20); }
.cfg-field-drag { color: rgba(255,255,255,0.25); font-size: 11px; cursor: grab; }
.cfg-field-ico { font-size: 13px; }
.cfg-field-info { flex: 1; min-width: 0; }
.cfg-field-name {
  font-size: 12.5px; font-weight: 600; color: #fff;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.cfg-field-meta { font-size: 10px; color: rgba(255,255,255,0.45); margin-top: 1px; }
.cfg-field-btn {
  width: 26px; height: 26px; border-radius: 6px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
  color: rgba(255,255,255,0.65); cursor: pointer; font-size: 11px;
  display: flex; align-items: center; justify-content: center;
  font-family: inherit;
}
.cfg-field-btn:hover { border-color: rgba(251,191,36,0.30); color: #fde68a; }
.cfg-field-btn.cfg-field-btn-danger { background: rgba(239,68,68,0.06); border-color: rgba(239,68,68,0.20); color: #fca5a5; }
.cfg-field-btn.cfg-field-btn-danger:hover { background: rgba(239,68,68,0.15); border-color: rgba(239,68,68,0.40); color: #fff; }

.cfg-add-field {
  width: 100%;
  margin-top: 8px;
  padding: 11px;
  background: rgba(251,191,36,0.06);
  border: 1px dashed rgba(251,191,36,0.30);
  border-radius: 8px;
  color: #fde68a; font-size: 12px; font-weight: 700;
  cursor: pointer; font-family: inherit;
  text-align: center;
}
.cfg-add-field:hover { background: rgba(251,191,36,0.12); border-color: rgba(251,191,36,0.50); }

/* Tab Configurar pegada al final · color gris neutro como Factura/Link */
.lcp-tab-configurar { color: rgba(255,255,255,0.45) !important; }
.lcp-tab-configurar:hover { color: rgba(255,255,255,0.85) !important; }

.cfg-footer {
  padding: 12px 18px;
  border-top: 1px solid rgba(255,255,255,0.05);
  display: flex; justify-content: flex-end;
  background: rgba(0,0,0,0.30);
}
.cfg-auto-wrap {
  padding: 16px 18px;
  display: flex; flex-direction: column; gap: 12px;
  max-height: 50vh; overflow-y: auto;
}

/* V5-P1 · click en el área principal del campo (nombre+meta) abre el editor */
.cfg-field-item .cfg-field-info {
  background: transparent !important; border: 0 !important;
  text-align: left !important; padding: 0 !important;
  cursor: pointer !important; font-family: inherit !important;
}
.cfg-field-item .cfg-field-info:hover .cfg-field-name { color: #fde68a !important; }

/* ================================================================== */
/* V5-P1 · Field Editor M1.B overlay · siempre encima (Teleport)       */
/* ================================================================== */
.lcp-feditor-overlay {
  z-index: 9999;  /* encima de todo, incluso el cfg-overlay z:60 */
  background: rgba(0,0,0,0.78) !important;
}
/* Restyle del field editor a M1.B oscuro · más consistente con el resto */
.lcp-feditor-overlay > div {
  background: linear-gradient(135deg, rgba(20,20,20,0.99), rgba(8,8,8,0.99)) !important;
  border: 1px solid rgba(251,191,36,0.30) !important;
  box-shadow: 0 24px 60px rgba(0,0,0,0.75), 0 0 0 1px rgba(251,191,36,0.10) !important;
  position: relative;
}
.lcp-feditor-overlay > div::before {
  content: ""; position: absolute; top: 0; left: 0; right: 0; height: 2px;
  background: linear-gradient(90deg, transparent, #fbbf24, transparent); opacity: 0.6;
  border-radius: 14px 14px 0 0;
}
/* Header del field editor · suavizar gradient azul/morado */
.lcp-feditor-overlay [class*="bg-gradient-to-r"] {
  background: rgba(0,0,0,0.30) !important;
}
.lcp-feditor-overlay [class*="bg-gradient-to-br"] {
  background: linear-gradient(135deg, rgba(251,191,36,0.18), rgba(245,158,11,0.10)) !important;
  color: #fbbf24 !important;
  border: 1px solid rgba(251,191,36,0.30) !important;
  box-shadow: none !important;
}
/* Texto del header */
.lcp-feditor-overlay h3 { color: #fff !important; }
.lcp-feditor-overlay [class*="text-slate-500"] { color: rgba(255,255,255,0.55) !important; }
.lcp-feditor-overlay [class*="text-slate-700"],
.lcp-feditor-overlay [class*="text-slate-300"] { color: rgba(255,255,255,0.80) !important; }
.lcp-feditor-overlay [class*="text-slate-900"],
.lcp-feditor-overlay [class*="text-slate-100"] { color: #fff !important; }
.lcp-feditor-overlay [class*="text-slate-400"] { color: rgba(255,255,255,0.45) !important; }
/* Inputs · selects · textarea */
.lcp-feditor-overlay input,
.lcp-feditor-overlay select,
.lcp-feditor-overlay textarea {
  background: rgba(0,0,0,0.40) !important;
  border-color: rgba(255,255,255,0.12) !important;
  color: #fff !important;
}
.lcp-feditor-overlay input:focus,
.lcp-feditor-overlay select:focus,
.lcp-feditor-overlay textarea:focus {
  border-color: rgba(251,191,36,0.55) !important;
  outline: none !important;
}
.lcp-feditor-overlay select option { background: #0a0a0a; color: #fff; }
/* Cards de tipo · oscuras + activo dorado */
.lcp-feditor-overlay button[class*="rounded-lg"][class*="border"] {
  background: rgba(255,255,255,0.03) !important;
  border-color: rgba(255,255,255,0.06) !important;
  color: rgba(255,255,255,0.85) !important;
}
.lcp-feditor-overlay button[class*="rounded-lg"][class*="border"]:hover {
  background: rgba(251,191,36,0.04) !important;
  border-color: rgba(251,191,36,0.25) !important;
}
.lcp-feditor-overlay button[class*="border-blue-500"],
.lcp-feditor-overlay button[class*="ring-blue-500"] {
  background: linear-gradient(135deg, rgba(251,191,36,0.12), rgba(245,158,11,0.04)) !important;
  border-color: rgba(251,191,36,0.45) !important;
  box-shadow: 0 0 0 3px rgba(251,191,36,0.08) !important;
}
/* Vista previa box · oscura */
.lcp-feditor-overlay [class*="bg-slate-100"],
.lcp-feditor-overlay [class*="bg-slate-50"],
.lcp-feditor-overlay [class*="bg-slate-800/30"],
.lcp-feditor-overlay [class*="bg-slate-800/50"] {
  background: rgba(0,0,0,0.30) !important;
  border-color: rgba(255,255,255,0.06) !important;
}
/* Amber alert box (file/image) */
.lcp-feditor-overlay [class*="bg-amber-50"] {
  background: rgba(251,191,36,0.08) !important;
  border-color: rgba(251,191,36,0.25) !important;
  color: #fde68a !important;
}
/* Footer · botón cancelar y guardar */
.lcp-feditor-overlay button[class*="bg-slate-200"],
.lcp-feditor-overlay button[class*="bg-slate-700"] {
  background: rgba(255,255,255,0.04) !important;
  border: 1px solid rgba(255,255,255,0.10) !important;
  color: rgba(255,255,255,0.75) !important;
}
.lcp-feditor-overlay button[class*="bg-slate-200"]:hover,
.lcp-feditor-overlay button[class*="bg-slate-700"]:hover {
  background: rgba(255,255,255,0.08) !important;
  color: #fff !important;
}
.lcp-feditor-overlay button[class*="bg-gradient-to-r"][class*="from-blue-500"] {
  background: linear-gradient(135deg, #fbbf24, #f59e0b) !important;
  color: #000 !important;
  box-shadow: 0 2px 8px rgba(251,191,36,0.30) !important;
}
.lcp-feditor-overlay code {
  background: rgba(255,255,255,0.06) !important;
  color: #fde68a !important;
  padding: 1px 5px; border-radius: 4px;
  font-size: 11px;
}
/* === FIN modal Configurar Kommo === */
.lcp-field-ro {
  font-family: ui-monospace, SFMono-Regular, monospace;
  font-size: 8px;
  color: rgba(255,255,255,0.30);
  letter-spacing: 0.5px;
  padding: 2px 6px;
  background: rgba(255,255,255,0.03);
  border-radius: 4px;
  white-space: nowrap;
}

/* Empty blocks */
.lcp-empty-block {
  padding: 24px 18px;
  text-align: center;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.lcp-empty-block-sm { padding: 16px 18px; }
.lcp-empty-icon { font-size: 28px; margin-bottom: 6px; opacity: 0.6; }
.lcp-empty-text {
  font-size: 11px;
  color: rgba(255,255,255,0.45);
  margin-bottom: 8px;
}
.lcp-empty-btn {
  background: transparent;
  border: 1px solid rgba(251,191,36,0.40);
  color: #fbbf24;
  font-size: 11px; font-weight: 700;
  padding: 6px 14px;
  border-radius: 7px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}
.lcp-empty-btn:hover {
  background: rgba(251,191,36,0.10);
  box-shadow: 0 0 12px rgba(251,191,36,0.25);
}

/* CONTACTO */
.lcp-contact {
  padding: 12px 18px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.lcp-contact-head {
  display: flex; align-items: center; gap: 10px;
  margin-bottom: 8px;
}
.lcp-contact-img,
.lcp-contact-init {
  width: 28px; height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  object-fit: cover;
}
.lcp-contact-init {
  background: linear-gradient(135deg, #6366f1, #4338ca);
  display: flex; align-items: center; justify-content: center;
  font-weight: 900; font-size: 10px; color: #fff;
}
.lcp-contact-info { flex: 1; min-width: 0; }
.lcp-contact-name {
  font-size: 12px; font-weight: 800;
  color: #fff;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.lcp-contact-channel {
  font-size: 9px;
  color: rgba(255,255,255,0.45);
  letter-spacing: 0.3px;
}
.lcp-contact-rows { display: flex; flex-direction: column; gap: 0; }

/* BOTTOM ACTIONS */
.lcp-bottom-actions {
  display: flex; flex-direction: column; gap: 6px;
  padding: 12px 18px;
}
.lcp-bottom-btn {
  width: 100%;
  padding: 9px 12px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 7px;
  color: rgba(255,255,255,0.75);
  font-size: 11px; font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}
.lcp-bottom-btn:hover {
  background: rgba(251,191,36,0.10);
  border-color: rgba(251,191,36,0.40);
  color: #fbbf24;
}
.lcp-bottom-btn--task {
  background: linear-gradient(180deg, rgba(168,85,247,0.10), rgba(168,85,247,0.04));
  border-color: rgba(168,85,247,0.35);
  color: #c4b5fd;
}
.lcp-bottom-btn--task:hover {
  background: rgba(168,85,247,0.18);
  border-color: rgba(168,85,247,0.55);
  color: #ddd6fe;
}

/* Popover de Acciones (⚙ · 2026-05-12 PM · C2) ──────────────────────── */
.lcp-actions { position: relative; }
.lcp-act-btn.is-active {
  background: rgba(251,191,36,0.15);
  border-color: rgba(251,191,36,0.45);
  color: #fde68a;
}
.lcp-actions-popover {
  position: fixed;
  width: 270px;
  background: linear-gradient(180deg, #111, #060606);
  border: 1px solid rgba(251,191,36,0.30);
  border-radius: 12px;
  box-shadow: 0 16px 40px rgba(0,0,0,0.65), inset 0 1px 0 rgba(251,191,36,0.10);
  z-index: 9999;
  overflow: hidden;
  animation: popIn 0.18s ease;
}
@keyframes popIn {
  from { opacity: 0; transform: translateY(-6px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
.lcp-actions-pop-header {
  padding: 11px 14px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  background: rgba(251,191,36,0.04);
}
.lcp-actions-pop-header span {
  font-size: 10px;
  font-weight: 900;
  letter-spacing: 1.5px;
  color: rgba(255,255,255,0.7);
  text-transform: uppercase;
}
.lcp-actions-pop-section { padding: 6px; }
.lcp-actions-pop-section + .lcp-actions-pop-section {
  border-top: 1px solid rgba(255,255,255,0.04);
}
.lcp-actions-pop-title {
  padding: 6px 8px 4px;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: 1.5px;
  color: rgba(255,255,255,0.35);
  text-transform: uppercase;
}
.lcp-actions-pop-title--danger { color: rgba(239,68,68,0.6); }
.lcp-actions-pop-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 7px;
  cursor: pointer;
  transition: all 0.12s;
  font-size: 12.5px;
  color: rgba(255,255,255,0.82);
  background: transparent;
  border: 0;
  width: 100%;
  text-align: left;
  font-family: inherit;
}
.lcp-actions-pop-item:hover {
  background: rgba(255,255,255,0.04);
  transform: translateX(2px);
}
.lcp-actions-pop-item--danger { color: #fca5a5; }
.lcp-actions-pop-item--danger:hover { background: rgba(239,68,68,0.08); }

/* ── Selector de inbox para escribir al cliente (estilo Kommo · M1.B) ── */
.lcp-phone-wrap { position: relative; }
.lcp-phone-btn {
  display: inline-flex; align-items: center; gap: 7px;
  background: transparent; border: 1px solid transparent;
  font: inherit; cursor: default; padding: 4px 8px; border-radius: 8px;
}
.lcp-phone-btn.is-clickable { cursor: pointer; background: rgba(251,191,36,0.08); border-color: rgba(251,191,36,0.22); }
.lcp-phone-btn.is-clickable:hover { background: rgba(251,191,36,0.14); border-color: rgba(251,191,36,0.45); }
.lcp-phone-chev { color: #fbbf24; font-size: 10px; }

/* "Responder desde" · toggle multi-número (P1 interruptor segmentado) */
.lcp-rf-seg {
  display: flex; gap: 4px; padding: 4px; border-radius: 13px;
  background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07);
}
.lcp-rf-btn {
  flex: 1; min-width: 0; cursor: pointer; font: inherit;
  border: 1px solid transparent; border-radius: 10px; padding: 8px 7px;
  background: transparent; color: rgba(255,255,255,0.5); text-align: center;
  transition: background 0.15s, border-color 0.15s, transform 0.15s, box-shadow 0.18s;
}
.lcp-rf-btn:hover:not(.is-busy) { color: rgba(255,255,255,0.8); transform: translateY(-1px); }
.lcp-rf-btn.is-busy { cursor: progress; opacity: 0.6; }
.lcp-rf-num {
  display: flex; align-items: center; justify-content: center; gap: 6px;
  font-size: 12.5px; font-weight: 800; white-space: nowrap;
}
.lcp-rf-lbl {
  display: block; margin-top: 2px; font-size: 9.5px; font-weight: 600;
  letter-spacing: 0.3px; text-transform: uppercase; opacity: 0.65;
}
.lcp-rf-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.dot-azul { background: #4d9bff; box-shadow: 0 0 6px rgba(77,155,255,0.7); }
.dot-verde { background: #46d6a6; box-shadow: 0 0 6px rgba(70,214,166,0.7); }
.lcp-rf-btn.is-on-azul {
  color: #fff; background: rgba(77,155,255,0.13);
  border-color: rgba(77,155,255,0.5); box-shadow: 0 6px 16px rgba(77,155,255,0.16);
}
.lcp-rf-btn.is-on-verde {
  color: #fff; background: rgba(70,214,166,0.12);
  border-color: rgba(70,214,166,0.5); box-shadow: 0 6px 16px rgba(70,214,166,0.16);
}
.lcp-inbox-pop {
  position: absolute; right: 0; top: calc(100% + 8px); z-index: 60;
  width: 360px; border-radius: 16px; overflow: hidden;
  background: rgba(10,10,10,0.96);
  border: 1.5px solid rgba(255,255,255,0.10);
  box-shadow: 0 0 0 1px rgba(255,255,255,0.04) inset, 0 22px 50px rgba(0,0,0,0.6);
}
.lcp-inbox-pop-head { padding: 15px 16px 13px; border-bottom: 1px solid rgba(255,255,255,0.05); }
.lcp-inbox-pop-lock {
  float: right; font-size: 9px; font-weight: 800; letter-spacing: 0.5px;
  text-transform: uppercase; color: #fbbf24;
  background: rgba(251,191,36,0.10); border: 1px solid rgba(251,191,36,0.28);
  padding: 3px 8px; border-radius: 999px;
}
.lcp-inbox-pop-t { font-size: 15px; font-weight: 800; color: #fff; }
.lcp-inbox-pop-d { font-size: 11px; color: rgba(255,255,255,0.50); margin-top: 3px; }
.lcp-inbox-pop-list { max-height: 290px; overflow-y: auto; padding: 4px 0; }
.lcp-inbox-grp {
  font-size: 9.5px; font-weight: 800; letter-spacing: 2px;
  text-transform: uppercase; color: rgba(255,255,255,0.32);
  padding: 13px 18px 6px;
}
.lcp-inbox-grp.is-muted { color: rgba(255,255,255,0.22); }
.lcp-inbox-opt {
  position: relative; display: block; width: 100%; text-align: left;
  padding: 12px 16px 12px 18px; cursor: pointer;
  background: transparent; border: none; transition: background 0.15s;
}
.lcp-inbox-opt::before {
  content: ''; position: absolute; left: 0; top: 13px; bottom: 13px;
  width: 2px; border-radius: 2px; background: transparent; transition: 0.15s;
}
.lcp-inbox-opt:hover { background: rgba(251,191,36,0.04); }
.lcp-inbox-opt.is-sel {
  background: linear-gradient(90deg, rgba(251,191,36,0.07), transparent);
}
.lcp-inbox-opt.is-sel::before {
  background: #fbbf24; box-shadow: 0 0 10px rgba(251,191,36,0.5);
}
.lcp-inbox-opt.is-hidden { opacity: 0.45; cursor: default; }
.lcp-inbox-opt.is-hidden:hover { background: transparent; }
.lcp-inbox-top { display: flex; align-items: center; gap: 9px; }
.lcp-inbox-num {
  font-size: 16px; font-weight: 700; letter-spacing: 0.3px; color: #fff;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.lcp-inbox-opt.is-sel .lcp-inbox-num { color: #fbbf24; }
.lcp-inbox-opt.is-hidden .lcp-inbox-num { font-size: 13px; }
.lcp-inbox-dot {
  width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0;
  margin-left: auto; background: #22c55e;
  box-shadow: 0 0 6px rgba(34,197,94,0.7);
}
.lcp-inbox-sub { display: block; font-size: 11px; color: rgba(255,255,255,0.50); margin-top: 3px; }
.lcp-inbox-hidden-bar {
  display: block; width: 100%; text-align: center; cursor: pointer;
  background: transparent; border: none;
  border-top: 1px solid rgba(255,255,255,0.05);
  padding: 9px 16px; font-size: 10.5px; letter-spacing: 0.3px;
  color: rgba(255,255,255,0.32);
}
.lcp-inbox-hidden-bar u { color: rgba(255,255,255,0.50); }
.lcp-inbox-empty { padding: 18px 16px; text-align: center; font-size: 12px; color: rgba(255,255,255,0.4); }
.lcp-inbox-pop-foot { display: flex; gap: 9px; padding: 12px 16px; }
.lcp-inbox-btn {
  flex: 1; text-align: center; padding: 10px; border-radius: 10px;
  font-size: 12.5px; font-weight: 800; cursor: pointer; border: none;
}
.lcp-inbox-btn.sec { background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.12); color: #fff; }
.lcp-inbox-btn.pri {
  background: linear-gradient(135deg, #22c55e, #16a34a); color: #04231a;
  box-shadow: 0 0 16px rgba(34,197,94,0.4);
}
.lcp-inbox-btn.pri:disabled { opacity: 0.5; cursor: not-allowed; box-shadow: none; }
.lcp-inbox-btn.pri:not(:disabled):hover { filter: brightness(1.07); }
.lcp-pop-icon {
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  flex-shrink: 0;
}
.lcp-pop-icon--task { background: linear-gradient(135deg, rgba(168,85,247,0.20), rgba(168,85,247,0.08)); color: #c4b5fd; border: 1px solid rgba(168,85,247,0.30); }
.lcp-pop-icon--reclaim { background: linear-gradient(135deg, rgba(251,191,36,0.20), rgba(251,191,36,0.08)); color: #fbbf24; border: 1px solid rgba(251,191,36,0.30); }
.lcp-pop-icon--close { background: linear-gradient(135deg, rgba(34,197,94,0.20), rgba(34,197,94,0.08)); color: #86efac; border: 1px solid rgba(34,197,94,0.30); }
.lcp-pop-icon--delete { background: linear-gradient(135deg, rgba(239,68,68,0.20), rgba(239,68,68,0.08)); color: #fca5a5; border: 1px solid rgba(239,68,68,0.30); }
.lcp-pop-info { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.lcp-pop-lbl { font-weight: 600; line-height: 1.2; }
.lcp-pop-desc { font-size: 10.5px; color: rgba(255,255,255,0.45); margin-top: 2px; }

/* INPUTS / SELECTS / TEXTAREAS dentro del aside · M1.B */
.lcp-aside input[type="text"],
.lcp-aside input[type="number"],
.lcp-aside input[type="email"],
.lcp-aside input[type="tel"],
.lcp-aside input[type="url"],
.lcp-aside input[type="date"],
.lcp-aside input[type="datetime-local"],
.lcp-aside input[type="time"],
.lcp-aside textarea,
.lcp-aside select {
  background: rgba(255,255,255,0.04);
  color: #fff;
  border: 1px solid rgba(251,191,36,0.30);
  border-radius: 6px;
}
.lcp-aside input:focus,
.lcp-aside textarea:focus,
.lcp-aside select:focus {
  outline: none;
  border-color: #fbbf24;
  box-shadow: 0 0 0 3px rgba(251,191,36,0.20);
}
.lcp-aside select option { background: #0a0a0a; color: #fff; }

/* Custom field value hover (cursor-text inline edit) */
.lcp-aside .lcp-field-val-wrap span.cursor-text,
.lcp-aside .lcp-field-val-wrap span.cursor-pointer {
  color: rgba(255,255,255,0.85);
}
.lcp-aside .lcp-field-val-wrap span.cursor-text:hover,
.lcp-aside .lcp-field-val-wrap span.cursor-pointer:hover {
  background: rgba(251,191,36,0.10);
  color: #fbbf24;
}
.lcp-aside .lcp-field-val-wrap .text-slate-400,
.lcp-aside .lcp-field-val-wrap .text-slate-500,
.lcp-aside .lcp-field-val-wrap .text-slate-600,
.lcp-aside .lcp-field-val-wrap .text-slate-300 {
  color: rgba(255,255,255,0.35) !important;
}
.lcp-aside .lcp-field-val-wrap .text-slate-900,
.lcp-aside .lcp-field-val-wrap .text-slate-100,
.lcp-aside .lcp-field-val-wrap .text-slate-200,
.lcp-aside .lcp-field-val-wrap .text-slate-700 {
  color: rgba(255,255,255,0.85) !important;
}
.lcp-aside .lcp-field-val-wrap .text-blue-600,
.lcp-aside .lcp-field-val-wrap .text-blue-400,
.lcp-aside .lcp-field-val-wrap .text-blue-500 {
  color: #fbbf24 !important;
}
.lcp-aside .lcp-field-val-wrap .bg-blue-500 { background: #fbbf24 !important; color: #000 !important; }
.lcp-aside .lcp-field-val-wrap .bg-slate-100,
.lcp-aside .lcp-field-val-wrap .bg-slate-800 {
  background: rgba(255,255,255,0.06) !important;
}
.lcp-aside .lcp-field-val-wrap .text-amber-400 { color: #fbbf24 !important; }

/* showCreateForm header dark */
.lcp-aside .px-4.py-3.border-b {
  border-color: rgba(255,255,255,0.06) !important;
}

/* ─── Modal "eliminar contacto e historial" · M1.B danger · D2 + D4 ─── */
.lcp-del-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.72);
  backdrop-filter: blur(10px);
  display: flex; align-items: center; justify-content: center;
  z-index: 9999;
  padding: 20px;
  animation: lcp-del-fade-in 0.18s ease-out;
}
@keyframes lcp-del-fade-in { from { opacity: 0; } to { opacity: 1; } }
.lcp-del-modal {
  width: 100%; max-width: 460px;
  background:
    radial-gradient(ellipse at 0% 0%, rgba(239,68,68,0.18), transparent 60%),
    radial-gradient(circle at 100% 0%, rgba(239,68,68,0.10), transparent 60%),
    #0a0a0a;
  border: 1px solid rgba(239,68,68,0.40);
  border-radius: 16px;
  padding: 22px 22px 18px;
  box-shadow:
    0 0 0 1px rgba(239,68,68,0.12),
    0 20px 60px rgba(0,0,0,0.6),
    0 0 80px rgba(239,68,68,0.15);
  animation: lcp-del-pop 0.26s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes lcp-del-pop { from { transform: scale(0.92); opacity: 0; } to { transform: scale(1); opacity: 1; } }

.lcp-del-head {
  display: flex; align-items: center; gap: 14px;
  padding-bottom: 16px;
  border-bottom: 1px solid rgba(239,68,68,0.15);
  margin-bottom: 16px;
}
.lcp-del-icon {
  width: 48px; height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, rgba(239,68,68,0.20), rgba(239,68,68,0.08));
  border: 1px solid rgba(239,68,68,0.40);
  display: flex; align-items: center; justify-content: center;
  font-size: 22px;
  box-shadow: inset 0 1px 0 rgba(239,68,68,0.25);
}
.lcp-del-title-block { flex: 1; }
.lcp-del-lbl {
  font-size: 10px; letter-spacing: 1.5px; text-transform: uppercase;
  color: rgba(239,68,68,0.85); font-weight: 800;
}
.lcp-del-title {
  font-size: 17px; font-weight: 900; color: #fff;
  margin: 2px 0 0; letter-spacing: -0.2px;
}

.lcp-del-contact {
  padding: 12px 14px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
  margin-bottom: 16px;
  display: flex; align-items: center; gap: 12px;
}
.lcp-del-avatar {
  width: 40px; height: 40px; border-radius: 11px;
  background: linear-gradient(135deg, #6366f1, #4338ca, #7c3aed);
  display: flex; align-items: center; justify-content: center;
  font-weight: 900; font-size: 14px; color: #fff;
  box-shadow: 0 0 0 2px rgba(251,191,36,0.40), 0 0 0 3.5px #050505;
  flex-shrink: 0;
}
.lcp-del-name { font-size: 14px; font-weight: 800; color: #fff; }
.lcp-del-phone {
  font-size: 11px; color: #fbbf24;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-weight: 600; margin-top: 2px;
}

.lcp-del-warn {
  background: rgba(239,68,68,0.06);
  border: 1px solid rgba(239,68,68,0.20);
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 16px;
}
.lcp-del-warn ul { list-style: none; padding: 0; margin: 0; }
.lcp-del-warn li {
  padding: 3px 0 3px 18px;
  position: relative;
  font-size: 11.5px; color: rgba(255,255,255,0.70);
  line-height: 1.5;
}
.lcp-del-warn li::before {
  content: '✗';
  position: absolute; left: 0;
  color: #ef4444; font-weight: 800;
}
.lcp-del-warn b { color: #fff; }

.lcp-del-confirm-lbl {
  font-size: 11px; color: rgba(255,255,255,0.65);
  margin-bottom: 6px;
}
.lcp-del-confirm-lbl b {
  font-family: ui-monospace, SFMono-Regular, monospace;
  color: #ef4444; font-weight: 800;
  padding: 1px 6px;
  background: rgba(239,68,68,0.10);
  border-radius: 4px;
}
.lcp-del-input {
  width: 100%;
  background: rgba(15,15,15,0.6);
  border: 1px solid rgba(239,68,68,0.30);
  border-radius: 10px;
  padding: 10px 12px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 13px; color: #fff;
  outline: 0;
}
.lcp-del-input:focus {
  border-color: rgba(239,68,68,0.60);
  box-shadow: 0 0 0 3px rgba(239,68,68,0.10);
}
.lcp-del-input:disabled { opacity: 0.5; cursor: not-allowed; }

.lcp-del-actions {
  display: flex; gap: 8px; margin-top: 18px;
}
.lcp-del-cancel {
  flex: 1; padding: 11px;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 10px;
  color: rgba(255,255,255,0.7);
  font-size: 12px; font-weight: 700; letter-spacing: 0.4px;
  cursor: pointer;
}
.lcp-del-cancel:hover:not(:disabled) { color: #fff; background: rgba(255,255,255,0.08); }
.lcp-del-cancel:disabled { opacity: 0.5; cursor: not-allowed; }

.lcp-del-confirm {
  flex: 1.6; padding: 11px;
  background: rgba(239,68,68,0.15);
  border: 1px solid rgba(239,68,68,0.40);
  border-radius: 10px;
  color: rgba(239,68,68,0.65);
  font-size: 12px; font-weight: 900; letter-spacing: 0.6px; text-transform: uppercase;
  cursor: not-allowed;
  transition: all 0.18s;
}
.lcp-del-confirm.is-enabled {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(239,68,68,0.35);
}
.lcp-del-confirm.is-enabled:hover:not(:disabled) {
  filter: brightness(1.1);
  box-shadow: 0 4px 20px rgba(239,68,68,0.5);
}
.lcp-del-confirm:disabled { cursor: not-allowed; }

/* Banner "X está atendiendo en tu ausencia" (M v5 · M1.B) */
.lcp-attend-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 14px 12px;
  padding: 10px 12px;
  background: linear-gradient(180deg, rgba(139,92,246,0.16), rgba(139,92,246,0.05));
  border: 1px solid rgba(139,92,246,0.45);
  border-radius: 10px;
  position: relative;
  overflow: hidden;
  box-shadow: inset 0 1px 0 rgba(196,181,253,0.18), 0 4px 12px rgba(139,92,246,0.15);
}
.lcp-attend-banner::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(196,181,253,0.7), transparent);
  pointer-events: none;
}
.lcp-attend-banner.is-responsible {
  background: linear-gradient(180deg, rgba(251,191,36,0.16), rgba(139,92,246,0.06));
  border-color: rgba(251,191,36,0.55);
  box-shadow: inset 0 1px 0 rgba(253,224,71,0.22), 0 4px 14px rgba(251,191,36,0.18);
}
.lcp-attend-banner.is-responsible::before {
  background: linear-gradient(90deg, transparent, rgba(253,224,71,0.75), transparent);
}
.lcp-attend-banner__icon {
  width: 32px; height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6, #6d28d9);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  box-shadow: 0 3px 8px rgba(139,92,246,0.35);
}
.lcp-attend-banner.is-responsible .lcp-attend-banner__icon {
  background: linear-gradient(135deg, #fbbf24, #b45309);
  box-shadow: 0 3px 10px rgba(251,191,36,0.45);
}
.lcp-attend-banner__body {
  flex: 1;
  min-width: 0;
}
.lcp-attend-banner__text {
  font-size: 12px;
  color: rgba(255,255,255,0.88);
  line-height: 1.4;
}
.lcp-attend-banner__text b {
  color: #c4b5fd;
  font-weight: 800;
}
.lcp-attend-banner.is-responsible .lcp-attend-banner__text b {
  color: #fde68a;
}
.lcp-attend-banner__btn {
  flex-shrink: 0;
  padding: 7px 12px;
  border-radius: 8px;
  border: 1px solid rgba(253,224,71,0.55);
  background: linear-gradient(135deg, #fbbf24, #d97706);
  color: #1c1917;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.4px;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(251,191,36,0.4), inset 0 1px 0 rgba(253,224,71,0.6);
  transition: filter 120ms ease, transform 120ms ease;
}
.lcp-attend-banner__btn:hover:not(:disabled) {
  filter: brightness(1.08);
  transform: translateY(-1px);
}
.lcp-attend-banner__btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
